import React, { useState, useEffect, useCallback, useRef } from 'react'
import { io } from 'socket.io-client'
import {
  Layout, RefreshCcw, Plus, X, Eye, Film, BarChart2, CalendarDays,
  Trash2, ShieldCheck, Zap, Download, User, Lock, LogOut, KeyRound,
  Upload, Cloud, HardDrive, Monitor, Wifi, WifiOff,
  ChevronRight, Images, LayoutDashboard, CheckCircle2,
  Activity, Sparkles, PlayCircle, Megaphone, Clock, Tag, AlertOctagon,
  GripVertical, Radio, BookOpen, Lightbulb, ArrowRight, Star,
  MousePointerClick, Settings, CheckSquare, Layers, Globe,
  Database, LogIn
} from 'lucide-react'
import api, { SOCKET_URL } from './api'
import { ToastProvider, useToast } from './components/Toast'

// ─── Constants ────────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { key: 'home',       label: 'Dashboard',    icon: LayoutDashboard },
  { key: 'devices',    label: 'Devices',      icon: Monitor },
  { key: 'upload',     label: 'Upload Media', icon: Upload },
  { key: 'gallery',    label: 'Media Library',icon: Images },
  { key: 'calendar',   label: 'Schedule',     icon: CalendarDays },
  { key: 'reports',    label: 'Analytics',    icon: BarChart2 },
  { key: 'cloudinary', label: 'Cloud Accounts',icon: Cloud },
  { key: 'storage',    label: 'Cloud Storage', icon: Database },
  { key: 'guide',      label: 'How to Use',   icon: BookOpen },
]

const PAGE_TITLES = {
  home: 'Dashboard', devices: 'Devices', upload: 'Upload Media', gallery: 'Media Library',
  calendar: 'Schedule', reports: 'Analytics', cloudinary: 'Cloud Accounts',
  storage: 'Cloud Storage', guide: 'How to Use',
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// ─── UI helpers ───────────────────────────────────────────────────────────────

// ─── Design tokens ────────────────────────────────────────────────────────────
const STAT_CFGS = {
  green:  { bg: 'linear-gradient(135deg,#064e3b,#065f46,#047857)', glow: 'rgba(16,185,129,0.45)',  accent: '#10b981' },
  red:    { bg: 'linear-gradient(135deg,#7f1d1d,#991b1b,#b91c1c)', glow: 'rgba(244,63,94,0.45)',   accent: '#f43f5e' },
  blue:   { bg: 'linear-gradient(135deg,#1e1b4b,#1e3a8a,#1d4ed8)', glow: 'rgba(99,102,241,0.45)',  accent: '#6366f1' },
  purple: { bg: 'linear-gradient(135deg,#2e1065,#3b0764,#4a1d96)', glow: 'rgba(167,139,250,0.45)', accent: '#a78bfa' },
  amber:  { bg: 'linear-gradient(135deg,#451a03,#78350f,#92400e)', glow: 'rgba(245,158,11,0.45)',  accent: '#f59e0b' },
}

function StatCard({ icon: Icon, label, value, sub, color = 'blue', loading, delay = 0 }) {
  const cfg = STAT_CFGS[color] || STAT_CFGS.blue
  if (loading) return (
    <div className="rounded-2xl p-6 animate-pulse" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="space-y-3">
        <div className="w-11 h-11 rounded-2xl" style={{ background: 'rgba(255,255,255,0.07)' }} />
        <div className="h-2 w-16 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }} />
        <div className="h-10 w-20 rounded-xl" style={{ background: 'rgba(255,255,255,0.07)' }} />
      </div>
    </div>
  )
  return (
    <div className="rounded-2xl p-6 text-white relative overflow-hidden cursor-default group stat-float" style={{ background: cfg.bg, boxShadow: `0 0 0 1px rgba(255,255,255,0.06), 0 20px 60px rgba(0,0,0,0.5), 0 0 80px ${cfg.glow}`, animationDelay: `${delay}s` }}>
      <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-20 group-hover:opacity-35 group-hover:scale-110 transition-all duration-700" style={{ background: `radial-gradient(circle,${cfg.accent},transparent 70%)` }} />
      <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full opacity-10" style={{ background: `radial-gradient(circle,${cfg.accent},transparent 70%)` }} />
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.15) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.15) 1px,transparent 1px)', backgroundSize: '18px 18px' }} />
      <div className="relative">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4 shadow-xl" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
          <Icon size={20} className="text-white" />
        </div>
        <p className="text-white/50 text-[11px] font-black uppercase tracking-widest">{label}</p>
        <p className="text-5xl font-black mt-1 leading-none">{value}</p>
        {sub && <p className="text-white/40 text-xs mt-2 font-medium">{sub}</p>}
      </div>
      <div className="absolute bottom-0 left-6 right-6 h-px" style={{ background: `linear-gradient(90deg,transparent,${cfg.accent}88,transparent)` }} />
    </div>
  )
}

function Badge({ children, color = 'slate' }) {
  const c = {
    green:  'bg-emerald-400/15 text-emerald-300 border-emerald-400/25',
    red:    'bg-rose-400/15 text-rose-300 border-rose-400/25',
    blue:   'bg-blue-400/15 text-blue-300 border-blue-400/25',
    amber:  'bg-amber-400/15 text-amber-300 border-amber-400/25',
    violet: 'bg-violet-400/15 text-violet-300 border-violet-400/25',
    slate:  'bg-white/8 text-slate-400 border-white/10',
  }[color]
  return <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${c}`}>{children}</span>
}

const AVATAR_COLORS = ['bg-blue-500', 'bg-violet-500', 'bg-emerald-500', 'bg-rose-500', 'bg-amber-500', 'bg-indigo-500', 'bg-teal-500', 'bg-pink-500']
function deviceAvatar(name, id) {
  const color = AVATAR_COLORS[(id?.charCodeAt(0) || 0) % AVATAR_COLORS.length]
  const initials = name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : 'S?'
  return { color, initials }
}

function SectionCard({ title, sub, action, children }) {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(13,20,45,0.75)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 25px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)' }}>
      {(title || action) && (
        <div className="px-6 py-4 flex items-center justify-between gap-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div>
            {title && <h3 className="font-black text-white">{title}</h3>}
            {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  )
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl shrink-0" style={{ background: 'rgba(255,255,255,0.07)' }} />
          <div className="space-y-2">
            <div className="h-3 w-28 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }} />
            <div className="h-2 w-20 rounded-full" style={{ background: 'rgba(255,255,255,0.04)' }} />
          </div>
        </div>
      </td>
      <td className="px-6 py-4"><div className="h-8 w-36 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }} /></td>
      <td className="px-6 py-4"><div className="h-8 w-28 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }} /></td>
      <td className="px-6 py-4"><div className="h-6 w-20 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }} /></td>
      <td className="px-6 py-4"><div className="flex gap-2 justify-center"><div className="w-8 h-8 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }} /><div className="w-8 h-8 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }} /><div className="w-8 h-8 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }} /></div></td>
    </tr>
  )
}

const inputCls = "w-full rounded-xl py-2.5 px-4 text-sm font-medium outline-none transition-all focus:ring-2 focus:ring-blue-500/40"
const thCls = "px-6 py-3 text-[11px] font-bold text-slate-400 uppercase tracking-wider"
const tdCls = "px-6 py-4"

// ─── Inner App ────────────────────────────────────────────────────────────────

function AppInner() {
  const toast = useToast()

  const [token, setToken] = useState(localStorage.getItem('adminToken'))
  const [loginData, setLoginData] = useState({ username: '', password: '' })
  const [view, setView] = useState('home')
  const [devices, setDevices] = useState([])
  const [onlineDevices, setOnlineDevices] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [newDevice, setNewDevice] = useState({ id: '', name: '', group: '' })
  const [isSyncing, setIsSyncing] = useState(false)
  const [dataLoading, setDataLoading] = useState(true)
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [copiedDeviceId, setCopiedDeviceId] = useState(null)
  const [reportType, setReportType] = useState('summarized')
  const [reportDeviceId, setReportDeviceId] = useState('')
  const [reportGroup, setReportGroup] = useState('')
  const [reportFrom, setReportFrom] = useState('')
  const [reportTo, setReportTo] = useState('')
  const [reportData, setReportData] = useState(null)
  const [reportLoading, setReportLoading] = useState(false)
  const [storageStats, setStorageStats] = useState(null)
  const [cleanupLoading, setCleanupLoading] = useState(false)
  const [cloudinaryAccounts, setCloudinaryAccounts] = useState([])
  const [newAccount, setNewAccount] = useState({ name: '', cloudName: '', apiKey: '', apiSecret: '', folder: 'nexus-signage', isDefault: false })
  const [addingAccount, setAddingAccount] = useState(false)
  const [groupFilter, setGroupFilter] = useState('all')
  const [showBroadcast, setShowBroadcast] = useState(false)
  const [broadcastMsg, setBroadcastMsg] = useState('')
  const [broadcastType, setBroadcastType] = useState('warning')
  const [broadcastSending, setBroadcastSending] = useState(false)
  const [forceSyncing, setForceSyncing] = useState(new Set())
  const [forceLoggingIn, setForceLoggingIn] = useState(new Set())
  const [offlineToggling, setOfflineToggling] = useState(new Set())
  const [confirm, setConfirm] = useState(null)
  const [nowPlayingData, setNowPlayingData] = useState({})
  const [activityLog, setActivityLog] = useState([])
  const [cloudStorageData, setCloudStorageData] = useState(null)
  const [cloudStorageLoading, setCloudStorageLoading] = useState(false)
  // ── Feature: Live Preview ──────────────────────────────────────────────────
  const [previewDevice, setPreviewDevice] = useState(null)
  // ── Feature: Bulk Assign ───────────────────────────────────────────────────
  const [bulkMode, setBulkMode] = useState(false)
  const [bulkSelected, setBulkSelected] = useState(new Set())
  const [showBulkAssign, setShowBulkAssign] = useState(false)
  const [bulkAssigning, setBulkAssigning] = useState(false)
  const [bulkContent, setBulkContent] = useState([])       // media items to assign
  const [bulkGallerySelected, setBulkGallerySelected] = useState(new Set())
  // ── Feature: Offline Alert ─────────────────────────────────────────────────
  const prevOnlineRef = useRef([])

  // ── Direct upload to media library ─────────────────────────────────────────
  const [uploadFiles, setUploadFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const uploadInputRef = useRef(null)

  // ── Device Content modal (video-wise schedule) ─────────────────────────────
  const [deviceContentModal, setDeviceContentModal] = useState(null)
  const [deviceContent, setDeviceContent] = useState([])
  const [deviceContentSaving, setDeviceContentSaving] = useState(false)
  const [allMediaAssets, setAllMediaAssets] = useState([])
  const [contentGalleryOpen, setContentGalleryOpen] = useState(false)
  const [contentGallerySelected, setContentGallerySelected] = useState(new Set())
  const contentDragItem = useRef(null)
  const contentDragOver = useRef(null)

  const showConfirm = useCallback((message, onOk, opts = {}) => {
    setConfirm({ message, onOk, title: opts.title || 'Confirm', danger: opts.danger !== false })
  }, [])

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!token) return
    setIsSyncing(true)
    try {
      const d = await api.get('/devices')
      setDevices(d.data || [])
    } catch { toast('Failed to load data', 'error') }
    finally { setIsSyncing(false); setDataLoading(false) }
  }, [token, toast])

  const fetchAllMediaAssets = useCallback(async () => {
    if (!token) return
    try { const r = await api.get('/media-assets'); setAllMediaAssets(r.data || []) }
    catch { setAllMediaAssets([]) }
  }, [token])

  const fetchStorageStats = useCallback(async () => {
    if (!token) return
    try { const r = await api.get('/admin/storage/stats'); setStorageStats(r.data) }
    catch { setStorageStats(null) }
  }, [token])

  const fetchCloudinaryAccounts = useCallback(async () => {
    if (!token) return
    try { const r = await api.get('/cloudinary-accounts'); setCloudinaryAccounts(r.data || []) }
    catch { setCloudinaryAccounts([]) }
  }, [token])

  const fetchNowPlaying = useCallback(async () => {
    if (!token) return
    try { const r = await api.get('/now-playing'); setNowPlayingData(r.data || {}) }
    catch { setNowPlayingData({}) }
  }, [token])

  const fetchActivityLog = useCallback(async () => {
    if (!token) return
    try { const r = await api.get('/activity-log?limit=20'); setActivityLog(r.data || []) }
    catch { setActivityLog([]) }
  }, [token])

  const fetchCloudStorage = useCallback(async () => {
    if (!token) return
    setCloudStorageLoading(true)
    try { const r = await api.get('/admin/cloud-storage'); setCloudStorageData(r.data) }
    catch { setCloudStorageData(null) }
    finally { setCloudStorageLoading(false) }
  }, [token])

  useEffect(() => {
    if (!token) return
    fetchData(); fetchAllMediaAssets(); fetchStorageStats(); fetchCloudinaryAccounts(); fetchNowPlaying(); fetchActivityLog()
    const interval = setInterval(() => { fetchData(); fetchAllMediaAssets() }, 5 * 60 * 1000)
    const socket = io(SOCKET_URL)
    socket.on('active-devices-list', setOnlineDevices)
    socket.on('now-playing-update', (data) => setNowPlayingData(data || {}))
    return () => { socket.disconnect(); clearInterval(interval) }
  }, [token, fetchData, fetchAllMediaAssets, fetchStorageStats, fetchCloudinaryAccounts, fetchNowPlaying, fetchActivityLog])

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleLogin = async (e) => {
    e.preventDefault()
    try {
      const res = await api.post('/login', loginData)
      if (res.data.success) { localStorage.setItem('adminToken', res.data.token); setToken(res.data.token); window.location.reload() }
    } catch { toast('Invalid credentials', 'error') }
  }

  const handleLogout = () => { localStorage.removeItem('adminToken'); setToken(null); window.location.reload() }

  // ── Offline duration helper ───────────────────────────────────────────────
  const offlineDuration = (lastSeen) => {
    if (!lastSeen) return '—'
    const mins = Math.floor((Date.now() - new Date(lastSeen)) / 60000)
    if (mins < 2)  return 'Just now'
    if (mins < 60) return `${mins}m offline`
    const hrs = Math.floor(mins / 60)
    return hrs < 24 ? `${hrs}h offline` : `${Math.floor(hrs / 24)}d offline`
  }

  // ── Bulk Assign handler ───────────────────────────────────────────────────
  const handleBulkAssign = async () => {
    if (!bulkSelected.size || !bulkContent.length) return
    setBulkAssigning(true)
    try {
      const media = bulkContent.map((item, i) => ({
        fileUrl: item.cloudinaryUrl, fileType: item.fileType, duration: 10, rotation: 0,
        order: i, originalFilename: item.originalFilename || '',
        schedule: { enabled: false, startTime: '00:00', endTime: '23:59', days: [...DAYS] }
      }))
      await api.post('/devices/bulk-assign', { deviceIds: [...bulkSelected], media })
      toast(`✓ Content assigned to ${bulkSelected.size} device${bulkSelected.size > 1 ? 's' : ''}`, 'success')
      setShowBulkAssign(false); setBulkMode(false)
      setBulkSelected(new Set()); setBulkContent([]); setBulkGallerySelected(new Set())
      await fetchData()
    } catch (err) {
      toast(err.response?.data?.error || 'Bulk assign failed', 'error')
    } finally { setBulkAssigning(false) }
  }

  const toggleBulkDevice = (deviceId) => {
    setBulkSelected(prev => {
      const s = new Set(prev)
      s.has(deviceId) ? s.delete(deviceId) : s.add(deviceId)
      return s
    })
  }

  const handleCleanup = () => {
    showConfirm('Delete all orphaned Cloudinary files? This cannot be undone.', async () => {
      setCleanupLoading(true)
      try {
        const r = await api.delete('/admin/storage/cleanup')
        toast(`Cleaned! Deleted: ${r.data.deleted}, Failed: ${r.data.failed}`, 'success')
        fetchStorageStats()
      } catch (e) { toast('Cleanup failed: ' + (e.response?.data?.error || e.message), 'error') }
      finally { setCleanupLoading(false) }
    }, { title: 'Delete Orphaned Files', danger: true })
  }

  const handleAddAccount = async (e) => {
    e.preventDefault(); setAddingAccount(true)
    try {
      await api.post('/cloudinary-accounts', newAccount)
      setNewAccount({ name: '', cloudName: '', apiKey: '', apiSecret: '', folder: 'nexus-signage', isDefault: false })
      fetchCloudinaryAccounts()
      toast('Cloudinary account added!', 'success')
    } catch (err) { toast(err.response?.data?.error || 'Failed to add account', 'error') }
    finally { setAddingAccount(false) }
  }

  const handleDeleteAccount = (id) => {
    showConfirm('Delete this Cloudinary account?', async () => {
      try { await api.delete(`/cloudinary-accounts/${id}`); fetchCloudinaryAccounts(); fetchData(); toast('Account deleted', 'success') }
      catch (err) { toast(err.response?.data?.error || 'Delete failed', 'error') }
    }, { title: 'Delete Account', danger: true })
  }

  const handleSetDefaultAccount = async (id) => {
    try { await api.patch(`/cloudinary-accounts/${id}/default`); fetchCloudinaryAccounts(); toast('Default account updated', 'success') }
    catch (err) { toast(err.response?.data?.error || 'Failed', 'error') }
  }

  const handleSetDeviceCloudinaryAccount = async (deviceId, cloudinaryAccountId) => {
    try {
      await api.patch(`/devices/${encodeURIComponent(deviceId)}/cloudinary-account`, { cloudinaryAccountId: cloudinaryAccountId || null })
      fetchData()
    } catch (err) { toast(err.response?.data?.error || 'Failed', 'error') }
  }

  const handleSetDeviceGroup = async (deviceId, group) => {
    try { await api.patch(`/devices/${encodeURIComponent(deviceId)}/group`, { group: group || '' }); fetchData() }
    catch (err) { toast(err.response?.data?.error || 'Failed', 'error') }
  }

  const handleForceSync = async (deviceId) => {
    setForceSyncing(prev => new Set([...prev, deviceId]))
    try {
      await api.post(`/devices/${encodeURIComponent(deviceId)}/force-sync`)
      toast(`Force sync sent to ${deviceId}`, 'success')
      setTimeout(() => fetchData(), 3000)
    } catch (err) {
      toast(err.response?.data?.error || 'Force sync failed', 'error')
    } finally {
      setForceSyncing(prev => { const n = new Set(prev); n.delete(deviceId); return n })
    }
  }

  const handleForceLogin = async (deviceId, deviceName) => {
    setForceLoggingIn(prev => new Set([...prev, deviceId]))
    try {
      await api.post(`/devices/${encodeURIComponent(deviceId)}/force-login`)
      toast(`✅ Force login queued for "${deviceName || deviceId}". Device will auto-login within 30s.`, 'success')
    } catch (err) {
      toast(err.response?.data?.error || 'Force login failed', 'error')
    } finally {
      setForceLoggingIn(prev => { const n = new Set(prev); n.delete(deviceId); return n })
    }
  }

  const handleToggleOfflineMode = async (deviceId) => {
    setOfflineToggling(prev => new Set([...prev, deviceId]))
    try {
      const res = await api.put(`/devices/${encodeURIComponent(deviceId)}/offline-mode`)
      const isOn = res.data.offlineMode
      // Update device in local state without full refetch
      setDevices(prev => prev.map(d =>
        d.deviceId === deviceId ? { ...d, offlineMode: isOn } : d
      ))
      toast(isOn ? `📴 Offline mode ON — device will cache plays locally` : `🌐 Offline mode OFF`, 'success')
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to toggle offline mode', 'error')
    } finally {
      setOfflineToggling(prev => { const n = new Set(prev); n.delete(deviceId); return n })
    }
  }

  // ── Direct upload handler ──────────────────────────────────────────────────
  const handleUploadDirect = async () => {
    if (!uploadFiles.length) { toast('Select files first', 'warning'); return }
    setUploading(true); setUploadProgress(0)
    try {
      const fd = new FormData()
      uploadFiles.forEach(f => fd.append('files', f))
      await api.post('/upload-files', fd, {
        onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total)) }
      })
      setUploadFiles([])
      await fetchAllMediaAssets()
      toast(`${uploadFiles.length} file(s) uploaded to library!`, 'success')
    } catch (err) { toast(err.response?.data?.error || 'Upload failed', 'error') }
    finally { setUploading(false); setUploadProgress(0) }
  }

  const handlePickUploadFiles = (e) => {
    const picked = Array.from(e.target.files || [])
    setUploadFiles(prev => {
      const existing = new Set(prev.map(f => f.name + f.size))
      return [...prev, ...picked.filter(f => !existing.has(f.name + f.size))]
    })
    e.target.value = ''
  }

  const handleDeleteMediaAsset = (asset) => {
    showConfirm(
      `Delete "${asset.originalFilename || asset.cloudinaryUrl.split('/').pop()}"? This removes it from Cloudinary.`,
      async () => {
        try {
          await api.delete(`/media-assets/${asset._id}`)
          await fetchAllMediaAssets()
          fetchStorageStats()
          toast('Media deleted', 'success')
        } catch (err) { toast(err.response?.data?.error || 'Delete failed', 'error') }
      },
      { title: 'Delete Media', danger: true }
    )
  }

  // ── Device Content Modal handlers ─────────────────────────────────────────

  const openDeviceContentModal = useCallback(async (device) => {
    setDeviceContentModal(device)
    setContentGalleryOpen(false)
    setContentGallerySelected(new Set())
    // Pre-populate: use assignedMedia if set, else fall back to old playlist items
    if (device.assignedMedia?.length > 0) {
      setDeviceContent(device.assignedMedia.map(it => ({
        fileUrl: it.fileUrl,
        fileType: it.fileType,
        duration: it.duration ?? 10,
        rotation: it.rotation ?? 0,
        originalFilename: it.originalFilename || '',
        schedule: {
          enabled: !!it.schedule?.enabled,
          startTime: it.schedule?.startTime || '00:00',
          endTime: it.schedule?.endTime || '23:59',
          days: it.schedule?.days?.length ? [...it.schedule.days] : [...DAYS]
        }
      })))
    } else if (device.assignedPlaylist?.items?.length > 0) {
      setDeviceContent(device.assignedPlaylist.items.map(it => ({
        fileUrl: it.fileUrl,
        fileType: it.fileType,
        duration: it.duration ?? 10,
        rotation: it.rotation ?? 0,
        originalFilename: '',
        schedule: { enabled: false, startTime: '00:00', endTime: '23:59', days: [...DAYS] }
      })))
    } else {
      setDeviceContent([])
    }
    // allMediaAssets is already loaded globally — refresh if empty
    if (allMediaAssets.length === 0) fetchAllMediaAssets()
  }, [allMediaAssets.length, fetchAllMediaAssets])

  // Lazy-load cloud storage breakdown when the user navigates there
  const viewRef = useRef(null)
  useEffect(() => {
    if (view === 'storage' && viewRef.current !== 'storage') fetchCloudStorage()
    viewRef.current = view
  }, [view, fetchCloudStorage])

  // ── Offline Alert: toast when device drops from onlineDevices list ────────
  useEffect(() => {
    const prev = prevOnlineRef.current
    const wentOffline = prev.filter(id => !onlineDevices.includes(id))
    wentOffline.forEach(id => {
      const dev = devices.find(d => d.deviceId === id)
      toast(`⚠ ${dev?.deviceName || id} went offline`, 'error')
    })
    prevOnlineRef.current = onlineDevices
  }, [onlineDevices]) // eslint-disable-line react-hooks/exhaustive-deps

  const saveDeviceContent = async () => {
    if (!deviceContentModal) return
    setDeviceContentSaving(true)
    try {
      await api.put(`/devices/${encodeURIComponent(deviceContentModal.deviceId)}/content`, {
        media: deviceContent
      })
      setDeviceContentModal(null)
      await fetchData()
      toast('Content saved!', 'success')
    } catch (err) {
      toast(err.response?.data?.error || 'Could not save content', 'error')
    } finally {
      setDeviceContentSaving(false)
    }
  }

  const updateContentItem = (idx, patch) =>
    setDeviceContent(prev => { const a = [...prev]; a[idx] = { ...a[idx], ...patch }; return a })

  const updateContentSchedule = (idx, patch) =>
    setDeviceContent(prev => {
      const a = [...prev]
      a[idx] = { ...a[idx], schedule: { ...a[idx].schedule, ...patch } }
      return a
    })

  const removeContentItem = (idx) => {
    setDeviceContent(prev => prev.filter((_, i) => i !== idx))
  }

  const toggleContentDay = (idx, day) =>
    setDeviceContent(prev => {
      const a = [...prev]
      const days = [...(a[idx].schedule?.days || DAYS)]
      const i = days.indexOf(day)
      if (i >= 0) days.splice(i, 1); else days.push(day)
      a[idx] = { ...a[idx], schedule: { ...a[idx].schedule, days: days.length ? days : [...DAYS] } }
      return a
    })

  const handleContentDragStart = (idx) => { contentDragItem.current = idx }
  const handleContentDragEnter = (idx) => { contentDragOver.current = idx }
  const handleContentDragEnd = () => {
    const from = contentDragItem.current; const to = contentDragOver.current
    if (from === null || to === null || from === to) { contentDragItem.current = null; contentDragOver.current = null; return }
    setDeviceContent(prev => {
      const a = [...prev]; const dragged = a.splice(from, 1)[0]; a.splice(to, 0, dragged); return a
    })
    contentDragItem.current = null; contentDragOver.current = null
  }

  const addGalleryItemsToContent = () => {
    const toAdd = allMediaAssets.filter(a => contentGallerySelected.has(a._id || a.cloudinaryUrl))
    setDeviceContent(prev => {
      const existingUrls = new Set(prev.map(it => it.fileUrl))
      const fresh = toAdd
        .filter(a => !existingUrls.has(a.cloudinaryUrl))
        .map(a => ({
          fileUrl: a.cloudinaryUrl,
          fileType: a.fileType,
          duration: 10,
          rotation: 0,
          originalFilename: a.originalFilename || '',
          schedule: { enabled: false, startTime: '00:00', endTime: '23:59', days: [...DAYS] }
        }))
      return [...prev, ...fresh]
    })
    setContentGallerySelected(new Set())
    setContentGalleryOpen(false)
    toast(`Added ${toAdd.length} item(s)`, 'success')
  }

  const handleBroadcast = async () => {
    if (!broadcastMsg.trim()) { toast('Enter a message first', 'warning'); return }
    setBroadcastSending(true)
    try {
      await api.post('/broadcast', { message: broadcastMsg.trim(), type: broadcastType })
      toast('Broadcast sent to all screens!', 'success')
      setShowBroadcast(false); setBroadcastMsg('')
    } catch (err) { toast(err.response?.data?.error || 'Broadcast failed', 'error') }
    finally { setBroadcastSending(false) }
  }

  const copyDeviceId = (id) => {
    if (!id) return
    const done = () => { setCopiedDeviceId(id); setTimeout(() => setCopiedDeviceId(null), 2000) }
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(id).then(done).catch(() => window.prompt('Device ID:', id))
    else window.prompt('Device ID:', id)
  }

  const handleRegister = async (e) => {
    e.preventDefault()
    try {
      await api.post('/devices/register', { deviceId: newDevice.id, deviceName: newDevice.name })
      if (newDevice.group) {
        await api.patch(`/devices/${encodeURIComponent(newDevice.id)}/group`, { group: newDevice.group }).catch(() => { })
      }
      setShowModal(false); setNewDevice({ id: '', name: '', group: '' }); fetchData()
      toast('Device registered!', 'success')
    } catch { toast('Registration failed', 'error') }
  }

  const handleDeleteDevice = (deviceId) => {
    showConfirm('Remove this device permanently?', async () => {
      try { await api.delete(`/devices/${deviceId}`); fetchData(); toast('Device removed', 'success') }
      catch { toast('Delete failed', 'error') }
    }, { title: 'Delete Device', danger: true })
  }

  const issuePlayerJwt = async (deviceId) => {
    try {
      const r = await api.post(`/devices/${encodeURIComponent(deviceId)}/player-token`)
      if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(r.data.token); toast(`Player JWT copied! (${r.data.expiresIn || ''})`, 'success') }
      else window.prompt('Copy JWT:', r.data.token)
    } catch { toast('Could not issue JWT. Register device first.', 'error') }
  }


  const downloadReport = async () => {
    try {
      setIsSyncing(true)
      const { data: list } = await api.get('/devices')
      const headers = ['Device ID', 'Location', 'IP', 'Plays', 'Status', 'Last Active', 'Duration', 'Playlist']
      const rows = list.map(d => {
        const online = onlineDevices.includes(d.deviceId)
        const last = d.lastSeen ? new Date(d.lastSeen) : null
        const now = new Date()
        let dur = online ? 'Currently Active' : !last ? 'Unknown' : (() => { const m = Math.floor(Math.max(0, now - last) / 60000); const h = Math.floor(m / 60); return h > 0 ? `${h}h ${m % 60}m ago` : m < 1 ? 'Just offline' : `${m}m ago` })()
        return [`"${d.deviceId}"`, `"${d.deviceName || 'Unnamed'}"`, `"${d.ipAddress || '0.0.0.0'}"`, `"${d.playCount || 0}"`, `"${online ? 'ONLINE' : 'OFFLINE'}"`, `"${last ? last.toLocaleString('en-IN') : '—'}"`, `"${dur}"`, `"${d.assignedPlaylist?.name || 'No Playlist'}"`].join(',')
      })
      const blob = new Blob([[headers.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' })
      const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `Nexus_Audit_${new Date().toISOString().split('T')[0]}.csv` })
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      toast('Device audit CSV downloaded!', 'success')
    } catch { toast('Export failed', 'error') } finally { setIsSyncing(false) }
  }



  const generateReport = async () => {
    if (reportType === 'playerwise' && !reportDeviceId) { toast('Select a device first', 'warning'); return }
    setReportLoading(true); setReportData(null)
    try {
      const p = new URLSearchParams()
      if (reportFrom) p.append('from', reportFrom)
      if (reportTo) p.append('to', reportTo)
      if (reportType === 'playerwise') p.append('deviceId', reportDeviceId)
      if (reportType === 'groupwise' && reportGroup) p.append('group', reportGroup)
      const endpointMap = { summarized: 'summarized', playerwise: 'playerwise', groupwise: 'groupwise', mediawise: 'mediawise' }
      const { data } = await api.get(`/reports/${endpointMap[reportType]}?${p}`)
      setReportData({ type: reportType, rows: data })
    } catch (err) { toast(err.response?.data?.error || 'Report failed', 'error') }
    finally { setReportLoading(false) }
  }

  const exportReportCSV = () => {
    if (!reportData?.rows?.length) return
    const { type, rows } = reportData
    const now = new Date().toLocaleString('en-IN')
    const dateRange = reportFrom || reportTo ? `${reportFrom || 'Start'} → ${reportTo || 'Today'}` : 'All Time'
    // Use originalFilename first; fall back to last segment of URL
    const getFileName = (r) => {
      if (r.originalFilename) return r.originalFilename
      try { return decodeURIComponent(new URL(r.fileUrl).pathname.split('/').filter(Boolean).pop() || '—') } catch { return '—' }
    }
    const fmtPlayTime = (secs) => { const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60; return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s` }
    let headers, lines

    if (type === 'summarized') {
      const totalPlays = rows.reduce((s, r) => s + r.totalPlays, 0)
      headers = ['Rank', 'Device Name', 'Zone', 'Total Plays', '% of Total', 'Unique Media', 'Avg Plays/Media']
      lines = rows.map((r, i) => [`#${i + 1}`, `"${r.deviceName}"`, `"${r.group}"`, r.totalPlays, totalPlays > 0 ? `${((r.totalPlays / totalPlays) * 100).toFixed(1)}%` : '0%', r.uniqueMedia, r.uniqueMedia > 0 ? Math.round(r.totalPlays / r.uniqueMedia) : 0].join(','))
      lines.push(''); lines.push(`"TOTAL",,"","${totalPlays.toLocaleString()}","100%","${rows.reduce((s, r) => s + r.uniqueMedia, 0)}",`)
    } else if (type === 'playerwise') {
      const totalPlays = rows.reduce((s, r) => s + r.plays, 0)
      headers = ['Rank', 'File Name', 'Type', 'Duration', 'Plays', '% of Total', 'Total Air Time', 'Last Played']
      lines = rows.map((r, i) => [`#${i + 1}`, `"${getFileName(r)}"`, r.fileType, `${r.duration}s`, r.plays, totalPlays > 0 ? `${((r.plays / totalPlays) * 100).toFixed(1)}%` : '0%', `"${fmtPlayTime(r.plays * r.duration)}"`, r.lastPlayedAt ? new Date(r.lastPlayedAt).toLocaleString('en-IN') : '—'].join(','))
      lines.push(''); lines.push(`"TOTAL",,,"","${totalPlays}","100%","${fmtPlayTime(rows.reduce((s, r) => s + r.plays * r.duration, 0))}"`)
    } else if (type === 'groupwise') {
      const totalPlays = rows.reduce((s, r) => s + r.totalPlays, 0)
      headers = ['Rank', 'Zone / Group', 'Devices', 'Total Plays', '% of Total', 'Unique Media', 'Avg Plays/Device']
      lines = rows.map((r, i) => [`#${i + 1}`, `"${r.group}"`, r.deviceCount, r.totalPlays, totalPlays > 0 ? `${((r.totalPlays / totalPlays) * 100).toFixed(1)}%` : '0%', r.uniqueMedia, r.deviceCount > 0 ? Math.round(r.totalPlays / r.deviceCount) : 0].join(','))
    } else {
      // mediawise
      const totalPlays = rows.reduce((s, r) => s + r.totalPlays, 0)
      headers = ['Rank', 'File Name', 'Type', 'Duration', 'Total Plays', '% of Total', 'Devices', 'Total Air Time', 'Last Played']
      lines = rows.map((r, i) => [`#${i + 1}`, `"${getFileName(r)}"`, r.fileType, `${r.duration}s`, r.totalPlays, totalPlays > 0 ? `${((r.totalPlays / totalPlays) * 100).toFixed(1)}%` : '0%', r.deviceCount, `"${fmtPlayTime(r.totalPlays * r.duration)}"`, r.lastPlayedAt ? new Date(r.lastPlayedAt).toLocaleString('en-IN') : '—'].join(','))
      lines.push(''); lines.push(`"TOTAL",,,"","${totalPlays}","100%","","${fmtPlayTime(rows.reduce((s, r) => s + r.totalPlays * r.duration, 0))}"`)
    }
    const meta = [`"Nexus Esplanade Mall — ${type.charAt(0).toUpperCase() + type.slice(1)} Report"`, `"Generated: ${now}"`, `"Date Range: ${dateRange}"`, `"Total Records: ${rows.length}"`, '']
    const blob = new Blob([[...meta, headers.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' })
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `Nexus_${type}_${reportFrom || 'all'}_to_${reportTo || 'today'}.csv` })
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    toast('Report exported!', 'success')
  }


  // ── Derived ────────────────────────────────────────────────────────────────
  const allGroups = [...new Set(devices.map(d => d.group).filter(Boolean))].sort()
  const filteredDevices = groupFilter === 'all' ? devices : devices.filter(d => d.group === groupFilter)

  // ── Login ──────────────────────────────────────────────────────────────────

  if (!token) {
    return (
      <div className="min-h-screen flex" style={{ background: 'radial-gradient(ellipse 80% 60% at 10% 20%,rgba(99,102,241,0.2) 0%,transparent 60%), radial-gradient(ellipse 60% 50% at 90% 80%,rgba(16,185,129,0.12) 0%,transparent 55%), #030b1a' }}>
        {/* Left panel */}
        <div className="hidden lg:flex flex-col justify-between w-1/2 p-16 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#0f0a2e 0%,#1a0a4e 30%,#0d1a4a 60%,#051225 100%)' }}>
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle,rgba(99,102,241,0.25),transparent 70%)' }} />
          <div className="absolute -bottom-32 -left-20 w-72 h-72 rounded-full" style={{ background: 'radial-gradient(circle,rgba(16,185,129,0.15),transparent 70%)' }} />
          <div className="relative">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-10 shadow-2xl" style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', boxShadow: '0 8px 32px rgba(99,102,241,0.6)' }}>
              <Layout size={26} className="text-white" />
            </div>
            <h1 className="text-6xl font-black leading-[1.05] tracking-tight">Nexus<br />Esplanade<br /><span style={{ background: 'linear-gradient(135deg,#818cf8,#34d399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Mall</span></h1>
            <p className="text-indigo-300/70 mt-5 text-lg font-medium">Digital Signage Command Center</p>
          </div>
          <div className="relative space-y-4">
            {[
              ['Real-time monitoring', 'Watch all screens live with instant status'],
              ['Smart scheduling', 'Per-video time windows and day-of-week control'],
              ['Multi-account storage', 'Multiply free Cloudinary credits across accounts'],
              ['Emergency broadcast', 'Push alerts to all screens in one click'],
            ].map(([t, d]) => (
              <div key={t} className="flex items-start gap-4">
                <div className="w-7 h-7 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(99,102,241,0.25)', border: '1px solid rgba(99,102,241,0.4)' }}>
                  <CheckCircle2 size={14} className="text-indigo-300" />
                </div>
                <div>
                  <p className="font-bold text-sm text-white">{t}</p>
                  <p className="text-indigo-400/60 text-xs mt-0.5">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right panel */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-sm">
            <div className="lg:hidden text-center mb-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl" style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', boxShadow: '0 8px 32px rgba(99,102,241,0.5)' }}>
                <ShieldCheck size={28} className="text-white" />
              </div>
              <h1 className="text-2xl font-black text-white">Nexus Esplanade</h1>
            </div>
            <div className="rounded-3xl p-8" style={{ background: 'rgba(13,20,45,0.8)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 32px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)' }}>
              <div className="mb-7">
                <h2 className="text-2xl font-black text-white mb-1">Welcome back</h2>
                <p className="text-slate-500 text-sm">Sign in to your control panel</p>
              </div>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Username</label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-600" size={15} />
                    <input required className={`${inputCls} glass-input pl-10`} placeholder="Enter username" onChange={e => setLoginData({ ...loginData, username: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-600" size={15} />
                    <input required type="password" className={`${inputCls} glass-input pl-10`} placeholder="Enter password" onChange={e => setLoginData({ ...loginData, password: e.target.value })} />
                  </div>
                </div>
                <button type="submit" className="w-full text-white font-bold py-3.5 rounded-xl active:scale-[0.98] transition-all flex items-center justify-center gap-2 mt-2" style={{ background: 'linear-gradient(135deg,#4338ca,#4f46e5,#6366f1)', boxShadow: '0 8px 32px rgba(99,102,241,0.5)' }}>
                  Sign In <Zap size={15} />
                </button>
              </form>
            </div>
            <p className="text-center text-xs text-slate-700 mt-5">Nexus Esplanade Mall · Bhubaneswar</p>
          </div>
        </div>
      </div>
    )
  }

  // ── Page Content ───────────────────────────────────────────────────────────

  const renderContent = () => {
    switch (view) {

      // ── Devices ────────────────────────────────────────────────────────────
      case 'devices': {
        const online = onlineDevices.length
        const offline = devices.length - online
        return (
          <div className="space-y-6" style={{ animation: 'fadeInUp 0.35s ease-out' }}>

            {/* Mini stat strip */}
            <div className="grid grid-cols-3 gap-4">
              <StatCard icon={Wifi}    label="Online Screens"  value={online}            sub={`of ${devices.length} total`} color="green"  loading={dataLoading} delay={0}   />
              <StatCard icon={WifiOff} label="Offline Screens" value={offline}           color="red"    loading={dataLoading} delay={0.5} />
              <StatCard icon={Monitor} label="Total Registered" value={devices.length}  color="blue"   loading={dataLoading} delay={1}   />
            </div>

            {/* Zone / group filter */}
            {allGroups.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 text-[10px] font-black text-slate-600 uppercase tracking-widest mr-2">
                  <Activity size={11} /> Zone
                </div>
                {['all', ...allGroups].map(g => (
                  <button key={g} onClick={() => setGroupFilter(g)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-all duration-200 ${groupFilter === g ? 'text-white' : 'text-slate-500 hover:text-slate-200'}`}
                    style={groupFilter === g
                      ? { background: 'linear-gradient(135deg,#3730a3,#4f46e5)', boxShadow: '0 4px 16px rgba(99,102,241,0.4)' }
                      : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    {g === 'all' ? 'All Screens' : g}
                    {g !== 'all' && <span className={`ml-1.5 text-[10px] ${groupFilter === g ? 'opacity-70' : 'opacity-40'}`}>{devices.filter(d => d.group === g).length}</span>}
                  </button>
                ))}
              </div>
            )}

            {/* Device table with full management */}
            <SectionCard
              title="Screen Nodes"
              sub={`${filteredDevices.length}${groupFilter !== 'all' ? ` in "${groupFilter}"` : `/${devices.length}`} device${devices.length !== 1 ? 's' : ''}`}
              action={
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)' }}>
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-xs font-bold text-emerald-300">{online} live</span>
                  </div>
                  <button onClick={() => { setBulkMode(b => !b); setBulkSelected(new Set()) }}
                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-all"
                    style={bulkMode
                      ? { background: 'rgba(99,102,241,0.25)', border: '1px solid rgba(99,102,241,0.5)', color: '#a5b4fc' }
                      : { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#64748b' }}
                    title="Toggle bulk-select mode">
                    <CheckSquare size={12} /> {bulkMode ? 'Exit Bulk' : 'Bulk Assign'}
                  </button>
                  <button onClick={() => setShowModal(true)}
                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-all"
                    style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7' }}>
                    <Plus size={12} /> Add Device
                  </button>
                </div>
              }
            >
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.03)' }}>
                      {bulkMode && <th className={thCls} style={{ width: 40 }}><input type="checkbox" className="accent-indigo-500" checked={bulkSelected.size === filteredDevices.length && filteredDevices.length > 0} onChange={e => setBulkSelected(e.target.checked ? new Set(filteredDevices.map(d => d.deviceId)) : new Set())} /></th>}
                      <th className={thCls}>Device</th>
                      <th className={thCls}>Content</th>
                      <th className={thCls}>Cloud Account</th>
                      <th className={thCls}>Status</th>
                      <th className={`${thCls} text-center`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataLoading ? [1, 2, 3].map(i => <SkeletonRow key={i} />) : filteredDevices.length ? filteredDevices.map(device => {
                      const isOnline = onlineDevices.includes(device.deviceId)
                      const { color, initials } = deviceAvatar(device.deviceName, device.deviceId)
                      return (
                        <tr key={device.deviceId} className={`transition-all group relative ${isOnline ? 'neon-row-green' : 'neon-row'} ${bulkMode && bulkSelected.has(device.deviceId) ? 'ring-1 ring-indigo-500/40' : ''}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          {bulkMode && <td className={tdCls} style={{ width: 40 }}><input type="checkbox" className="accent-indigo-500 cursor-pointer" checked={bulkSelected.has(device.deviceId)} onChange={() => toggleBulkDevice(device.deviceId)} /></td>}
                          <td className={`${tdCls} relative`}>
                            <div className={`absolute left-0 top-3 bottom-3 w-0.5 rounded-r-full transition-all ${isOnline ? 'bg-emerald-400' : 'bg-rose-500/50'}`} style={isOnline ? { boxShadow: '0 0 8px rgba(16,185,129,0.8)' } : {}} />
                            <div className="flex items-center gap-3 pl-2">
                              <div className={`w-10 h-10 ${color} rounded-2xl flex items-center justify-center shrink-0 shadow-lg`} style={{ boxShadow: `0 4px 16px rgba(0,0,0,0.4)` }}>
                                <span className="text-white font-black text-xs">{initials}</span>
                              </div>
                              <div>
                                <p className="font-bold text-slate-100 text-sm group-hover:text-white transition-colors">{device.deviceName || 'Unnamed'}</p>
                                <button onClick={() => copyDeviceId(device.deviceId)} title="Click to copy"
                                  className={`text-[10px] font-mono truncate max-w-[160px] text-left transition-colors ${copiedDeviceId === device.deviceId ? 'text-emerald-400 font-bold' : 'text-slate-700 hover:text-indigo-400'}`}>
                                  {copiedDeviceId === device.deviceId ? '✓ Copied!' : device.deviceId}
                                </button>
                                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                  <p className="text-[11px] text-slate-600 font-mono">{device.ipAddress || '0.0.0.0'}</p>
                                  <input
                                    list={`zones-${device.deviceId}`}
                                    defaultValue={device.group || ''}
                                    placeholder="+ zone"
                                    className="text-[10px] font-bold text-violet-300 rounded-full px-2 py-0.5 outline-none cursor-pointer w-20"
                                    style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.25)' }}
                                    onBlur={e => { const val = e.target.value.trim(); if (val !== (device.group || '')) handleSetDeviceGroup(device.deviceId, val) }}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') { handleSetDeviceGroup(device.deviceId, e.target.value.trim()); e.target.blur() }
                                      if (e.key === 'Escape') { e.target.value = device.group || ''; e.target.blur() }
                                    }}
                                  />
                                  <datalist id={`zones-${device.deviceId}`}>
                                    <option value="" />
                                    {allGroups.map(g => <option key={g} value={g} />)}
                                  </datalist>
                                  {device.offlineMode && (
                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(6,182,212,0.12)', color: '#22d3ee', border: '1px solid rgba(6,182,212,0.25)' }}>📴 OFFLINE</span>
                                  )}
                                  {device.offlineMode && device.lastSyncAt && (
                                    <span className="text-[9px] text-slate-600" title={new Date(device.lastSyncAt).toLocaleString()}>
                                      {(() => { const m = Math.floor((Date.now() - new Date(device.lastSyncAt)) / 60000); return `synced ${m < 2 ? 'just now' : m < 60 ? `${m}m ago` : `${Math.floor(m/60)}h ago`}` })()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className={tdCls}>
                            <button onClick={() => openDeviceContentModal(device)}
                              className="flex items-center gap-2 px-3 py-2 rounded-xl border transition-all text-left group w-full max-w-[200px]"
                              style={device.assignedMedia?.length > 0
                                ? { background: 'rgba(109,40,217,0.2)', borderColor: 'rgba(139,92,246,0.35)', color: '#c4b5fd' }
                                : device.assignedPlaylist?._id
                                ? { background: 'rgba(5,150,105,0.15)', borderColor: 'rgba(52,211,153,0.3)', color: '#6ee7b7' }
                                : { background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)', color: '#475569' }}
                            >
                              <Film size={13} className="shrink-0" />
                              <div className="min-w-0">
                                {device.assignedMedia?.length > 0 ? (
                                  <><p className="text-[11px] font-black truncate">{device.assignedMedia.length} item{device.assignedMedia.length !== 1 ? 's' : ''}</p><p className="text-[10px] opacity-70 truncate">{device.assignedMedia.some(it => it.schedule?.enabled) ? '⏰ Scheduled' : 'Always on'}</p></>
                                ) : device.assignedPlaylist?.name ? (
                                  <><p className="text-[11px] font-black truncate">{device.assignedPlaylist.name}</p><p className="text-[10px] opacity-60">Legacy playlist</p></>
                                ) : (
                                  <p className="text-[11px] font-semibold">No content — click to add</p>
                                )}
                              </div>
                            </button>
                          </td>
                          <td className={tdCls}>
                            <select className="glass-select text-xs font-semibold py-2 px-3 rounded-lg outline-none cursor-pointer w-full max-w-[160px]" value={device.cloudinaryAccount || ''} onChange={e => handleSetDeviceCloudinaryAccount(device.deviceId, e.target.value)}>
                              <option value="">Default</option>
                              {cloudinaryAccounts.map(a => <option key={a._id} value={a._id}>{a.name}{a.isDefault ? ' ★' : ''}</option>)}
                            </select>
                          </td>
                          <td className={tdCls}>
                            {isOnline ? (
                              <div>
                                <Badge color="green"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />ONLINE</Badge>
                                <p className="text-[11px] text-slate-500 mt-1.5 font-mono">{device.playCount || 0} plays</p>
                              </div>
                            ) : (
                              <div>
                                <Badge color="red">OFFLINE</Badge>
                                <p className="text-[11px] font-bold mt-1.5" style={{ color: '#f87171' }}>{offlineDuration(device.lastSeen)}</p>
                              </div>
                            )}
                            <div className="mt-2">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wide">Cache</span>
                                <div className="flex items-center gap-1.5">
                                  {device.syncStatus?.total > 0 && (
                                    <span className={`text-[10px] font-bold ${device.syncStatus.downloaded === device.syncStatus.total ? 'text-emerald-400' : 'text-amber-400'}`}>
                                      {device.syncStatus.downloaded}/{device.syncStatus.total}
                                    </span>
                                  )}
                                  <button onClick={() => handleForceSync(device.deviceId)}
                                    disabled={forceSyncing.has(device.deviceId) || (!device.assignedPlaylist?._id && !device.assignedMedia?.length)}
                                    title={(device.assignedPlaylist?._id || device.assignedMedia?.length) ? 'Force re-download all files' : 'No content assigned'}
                                    className="p-0.5 rounded text-slate-600 hover:text-indigo-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                                    <RefreshCcw size={11} className={forceSyncing.has(device.deviceId) ? 'animate-spin' : ''} />
                                  </button>
                                </div>
                              </div>
                              {device.syncStatus?.total > 0 && (
                                <div className="h-1 rounded-full w-20 overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                                  <div className={`h-full rounded-full transition-all duration-500 ${device.syncStatus.downloaded === device.syncStatus.total ? 'bg-emerald-400' : 'bg-amber-400'}`}
                                    style={{ width: `${Math.round((device.syncStatus.downloaded / device.syncStatus.total) * 100)}%` }} />
                                </div>
                              )}
                            </div>
                          </td>
                          <td className={`${tdCls} text-center`}>
                            <div className="flex items-center justify-center gap-1.5">
                              <button onClick={() => setPreviewDevice(device)} title="Live Preview" className="p-2 rounded-lg text-slate-500 hover:text-emerald-300 transition-all" style={{ background: 'rgba(255,255,255,0.05)' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(16,185,129,0.15)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}><PlayCircle size={14} /></button>
                              <button onClick={() => openDeviceContentModal(device)} title="Edit Content" className="p-2 rounded-lg text-slate-500 hover:text-violet-300 transition-all" style={{ background: 'rgba(255,255,255,0.05)' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(139,92,246,0.2)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}><Eye size={14} /></button>
                              <button onClick={() => copyDeviceId(device.deviceId)} title="Copy ID" className={`p-2 rounded-lg transition-all ${copiedDeviceId === device.deviceId ? 'text-emerald-400' : 'text-slate-500 hover:text-blue-300'}`} style={{ background: copiedDeviceId === device.deviceId ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.05)' }} onMouseEnter={e=>{ if(copiedDeviceId!==device.deviceId) e.currentTarget.style.background='rgba(59,130,246,0.15)' }} onMouseLeave={e=>{ if(copiedDeviceId!==device.deviceId) e.currentTarget.style.background='rgba(255,255,255,0.05)' }}><Monitor size={14} /></button>
                              <button onClick={() => issuePlayerJwt(device.deviceId)} title="Issue JWT" className="p-2 rounded-lg text-slate-500 hover:text-amber-300 transition-all" style={{ background: 'rgba(255,255,255,0.05)' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(245,158,11,0.15)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}><KeyRound size={14} /></button>
                              <button onClick={() => handleForceLogin(device.deviceId, device.deviceName)} disabled={forceLoggingIn.has(device.deviceId)} title="Force Login — remotely log in this device" className="p-2 rounded-lg text-slate-500 hover:text-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all" style={{ background: 'rgba(255,255,255,0.05)' }} onMouseEnter={e=>{ if(!forceLoggingIn.has(device.deviceId)) e.currentTarget.style.background='rgba(6,182,212,0.15)' }} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}>{forceLoggingIn.has(device.deviceId) ? <RefreshCcw size={14} className="animate-spin"/> : <LogIn size={14} />}</button>
                              <button onClick={() => handleToggleOfflineMode(device.deviceId)} disabled={offlineToggling.has(device.deviceId)} title={device.offlineMode ? 'Offline mode ON — click to disable' : 'Enable offline mode for this device'} className={`p-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all ${device.offlineMode ? 'text-cyan-400' : 'text-slate-500 hover:text-cyan-400'}`} style={{ background: device.offlineMode ? 'rgba(6,182,212,0.15)' : 'rgba(255,255,255,0.05)', border: device.offlineMode ? '1px solid rgba(6,182,212,0.3)' : '1px solid transparent' }} onMouseEnter={e=>{ if(!offlineToggling.has(device.deviceId) && !device.offlineMode) e.currentTarget.style.background='rgba(6,182,212,0.1)' }} onMouseLeave={e=>{ if(!device.offlineMode) e.currentTarget.style.background='rgba(255,255,255,0.05)' }}>{offlineToggling.has(device.deviceId) ? <RefreshCcw size={14} className="animate-spin"/> : device.offlineMode ? <WifiOff size={14} /> : <Wifi size={14} />}</button>
                              <button onClick={() => handleDeleteDevice(device.deviceId)} title="Delete" className="p-2 rounded-lg text-slate-500 hover:text-rose-300 transition-all" style={{ background: 'rgba(255,255,255,0.05)' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(244,63,94,0.15)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}><Trash2 size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      )
                    }) : (
                      <tr><td colSpan={bulkMode ? 6 : 5} className="px-6 py-16 text-center">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(255,255,255,0.05)' }}><Monitor className="text-slate-600" size={32} /></div>
                        <p className="text-slate-500 font-bold">No devices{groupFilter !== 'all' ? ` in "${groupFilter}"` : ''}</p>
                        <p className="text-slate-700 text-xs mt-1">Click <strong className="text-slate-500">Add Device</strong> to register a screen</p>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </div>
        )
      }

      // ── Dashboard ──────────────────────────────────────────────────────────
      case 'home': {
        const online = onlineDevices.length
        const offline = devices.length - online
        return (
          <div className="space-y-6" style={{ animation: 'fadeInUp 0.35s ease-out' }}>

            {/* Welcome banner */}
            <div className="relative overflow-hidden rounded-3xl shadow-2xl" style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e3a8a 40%,#312e81 70%,#1e1b4b 100%)' }}>
              {/* Decorative background elements */}
              <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.02) 1px,transparent 1px)', backgroundSize: '32px 32px' }} />
              <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle,rgba(129,140,248,0.3),transparent 70%)' }} />
              <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full" style={{ background: 'radial-gradient(circle,rgba(56,189,248,0.2),transparent 70%)' }} />
              <div className="absolute top-0 left-1/2 w-96 h-96 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: 'radial-gradient(circle,rgba(99,102,241,0.15),transparent 70%)' }} />
              <div className="relative px-8 py-8 flex flex-col sm:flex-row sm:items-center gap-6">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                      </span>
                    </div>
                    <span className="text-indigo-300 text-xs font-bold uppercase tracking-widest">Nexus Esplanade Mall</span>
                  </div>
                  <h2 className="text-3xl font-black text-white leading-tight">Control Center</h2>
                  <p className="text-indigo-200/70 text-sm mt-2">
                    {devices.length} screens registered &nbsp;·&nbsp;
                    <span className="text-emerald-300 font-bold">{online} live</span>
                    {offline > 0 && <>&nbsp;·&nbsp;<span className="text-rose-300 font-bold">{offline} offline</span></>}
                    {allGroups.length > 0 && <>&nbsp;·&nbsp;<span className="text-sky-300">{allGroups.length} zones</span></>}
                  </p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <p className="text-indigo-400 text-[10px] font-bold uppercase tracking-widest mb-1">Network Health</p>
                    <p className="text-white font-black text-4xl leading-none">{devices.length > 0 ? Math.round((online / devices.length) * 100) : 0}<span className="text-xl text-indigo-300">%</span></p>
                    <p className="text-indigo-300/60 text-xs mt-1">uptime</p>
                  </div>
                  <div className="w-px h-16 bg-white/10 hidden sm:block" />
                  <div className="text-right">
                    <p className="text-indigo-400 text-[10px] font-bold uppercase tracking-widest mb-1">Live Now</p>
                    <p className="text-emerald-400 font-black text-4xl leading-none">{online}</p>
                    <p className="text-indigo-300/60 text-xs mt-1">screens</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard icon={Wifi} label="Online Screens" value={online} sub={`of ${devices.length} total`} color="green" loading={dataLoading} delay={0} />
              <StatCard icon={WifiOff} label="Offline Screens" value={offline} color="red" loading={dataLoading} delay={0.5} />
              <StatCard icon={Images} label="Media Files" value={allMediaAssets.length} color="blue" loading={dataLoading} delay={1} />
              <StatCard icon={HardDrive} label="Storage" value={storageStats ? `${storageStats.totalMB}MB` : '—'} sub={storageStats ? `${storageStats.totalAssets} files` : ''} color="purple" loading={dataLoading} delay={1.5} />
            </div>

            {/* Now Playing — live on-air grid */}
            <div className="relative overflow-hidden rounded-3xl p-6" style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1a1035 50%,#0f172a 100%)', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-20" style={{ background: 'radial-gradient(circle,#e11d48,transparent 70%)' }} />
              <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full opacity-15" style={{ background: 'radial-gradient(circle,#7c3aed,transparent 70%)' }} />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-2.5 h-2.5 rounded-full ${Object.keys(nowPlayingData).length > 0 ? 'bg-rose-500 animate-pulse' : 'bg-slate-600'}`} />
                  <span className="text-white font-black text-sm uppercase tracking-widest">Currently On Air</span>
                  {Object.keys(nowPlayingData).length > 0
                    ? <span className="text-rose-400 text-xs font-bold bg-rose-500/20 px-2.5 py-1 rounded-full ml-1">{Object.keys(nowPlayingData).length} live feed{Object.keys(nowPlayingData).length !== 1 ? 's' : ''}</span>
                    : <span className="text-slate-600 text-xs font-bold ml-1">No active feeds</span>
                  }
                  <button onClick={fetchNowPlaying} title="Refresh" className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-all text-slate-400 hover:text-white ml-auto"><RefreshCcw size={11} /></button>
                </div>
                {Object.keys(nowPlayingData).length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {Object.entries(nowPlayingData).map(([dId, info]) => {
                      const dev = devices.find(d => d.deviceId === dId)
                      const isOnline = onlineDevices.includes(dId)
                      return (
                        <div key={dId} className="rounded-2xl overflow-hidden border border-white/5 bg-white/5 group cursor-default hover:bg-white/10 transition-all">
                          <div className="relative aspect-video bg-black overflow-hidden">
                            {info.fileType === 'video'
                              ? <video src={info.fileUrl} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-all" muted playsInline />
                              : <img src={info.fileUrl} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-all" alt="" />
                            }
                            <div className="absolute top-1.5 left-1.5">
                              <span className={`flex items-center gap-1 text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full ${isOnline ? 'bg-emerald-500/80 text-white' : 'bg-rose-500/80 text-white'}`}>
                                <Radio size={7} className={isOnline ? 'animate-pulse' : ''} />{isOnline ? 'LIVE' : 'OFF'}
                              </span>
                            </div>
                            <div className="absolute bottom-1.5 right-1.5">
                              <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full bg-black/60 text-white">{info.fileType}</span>
                            </div>
                          </div>
                          <div className="p-2.5">
                            <p className="text-white font-bold text-[11px] truncate">{dev?.deviceName || info.deviceName || dId}</p>
                            <p className="text-slate-500 text-[10px] font-mono truncate">{info.fileUrl.split('/').pop()}</p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center border border-white/5">
                      <Radio size={22} className="text-slate-600" />
                    </div>
                    <p className="text-slate-500 text-sm font-bold">No screens broadcasting right now</p>
                    <p className="text-slate-600 text-xs">When a player app goes live, its feed will appear here in real-time</p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick link to Devices tab */}
            <button
              onClick={() => setView('devices')}
              className="w-full flex items-center justify-between px-6 py-4 rounded-2xl transition-all group"
              style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.15)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(99,102,241,0.08)'}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.2)' }}>
                  <Monitor size={16} className="text-indigo-400" />
                </div>
                <div className="text-left">
                  <p className="text-white font-bold text-sm">Manage Devices</p>
                  <p className="text-slate-500 text-xs mt-0.5">{devices.length} screens · <span className="text-emerald-400">{onlineDevices.length} online</span></p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-indigo-400 text-xs font-bold group-hover:gap-3 transition-all">
                Bulk Assign, Live Preview & more <ArrowRight size={14} />
              </div>
            </button>

            {/* Device table lives in the Devices tab */}
            {false && <SectionCard
              title="Screen Nodes"
              sub={`${filteredDevices.length}${groupFilter !== 'all' ? ` in "${groupFilter}"` : `/${devices.length}`} devices`}
              action={
                <button onClick={() => setView('devices')} className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-all" style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc' }}>
                  <Monitor size={12} /> Open Devices Tab
                </button>
              }
            >
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.03)' }}>
                      {bulkMode && <th className={thCls} style={{ width: 40 }}><input type="checkbox" className="accent-indigo-500" checked={bulkSelected.size === filteredDevices.length && filteredDevices.length > 0} onChange={e => setBulkSelected(e.target.checked ? new Set(filteredDevices.map(d => d.deviceId)) : new Set())} /></th>}
                      <th className={thCls}>Device</th>
                      <th className={thCls}>Content</th>
                      <th className={thCls}>Cloud Account</th>
                      <th className={thCls}>Status</th>
                      <th className={`${thCls} text-center`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataLoading ? [1, 2, 3].map(i => <SkeletonRow key={i} />) : filteredDevices.length ? filteredDevices.map(device => {
                      const isOnline = onlineDevices.includes(device.deviceId)
                      const { color, initials } = deviceAvatar(device.deviceName, device.deviceId)
                      return (
                        <tr key={device.deviceId} className={`transition-all group relative ${isOnline ? 'neon-row-green' : 'neon-row'} ${bulkMode && bulkSelected.has(device.deviceId) ? 'ring-1 ring-indigo-500/40' : ''}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          {bulkMode && <td className={tdCls} style={{ width: 40 }}><input type="checkbox" className="accent-indigo-500 cursor-pointer" checked={bulkSelected.has(device.deviceId)} onChange={() => toggleBulkDevice(device.deviceId)} /></td>}
                          <td className={`${tdCls} relative`}>
                            <div className={`absolute left-0 top-3 bottom-3 w-0.5 rounded-r-full transition-all ${isOnline ? 'bg-emerald-400' : 'bg-rose-500/50'}`} style={isOnline ? { boxShadow: '0 0 8px rgba(16,185,129,0.8)' } : {}} />
                            <div className="flex items-center gap-3 pl-2">
                              <div className={`w-10 h-10 ${color} rounded-2xl flex items-center justify-center shrink-0 shadow-lg`} style={{ boxShadow: `0 4px 16px rgba(0,0,0,0.4)` }}>
                                <span className="text-white font-black text-xs">{initials}</span>
                              </div>
                              <div>
                                <p className="font-bold text-slate-100 text-sm group-hover:text-white transition-colors">{device.deviceName || 'Unnamed'}</p>
                                <button
                                  onClick={() => copyDeviceId(device.deviceId)}
                                  title="Click to copy"
                                  className={`text-[10px] font-mono truncate max-w-[160px] text-left transition-colors ${copiedDeviceId === device.deviceId ? 'text-emerald-400 font-bold' : 'text-slate-700 hover:text-indigo-400'}`}
                                >
                                  {copiedDeviceId === device.deviceId ? '✓ Copied!' : device.deviceId}
                                </button>
                                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                  <p className="text-[11px] text-slate-600 font-mono">{device.ipAddress || '0.0.0.0'}</p>
                                  <input
                                    list={`zones-${device.deviceId}`}
                                    defaultValue={device.group || ''}
                                    placeholder="+ zone"
                                    className="text-[10px] font-bold text-violet-300 rounded-full px-2 py-0.5 outline-none cursor-pointer w-20"
                                    style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.25)' }}
                                    onBlur={e => {
                                      const val = e.target.value.trim()
                                      if (val !== (device.group || '')) handleSetDeviceGroup(device.deviceId, val)
                                    }}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter') { handleSetDeviceGroup(device.deviceId, e.target.value.trim()); e.target.blur() }
                                      if (e.key === 'Escape') { e.target.value = device.group || ''; e.target.blur() }
                                    }}
                                  />
                                  <datalist id={`zones-${device.deviceId}`}>
                                    <option value="" />
                                    {allGroups.map(g => <option key={g} value={g} />)}
                                  </datalist>
                                  {device.offlineMode && (
                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(6,182,212,0.12)', color: '#22d3ee', border: '1px solid rgba(6,182,212,0.25)' }}>📴 OFFLINE</span>
                                  )}
                                  {device.offlineMode && device.lastSyncAt && (
                                    <span className="text-[9px] text-slate-600" title={new Date(device.lastSyncAt).toLocaleString()}>
                                      {(() => { const m = Math.floor((Date.now() - new Date(device.lastSyncAt)) / 60000); return `synced ${m < 2 ? 'just now' : m < 60 ? `${m}m ago` : `${Math.floor(m/60)}h ago`}` })()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className={tdCls}>
                            <button
                              onClick={() => openDeviceContentModal(device)}
                              className="flex items-center gap-2 px-3 py-2 rounded-xl border transition-all text-left group w-full max-w-[200px]"
                              style={
                                device.assignedMedia?.length > 0
                                  ? { background: 'rgba(109,40,217,0.2)', borderColor: 'rgba(139,92,246,0.35)', color: '#c4b5fd' }
                                  : device.assignedPlaylist?._id
                                  ? { background: 'rgba(5,150,105,0.15)', borderColor: 'rgba(52,211,153,0.3)', color: '#6ee7b7' }
                                  : { background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)', color: '#475569' }
                              }
                            >
                              <Film size={13} className="shrink-0" />
                              <div className="min-w-0">
                                {device.assignedMedia?.length > 0 ? (
                                  <>
                                    <p className="text-[11px] font-black truncate">{device.assignedMedia.length} item{device.assignedMedia.length !== 1 ? 's' : ''}</p>
                                    <p className="text-[10px] opacity-70 truncate">{device.assignedMedia.some(it => it.schedule?.enabled) ? '⏰ Scheduled' : 'Always on'}</p>
                                  </>
                                ) : device.assignedPlaylist?.name ? (
                                  <>
                                    <p className="text-[11px] font-black truncate">{device.assignedPlaylist.name}</p>
                                    <p className="text-[10px] opacity-60">Legacy playlist</p>
                                  </>
                                ) : (
                                  <p className="text-[11px] font-semibold">No content — click to add</p>
                                )}
                              </div>
                            </button>
                          </td>
                          <td className={tdCls}>
                            <select className="glass-select text-xs font-semibold py-2 px-3 rounded-lg outline-none cursor-pointer w-full max-w-[160px]" value={device.cloudinaryAccount || ''} onChange={e => handleSetDeviceCloudinaryAccount(device.deviceId, e.target.value)}>
                              <option value="">Default</option>
                              {cloudinaryAccounts.map(a => <option key={a._id} value={a._id}>{a.name}{a.isDefault ? ' ★' : ''}</option>)}
                            </select>
                          </td>
                          <td className={tdCls}>
                            {isOnline ? (
                              <div>
                                <Badge color="green"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />ONLINE</Badge>
                                <p className="text-[11px] text-slate-500 mt-1.5 font-mono">{device.playCount || 0} plays</p>
                              </div>
                            ) : (
                              <div>
                                <Badge color="red">OFFLINE</Badge>
                                <p className="text-[11px] font-bold mt-1.5" style={{ color: '#f87171' }}>{offlineDuration(device.lastSeen)}</p>
                              </div>
                            )}
                            <div className="mt-2">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wide">Cache</span>
                                <div className="flex items-center gap-1.5">
                                  {device.syncStatus?.total > 0 && (
                                    <span className={`text-[10px] font-bold ${device.syncStatus.downloaded === device.syncStatus.total ? 'text-emerald-400' : 'text-amber-400'}`}>
                                      {device.syncStatus.downloaded}/{device.syncStatus.total}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => handleForceSync(device.deviceId)}
                                    disabled={forceSyncing.has(device.deviceId) || (!device.assignedPlaylist?._id && !device.assignedMedia?.length)}
                                    title={(device.assignedPlaylist?._id || device.assignedMedia?.length) ? 'Force re-download all files' : 'No content assigned'}
                                    className="p-0.5 rounded text-slate-600 hover:text-indigo-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                                  >
                                    <RefreshCcw size={11} className={forceSyncing.has(device.deviceId) ? 'animate-spin' : ''} />
                                  </button>
                                </div>
                              </div>
                              {device.syncStatus?.total > 0 && (
                                <div className="h-1 rounded-full w-20 overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                                  <div
                                    className={`h-full rounded-full transition-all duration-500 ${device.syncStatus.downloaded === device.syncStatus.total ? 'bg-emerald-400' : 'bg-amber-400'}`}
                                    style={{ width: `${Math.round((device.syncStatus.downloaded / device.syncStatus.total) * 100)}%` }}
                                  />
                                </div>
                              )}
                            </div>
                          </td>
                          <td className={`${tdCls} text-center`}>
                            <div className="flex items-center justify-center gap-1.5">
                              <button onClick={() => setPreviewDevice(device)} title="Live Preview" className="p-2 rounded-lg text-slate-500 hover:text-emerald-300 transition-all" style={{ background: 'rgba(255,255,255,0.05)' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(16,185,129,0.15)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}><PlayCircle size={14} /></button>
                              <button onClick={() => openDeviceContentModal(device)} title="Edit Content" className="p-2 rounded-lg text-slate-500 hover:text-violet-300 transition-all" style={{ background: 'rgba(255,255,255,0.05)' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(139,92,246,0.2)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}><Eye size={14} /></button>
                              <button onClick={() => copyDeviceId(device.deviceId)} title="Copy ID" className={`p-2 rounded-lg transition-all ${copiedDeviceId === device.deviceId ? 'text-emerald-400' : 'text-slate-500 hover:text-blue-300'}`} style={{ background: copiedDeviceId === device.deviceId ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.05)' }} onMouseEnter={e=>{ if(copiedDeviceId!==device.deviceId) e.currentTarget.style.background='rgba(59,130,246,0.15)' }} onMouseLeave={e=>{ if(copiedDeviceId!==device.deviceId) e.currentTarget.style.background='rgba(255,255,255,0.05)' }}><Monitor size={14} /></button>
                              <button onClick={() => issuePlayerJwt(device.deviceId)} title="Issue JWT" className="p-2 rounded-lg text-slate-500 hover:text-amber-300 transition-all" style={{ background: 'rgba(255,255,255,0.05)' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(245,158,11,0.15)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}><KeyRound size={14} /></button>
                              <button onClick={() => handleForceLogin(device.deviceId, device.deviceName)} disabled={forceLoggingIn.has(device.deviceId)} title="Force Login — remotely log in this device" className="p-2 rounded-lg text-slate-500 hover:text-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all" style={{ background: 'rgba(255,255,255,0.05)' }} onMouseEnter={e=>{ if(!forceLoggingIn.has(device.deviceId)) e.currentTarget.style.background='rgba(6,182,212,0.15)' }} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}>{forceLoggingIn.has(device.deviceId) ? <RefreshCcw size={14} className="animate-spin"/> : <LogIn size={14} />}</button>
                              <button onClick={() => handleToggleOfflineMode(device.deviceId)} disabled={offlineToggling.has(device.deviceId)} title={device.offlineMode ? 'Offline mode ON — click to disable' : 'Enable offline mode for this device'} className={`p-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all ${device.offlineMode ? 'text-cyan-400' : 'text-slate-500 hover:text-cyan-400'}`} style={{ background: device.offlineMode ? 'rgba(6,182,212,0.15)' : 'rgba(255,255,255,0.05)', border: device.offlineMode ? '1px solid rgba(6,182,212,0.3)' : '1px solid transparent' }} onMouseEnter={e=>{ if(!offlineToggling.has(device.deviceId) && !device.offlineMode) e.currentTarget.style.background='rgba(6,182,212,0.1)' }} onMouseLeave={e=>{ if(!device.offlineMode) e.currentTarget.style.background='rgba(255,255,255,0.05)' }}>{offlineToggling.has(device.deviceId) ? <RefreshCcw size={14} className="animate-spin"/> : device.offlineMode ? <WifiOff size={14} /> : <Wifi size={14} />}</button>
                              <button onClick={() => handleDeleteDevice(device.deviceId)} title="Delete" className="p-2 rounded-lg text-slate-500 hover:text-rose-300 transition-all" style={{ background: 'rgba(255,255,255,0.05)' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(244,63,94,0.15)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}><Trash2 size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      )
                    }) : (
                      <tr><td colSpan={5} className="px-6 py-16 text-center">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(255,255,255,0.05)' }}><Monitor className="text-slate-600" size={32} /></div>
                        <p className="text-slate-500 font-bold">No devices{groupFilter !== 'all' ? ` in "${groupFilter}"` : ''}</p>
                        <p className="text-slate-700 text-xs mt-1">Click <strong className="text-slate-500">Add Device</strong> to register a screen</p>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>}

            {/* Activity Log Timeline */}
            <SectionCard
              title="Activity Log"
              sub="Recent admin actions"
              action={
                <button onClick={fetchActivityLog} title="Refresh" className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 transition-all" style={{ background: 'rgba(255,255,255,0.06)' }}><RefreshCcw size={12} /></button>
              }
            >
              {activityLog.length > 0 ? (
                <div className="p-4 space-y-0.5 max-h-72 overflow-y-auto">
                  {activityLog.map((log, i) => {
                    const iconMap = { playlist: Film, device: Monitor, media: Images, broadcast: Megaphone, system: Sparkles }
                    const colorMap = {
                      playlist: { bg: 'rgba(124,58,237,0.15)', icon: '#a78bfa' },
                      device:   { bg: 'rgba(37,99,235,0.15)',  icon: '#60a5fa' },
                      media:    { bg: 'rgba(217,119,6,0.15)',  icon: '#fbbf24' },
                      broadcast:{ bg: 'rgba(220,38,38,0.15)',  icon: '#f87171' },
                      system:   { bg: 'rgba(2,132,199,0.15)',  icon: '#38bdf8' },
                    }
                    const LogIcon = iconMap[log.entityType] || Sparkles
                    const col = colorMap[log.entityType] || colorMap.system
                    const timeAgo = (date) => { const d = (Date.now() - new Date(date)) / 1000; if (d < 60) return 'just now'; if (d < 3600) return `${Math.floor(d/60)}m ago`; if (d < 86400) return `${Math.floor(d/3600)}h ago`; return `${Math.floor(d/86400)}d ago` }
                    return (
                      <div key={log._id || i} className="flex items-start gap-3 py-2.5 px-2 rounded-xl transition-colors group" onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.04)'} onMouseLeave={e=>e.currentTarget.style.background=''}>
                        <div className="relative shrink-0">
                          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: col.bg }}>
                            <LogIcon size={13} style={{ color: col.icon }} />
                          </div>
                          {i < activityLog.length - 1 && <div className="absolute left-4 top-8 w-px h-4" style={{ background: 'rgba(255,255,255,0.06)' }} />}
                        </div>
                        <div className="flex-1 min-w-0 pt-0.5">
                          <p className="text-xs font-bold text-slate-300 leading-snug">{log.description}</p>
                          {log.entityName && <p className="text-[10px] text-slate-600 font-mono mt-0.5 truncate">{log.entityName}</p>}
                        </div>
                        <span className="text-[10px] text-slate-700 font-mono shrink-0 pt-0.5 group-hover:text-slate-500 transition-colors">{timeAgo(log.createdAt)}</span>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <Sparkles size={22} className="text-slate-600" />
                  </div>
                  <p className="text-slate-500 font-bold text-sm">No activity yet</p>
                  <p className="text-slate-700 text-xs">Actions like assigning devices and broadcasts will appear here</p>
                </div>
              )}
            </SectionCard>

            {/* Storage strip */}
            {storageStats && (
              <div className="relative overflow-hidden rounded-3xl p-6 flex flex-wrap items-center gap-6" style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-10" style={{ background: 'radial-gradient(circle,#7c3aed,transparent 70%)' }} />
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border border-white/10" style={{ background: 'rgba(255,255,255,0.07)' }}>
                  <HardDrive size={20} className="text-slate-300" />
                </div>
                <div className="flex-1 min-w-0 flex flex-wrap gap-8">
                  <div>
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Total Storage</p>
                    <p className="text-3xl font-black text-white leading-none">{storageStats.totalMB}<span className="text-slate-500 text-sm font-medium ml-1">MB</span></p>
                    <p className="text-xs text-slate-600 mt-1">{storageStats.totalAssets} files</p>
                  </div>
                  {storageStats.orphanedAssets > 0 && (
                    <div>
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Wasted</p>
                      <p className="text-3xl font-black text-amber-400 leading-none">{storageStats.orphanedMB}<span className="text-slate-500 text-sm font-medium ml-1">MB</span></p>
                      <p className="text-xs text-slate-600 mt-1">{storageStats.orphanedAssets} orphaned files</p>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={fetchStorageStats} className="text-xs font-bold text-slate-500 hover:text-white px-4 py-2.5 rounded-xl border border-white/10 hover:bg-white/10 transition-all">Refresh</button>
                  <button onClick={handleCleanup} disabled={cleanupLoading || storageStats.orphanedAssets === 0} className="text-white text-xs font-bold px-5 py-2.5 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed" style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)', boxShadow: '0 4px 14px rgba(239,68,68,0.3)' }}>
                    {cleanupLoading ? 'Cleaning…' : `Clean ${storageStats.orphanedAssets} Files`}
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      }

      // ── Upload ─────────────────────────────────────────────────────────────
      case 'upload':
        return (
          <div className="max-w-2xl space-y-5" style={{ animation: 'fadeInUp 0.35s ease-out' }}>
            <div className="rounded-3xl overflow-hidden" style={{ background: 'rgba(13,20,45,0.75)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 25px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)' }}>
              <div className="px-8 py-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <h3 className="font-black text-white text-lg">Upload to Media Library</h3>
                <p className="text-slate-500 text-sm mt-0.5">Files are saved to Cloudinary and appear instantly in your Media Library</p>
              </div>
              <div className="p-8 space-y-5">
                <input ref={uploadInputRef} type="file" multiple accept="video/mp4,.mp4,.m4v,image/jpeg,image/png,image/gif,image/webp,image/avif,image/heic,.jpg,.jpeg,.png,.gif,.webp" className="hidden" onChange={handlePickUploadFiles} />
                <label
                  onClick={() => uploadInputRef.current?.click()}
                  className="flex flex-col items-center justify-center rounded-2xl p-12 cursor-pointer transition-all"
                  style={{ border: '2px dashed rgba(99,102,241,0.3)', background: 'rgba(99,102,241,0.04)' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(99,102,241,0.6)'; e.currentTarget.style.background='rgba(99,102,241,0.08)' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(99,102,241,0.3)'; e.currentTarget.style.background='rgba(99,102,241,0.04)' }}
                >
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 shadow-xl" style={{ background: 'linear-gradient(135deg,#3730a3,#4f46e5)', boxShadow: '0 8px 32px rgba(99,102,241,0.4)' }}>
                    <Upload className="text-white" size={28} />
                  </div>
                  <p className="text-sm font-black text-white">Click to select files</p>
                  <p className="text-xs text-slate-500 mt-1.5">MP4 · M4V · JPEG · PNG · GIF · WebP · AVIF · HEIC</p>
                </label>

                {uploadFiles.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black text-slate-500 uppercase tracking-wider">{uploadFiles.length} file{uploadFiles.length !== 1 ? 's' : ''} selected</span>
                      <button onClick={() => setUploadFiles([])} className="text-xs font-bold text-slate-600 hover:text-rose-400 transition-colors">Clear all</button>
                    </div>
                    {uploadFiles.map((f, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: f.type?.startsWith('video') ? 'rgba(37,99,235,0.2)' : 'rgba(124,58,237,0.2)' }}>
                          <Film size={14} className={f.type?.startsWith('video') ? 'text-blue-400' : 'text-purple-400'} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-slate-200 truncate">{f.name}</p>
                          <p className="text-[11px] text-slate-600">{(f.size / 1024 / 1024).toFixed(1)} MB</p>
                        </div>
                        <button onClick={() => setUploadFiles(prev => prev.filter((_, j) => j !== i))} className="p-1 rounded-lg text-slate-600 hover:text-rose-400 transition-all"><X size={13} /></button>
                      </div>
                    ))}
                  </div>
                )}

                {uploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-400">Uploading to Cloudinary…</span>
                      <span className="text-xs font-black text-indigo-400">{uploadProgress}%</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      <div className="h-full rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%`, background: 'linear-gradient(90deg,#4f46e5,#7c3aed)', boxShadow: '0 0 12px rgba(99,102,241,0.6)' }} />
                    </div>
                  </div>
                )}

                <button
                  onClick={handleUploadDirect}
                  disabled={uploading || !uploadFiles.length}
                  className="w-full flex items-center justify-center gap-2 text-white font-black py-4 rounded-2xl transition-all disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                  style={{ background: 'linear-gradient(135deg,#3730a3,#4f46e5,#6366f1)', boxShadow: '0 8px 32px rgba(99,102,241,0.5)' }}
                >
                  <Upload size={16} />
                  {uploading ? `Uploading… ${uploadProgress}%` : `Upload ${uploadFiles.length || ''} File${uploadFiles.length !== 1 ? 's' : ''} to Library`}
                </button>
              </div>
            </div>

            {/* Quick tip */}
            <div className="rounded-2xl p-5 flex items-start gap-4" style={{ background: 'rgba(109,40,217,0.12)', border: '1px solid rgba(139,92,246,0.2)' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(139,92,246,0.2)' }}>
                <Sparkles size={16} className="text-violet-400" />
              </div>
              <div>
                <p className="font-black text-violet-300 text-sm">What's next?</p>
                <p className="text-violet-400/70 text-xs mt-1 leading-relaxed">After uploading, go to <strong className="text-violet-300">Dashboard → device → Edit Content</strong> to assign videos to a screen and set schedules per video.</p>
              </div>
            </div>
          </div>
        )

      // ── Gallery / Media Library ────────────────────────────────────────────
      case 'gallery': {
        const videos = allMediaAssets.filter(a => a.fileType === 'video')
        const images = allMediaAssets.filter(a => a.fileType === 'image')

        const MediaCard = ({ asset }) => {
          const previewUrl = asset.cloudinaryUrl
          const name = asset.originalFilename || previewUrl.split('/').pop()
          return (
            <div className="aspect-video bg-slate-900 rounded-xl overflow-hidden relative group border-2 border-transparent hover:border-blue-300 hover:shadow-md transition-all shadow-sm">
              {asset.fileType === 'video'
                ? <video src={previewUrl} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" muted loop playsInline />
                : <img src={previewUrl} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" alt="media" />
              }
              {/* Preview button */}
              <button
                onClick={() => setSelectedVideo({ fileUrl: previewUrl, fileType: asset.fileType })}
                className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center"><PlayCircle size={22} className="text-white" /></div>
              </button>
              {/* Delete button */}
              <button
                onClick={() => handleDeleteMediaAsset(asset)}
                className="absolute top-2 right-2 w-7 h-7 bg-black/50 hover:bg-red-600 text-white rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all backdrop-blur-sm"
                title="Delete"
              >
                <Trash2 size={12} />
              </button>
              {/* Bottom bar */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 pt-4 pb-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-[10px] font-bold text-white/80 truncate">{name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full ${asset.fileType === 'video' ? 'bg-blue-500/80' : 'bg-purple-500/80'} text-white`}>{asset.fileType}</span>
                </div>
              </div>
            </div>
          )
        }

        return (
          <div className="space-y-5" style={{ animation: 'fadeInUp 0.35s ease-out' }}>

            {/* Header bar */}
            <div className="rounded-2xl p-3.5 flex items-center gap-3 flex-wrap" style={{ background: 'rgba(13,20,45,0.75)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
              <div className="flex items-center gap-2.5 flex-1 min-w-0">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg,#2563eb,#4f46e5)' }}>
                  <Images size={16} className="text-white" />
                </div>
                <div>
                  <p className="font-black text-white text-sm">All Media Files</p>
                  <p className="text-xs text-slate-500">{allMediaAssets.length} file{allMediaAssets.length !== 1 ? 's' : ''} · {videos.length} videos · {images.length} images</p>
                </div>
              </div>
              <button
                onClick={() => setView('upload')}
                className="flex items-center gap-2 text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-all shrink-0"
                style={{ background: 'linear-gradient(135deg,#3730a3,#4f46e5)', boxShadow: '0 4px 16px rgba(99,102,241,0.4)' }}
              >
                <Upload size={13} /> Upload More
              </button>
            </div>

            {allMediaAssets.length === 0 ? (
              <div className="rounded-2xl p-16 text-center" style={{ background: 'rgba(13,20,45,0.75)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(99,102,241,0.2)' }}><Images className="text-indigo-400" size={36} /></div>
                <p className="text-slate-400 font-bold text-lg">No media yet</p>
                <p className="text-slate-600 text-sm mt-2">Upload videos and images from the Upload page</p>
                <button onClick={() => setView('upload')} className="mt-5 text-white text-xs font-bold px-5 py-2.5 rounded-xl transition-all" style={{ background: 'linear-gradient(135deg,#3730a3,#4f46e5)' }}>Go to Upload</button>
              </div>
            ) : (
              <>
                {/* Videos section */}
                {videos.length > 0 && (
                  <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(13,20,45,0.75)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="px-6 py-4 flex items-center gap-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#2563eb,#4f46e5)' }}><Film size={15} className="text-white" /></div>
                      <div>
                        <h3 className="font-black text-white">Videos</h3>
                        <p className="text-xs text-slate-500">{videos.length} file{videos.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                      {videos.map((asset) => <MediaCard key={asset._id} asset={asset} />)}
                    </div>
                  </div>
                )}

                {/* Images section */}
                {images.length > 0 && (
                  <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(13,20,45,0.75)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="px-6 py-4 flex items-center gap-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#7c3aed,#9333ea)' }}><Images size={15} className="text-white" /></div>
                      <div>
                        <h3 className="font-black text-white">Images</h3>
                        <p className="text-xs text-slate-500">{images.length} file{images.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                      {images.map((asset) => <MediaCard key={asset._id} asset={asset} />)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )
      }

      // ── Schedule Calendar ──────────────────────────────────────────────────
      case 'calendar': {
        const nowDay = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date().getDay()]

        // For each device, compute per-day active video count (scheduled + always-on)
        const deviceRows = devices.map(d => {
          const media = d.assignedMedia || []
          const perDay = {}
          DAYS.forEach(day => {
            const count = media.filter(it => {
              if (!it.schedule?.enabled) return true          // always-on
              const days = it.schedule.days?.length ? it.schedule.days : DAYS
              return days.includes(day)
            }).length
            perDay[day] = count
          })
          const alwaysOn = media.filter(it => !it.schedule?.enabled).length
          const scheduled = media.filter(it => it.schedule?.enabled).length
          return { device: d, perDay, alwaysOn, scheduled, total: media.length }
        }).filter(r => r.total > 0 || true) // show all devices

        const totalScheduled = devices.reduce((s, d) => s + (d.assignedMedia || []).filter(it => it.schedule?.enabled).length, 0)
        const totalAlwaysOn  = devices.reduce((s, d) => s + (d.assignedMedia || []).filter(it => !it.schedule?.enabled).length, 0)
        const devicesWithContent = devices.filter(d => d.assignedMedia?.length > 0).length

        return (
          <div className="space-y-6" style={{ animation: 'fadeInUp 0.35s ease-out' }}>

            {/* Hero */}
            <div className="relative overflow-hidden rounded-3xl p-7" style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e1b4b 60%,#0a1628 100%)' }}>
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-20" style={{ background: 'radial-gradient(circle,#818cf8,transparent 70%)' }} />
              <div className="absolute -bottom-12 -left-12 w-44 h-44 rounded-full opacity-15" style={{ background: 'radial-gradient(circle,#34d399,transparent 70%)' }} />
              <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-2xl" style={{ background: 'linear-gradient(135deg,#6366f1,#4f46e5)' }}>
                    <CalendarDays size={24} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-white">Content Schedule</h2>
                    <p className="text-indigo-300/70 text-sm mt-1">Video-wise schedule per device · Today is <span className="text-indigo-300 font-bold">{nowDay}</span></p>
                  </div>
                </div>
                <div className="flex gap-3 flex-wrap">
                  {[
                    { label: 'With Content', value: devicesWithContent, c: '#10b981', bg: 'rgba(16,185,129,0.15)' },
                    { label: 'Always On', value: totalAlwaysOn, c: '#60a5fa', bg: 'rgba(96,165,250,0.15)' },
                    { label: 'Scheduled', value: totalScheduled, c: '#a78bfa', bg: 'rgba(167,139,250,0.15)' },
                  ].map(({ label, value, c, bg }) => (
                    <div key={label} className="flex flex-col items-center justify-center rounded-2xl px-5 py-3 min-w-[80px] border" style={{ background: bg, borderColor: `${c}30` }}>
                      <p className="text-white font-black text-2xl leading-none">{value}</p>
                      <p className="text-[10px] font-bold uppercase tracking-wider mt-1" style={{ color: `${c}cc` }}>{label}</p>
                    </div>
                  ))}
                  <button
                    onClick={() => setView('devices')}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold text-white/70 hover:text-white border border-white/10 hover:bg-white/10 transition-all self-center"
                  >
                    <Monitor size={13} /> Manage Devices
                  </button>
                </div>
              </div>
            </div>

            {/* Weekly Grid */}
            <div className="rounded-3xl overflow-hidden" style={{ background: 'rgba(13,20,45,0.75)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 25px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)' }}>
              <div className="px-6 py-4 flex items-center gap-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#6366f1,#4f46e5)' }}>
                  <CalendarDays size={14} className="text-white" />
                </div>
                <div>
                  <h3 className="font-black text-white">Weekly Broadcast Grid</h3>
                  <p className="text-xs text-slate-400">Active video count per device per day — click a device to edit its content</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <th className="px-5 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider text-left w-52">Device</th>
                      {DAYS.map(d => (
                        <th key={d} className={`px-2 py-4 text-[11px] font-black uppercase tracking-wider text-center ${d === nowDay ? 'text-indigo-400' : 'text-slate-500'}`}>
                          <div className="inline-flex flex-col items-center gap-1">
                            <span className={`w-8 h-8 rounded-xl flex items-center justify-center text-[11px] font-black ${d === nowDay ? 'text-white shadow-lg' : 'text-slate-500'}`}
                              style={d === nowDay ? { background: 'linear-gradient(135deg,#6366f1,#4f46e5)' } : {}}>
                              {d}
                            </span>
                            {d === nowDay && <span className="text-[8px] text-indigo-400 font-black uppercase tracking-widest">today</span>}
                          </div>
                        </th>
                      ))}
                      <th className="px-4 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider text-center">Always On</th>
                      <th className="px-4 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider text-center">Scheduled</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {devices.length === 0 && (
                      <tr><td colSpan={11} className="px-6 py-16 text-center">
                        <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.2)' }}>
                          <CalendarDays size={28} className="text-indigo-400" />
                        </div>
                        <p className="text-slate-400 font-bold">No devices registered yet</p>
                      </td></tr>
                    )}
                    {deviceRows.map(({ device: d, perDay, alwaysOn, scheduled, total }) => {
                      const { color: avatarColor, initials } = deviceAvatar(d.deviceName, d.deviceId)
                      const isOnline = onlineDevices.includes(d.deviceId)
                      const hasContent = total > 0
                      return (
                        <tr key={d.deviceId} className="neon-row transition-colors group">
                          {/* Device name */}
                          <td className="px-5 py-4">
                            <button
                              onClick={() => openDeviceContentModal(d)}
                              className="flex items-center gap-3 text-left group/btn w-full"
                            >
                              <div className={`w-9 h-9 ${avatarColor} rounded-xl flex items-center justify-center shrink-0 shadow-sm`}>
                                <span className="text-white font-black text-[10px]">{initials}</span>
                              </div>
                              <div className="min-w-0">
                                <p className="font-bold text-slate-100 text-sm truncate group-hover/btn:text-indigo-400 transition-colors">{d.deviceName || 'Unnamed'}</p>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  {isOnline
                                    ? <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                                    : <span className="w-1.5 h-1.5 rounded-full bg-slate-600 shrink-0" />
                                  }
                                  <span className="text-[10px] text-slate-500 font-medium">{isOnline ? 'Online' : 'Offline'}</span>
                                  {d.group && <span className="text-[10px] font-bold text-violet-300 px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(139,92,246,0.15)' }}>{d.group}</span>}
                                </div>
                              </div>
                            </button>
                          </td>

                          {/* Per-day cells */}
                          {DAYS.map(day => {
                            const count = perDay[day] || 0
                            const isToday = day === nowDay
                            return (
                              <td key={day} className="px-2 py-4 text-center">
                                {!hasContent ? (
                                  <div className="w-9 h-9 rounded-xl mx-auto flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                                    <span className="text-slate-600 text-[10px] font-black">—</span>
                                  </div>
                                ) : count === 0 ? (
                                  <div className="w-9 h-9 rounded-xl mx-auto flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                                    <span className="text-slate-600 text-[10px] font-black">0</span>
                                  </div>
                                ) : (
                                  <div
                                    className={`w-9 h-9 rounded-xl mx-auto flex items-center justify-center shadow-sm font-black text-sm ${isToday ? 'text-white' : 'text-indigo-300'}`}
                                    style={isToday
                                      ? { background: 'linear-gradient(135deg,#6366f1,#4f46e5)', boxShadow: '0 4px 12px rgba(99,102,241,0.4)' }
                                      : { background: `rgba(99,102,241,0.14)`, border: '1px solid rgba(99,102,241,0.25)' }
                                    }
                                  >
                                    {count}
                                  </div>
                                )}
                              </td>
                            )
                          })}

                          {/* Always On */}
                          <td className="px-4 py-4 text-center">
                            {alwaysOn > 0
                              ? <span className="inline-flex items-center gap-1 text-[11px] font-black text-emerald-300 px-2.5 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.2)' }}>{alwaysOn} video{alwaysOn !== 1 ? 's' : ''}</span>
                              : <span className="text-slate-600 text-[11px] font-bold">—</span>
                            }
                          </td>

                          {/* Scheduled */}
                          <td className="px-4 py-4 text-center">
                            {scheduled > 0
                              ? <span className="inline-flex items-center gap-1 text-[11px] font-black text-violet-300 px-2.5 py-1 rounded-full" style={{ background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.2)' }}>{scheduled} video{scheduled !== 1 ? 's' : ''}</span>
                              : <span className="text-slate-600 text-[11px] font-bold">—</span>
                            }
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Day-by-day breakdown — what's playing today */}
            <div className="rounded-3xl overflow-hidden" style={{ background: 'rgba(13,20,45,0.75)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 25px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)' }}>
              <div className="px-6 py-4 flex items-center gap-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}>
                  <Zap size={14} className="text-white" />
                </div>
                <div>
                  <h3 className="font-black text-white">Today — {nowDay}</h3>
                  <p className="text-xs text-slate-400">All videos scheduled for today across every device</p>
                </div>
              </div>
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {devices.filter(d => (d.assignedMedia || []).length > 0).map(d => {
                  const todayMedia = (d.assignedMedia || []).filter(it => {
                    if (!it.schedule?.enabled) return true
                    const days = it.schedule.days?.length ? it.schedule.days : DAYS
                    return days.includes(nowDay)
                  })
                  if (!todayMedia.length) return null
                  const { color: avatarColor, initials } = deviceAvatar(d.deviceName, d.deviceId)
                  return (
                    <div key={d.deviceId} className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      {/* Device header */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-8 h-8 ${avatarColor} rounded-xl flex items-center justify-center shrink-0 shadow-sm`}>
                          <span className="text-white font-black text-[10px]">{initials}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-black text-white text-sm truncate">{d.deviceName || 'Unnamed'}</p>
                          <p className="text-[10px] text-slate-400">{todayMedia.length} video{todayMedia.length !== 1 ? 's' : ''} active today</p>
                        </div>
                        <button
                          onClick={() => openDeviceContentModal(d)}
                          className="text-[10px] font-bold text-indigo-300 px-2 py-1 rounded-lg transition-all shrink-0"
                          style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.25)' }}
                          onMouseEnter={e => e.currentTarget.style.background='rgba(99,102,241,0.25)'}
                          onMouseLeave={e => e.currentTarget.style.background='rgba(99,102,241,0.15)'}
                        >Edit</button>
                      </div>
                      {/* Video list */}
                      <div className="space-y-1.5">
                        {todayMedia.map((it, i) => {
                          const name = it.originalFilename || it.fileUrl.split('/').pop()
                          const isScheduled = it.schedule?.enabled
                          return (
                            <div key={i} className="flex items-center gap-2 rounded-xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)' }}>
                              <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded-full shrink-0 ${it.fileType === 'video' ? 'text-blue-300' : 'text-purple-300'}`} style={it.fileType === 'video' ? { background: 'rgba(59,130,246,0.2)' } : { background: 'rgba(139,92,246,0.2)' }}>{it.fileType}</span>
                              <p className="text-[11px] font-semibold text-slate-300 truncate flex-1">{name}</p>
                              {isScheduled
                                ? <span className="text-[9px] font-black text-violet-300 px-1.5 py-0.5 rounded-full shrink-0" style={{ background: 'rgba(139,92,246,0.15)' }}>{it.schedule.startTime}–{it.schedule.endTime}</span>
                                : <span className="text-[9px] font-bold text-emerald-400 shrink-0">Always</span>
                              }
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                }).filter(Boolean)}
                {devices.filter(d => {
                  const m = d.assignedMedia || []
                  return m.some(it => {
                    if (!it.schedule?.enabled) return true
                    return (it.schedule.days?.length ? it.schedule.days : DAYS).includes(nowDay)
                  })
                }).length === 0 && (
                  <div className="col-span-full text-center py-12">
                    <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.15)' }}>
                      <CalendarDays size={28} className="text-emerald-400" />
                    </div>
                    <p className="text-slate-400 font-bold">No content scheduled for today</p>
                    <p className="text-slate-500 text-xs mt-1">Go to Dashboard → device → Edit Content to assign videos</p>
                  </div>
                )}
              </div>
            </div>

          </div>
        )
      }

      // ── Analytics ──────────────────────────────────────────────────────────
      case 'reports': {
        const online = onlineDevices.length
        const uptime = devices.length > 0 ? ((online / devices.length) * 100).toFixed(0) : 0
        const allPlays = devices.reduce((s, d) => s + (Number(d.playCount) || 0), 0)
        const unassigned = devices.filter(d => !d.assignedMedia?.length && !d.assignedPlaylist?._id).length
        const getFileName = (r) => { if (r?.originalFilename) return r.originalFilename; try { return decodeURIComponent(new URL(r?.fileUrl).pathname.split('/').filter(Boolean).pop() || '—') } catch { return '—' } }
        const fmtTime = (secs) => { const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60; return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s` }
        const REPORT_TYPES = [
          { key: 'summarized', label: 'Summarized', icon: BarChart2, desc: 'Total plays per device', color: 'from-blue-500 to-indigo-600', grad: 'linear-gradient(135deg,#3b82f6,#4f46e5)', accentBg: 'bg-blue-50', accentText: 'text-blue-600', barGrad: 'from-blue-500 to-indigo-500', glow: '#3b82f660' },
          { key: 'playerwise', label: 'Playerwise', icon: Monitor, desc: 'One device in detail', color: 'from-violet-500 to-purple-600', grad: 'linear-gradient(135deg,#8b5cf6,#9333ea)', accentBg: 'bg-violet-50', accentText: 'text-violet-600', barGrad: 'from-violet-500 to-purple-500', glow: '#8b5cf660' },
          { key: 'groupwise', label: 'Groupwise', icon: Activity, desc: 'By zone / group', color: 'from-emerald-500 to-teal-600', grad: 'linear-gradient(135deg,#10b981,#0d9488)', accentBg: 'bg-emerald-50', accentText: 'text-emerald-600', barGrad: 'from-emerald-500 to-teal-500', glow: '#10b98160' },
          { key: 'mediawise', label: 'Mediawise', icon: Film, desc: 'Per media file', color: 'from-amber-500 to-orange-500', grad: 'linear-gradient(135deg,#f59e0b,#f97316)', accentBg: 'bg-amber-50', accentText: 'text-amber-600', barGrad: 'from-amber-400 to-orange-500', glow: '#f59e0b60' },
        ]
        const activeRt = REPORT_TYPES.find(r => r.key === reportType) || REPORT_TYPES[0]
        return (
          <div className="space-y-5" style={{ animation: 'fadeInUp 0.35s ease-out' }}>

            {/* ── Hero Banner ── */}
            <div className="relative overflow-hidden rounded-3xl shadow-2xl" style={{ background: 'linear-gradient(135deg,#0d1117 0%,#1e1b4b 60%,#0f172a 100%)' }}>
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-20" style={{ background: 'radial-gradient(circle,#818cf8,transparent 70%)' }} />
              <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full opacity-15" style={{ background: 'radial-gradient(circle,#38bdf8,transparent 70%)' }} />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-5" style={{ background: 'radial-gradient(circle,#a78bfa,transparent 70%)' }} />
              <div className="relative px-8 py-8 flex flex-col lg:flex-row lg:items-center gap-6">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-2xl shrink-0" style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)' }}>
                    <BarChart2 size={22} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-white font-black text-2xl leading-tight">Analytics Dashboard</h2>
                    <p className="text-indigo-300 text-sm font-medium mt-0.5">Nexus Esplanade Mall · Real-time Insights</p>
                  </div>
                </div>
                <div className="flex gap-3 flex-wrap">
                  {[
                    { label: 'Uptime', value: `${uptime}%`, icon: Activity, c: '#10b981', bg: 'rgba(16,185,129,0.15)' },
                    { label: 'Online', value: online, icon: Monitor, c: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
                    { label: 'Total Plays', value: allPlays.toLocaleString(), icon: BarChart2, c: '#a78bfa', bg: 'rgba(167,139,250,0.15)' },
                    { label: 'Unassigned', value: unassigned, icon: WifiOff, c: '#fbbf24', bg: 'rgba(251,191,36,0.15)' },
                  ].map(({ label, value, icon: Icon, c, bg }) => (
                    <div key={label} className="flex flex-col items-center justify-center rounded-2xl px-5 py-3.5 min-w-[86px] border" style={{ background: bg, borderColor: `${c}30` }}>
                      <Icon size={15} style={{ color: c }} className="mb-1.5" />
                      <p className="text-white font-black text-xl leading-none">{value}</p>
                      <p className="text-[10px] font-bold uppercase tracking-wider mt-1" style={{ color: `${c}cc` }}>{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Report Generator Card ── */}
            <div className="rounded-3xl overflow-hidden" style={{ background: 'rgba(13,20,45,0.75)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 25px 50px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)' }}>

              {/* Step 1 — Type Selector */}
              <div className="p-6 pb-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.12)' }}>
                    <span className="text-white text-[10px] font-black">1</span>
                  </div>
                  <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Select Report Type</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {REPORT_TYPES.map(({ key, label, icon: Icon, desc, grad, accentBg, accentText }) => {
                    const active = reportType === key
                    return (
                      <button key={key} onClick={() => { setReportType(key); setReportData(null) }}
                        className={`relative p-4 rounded-2xl text-left transition-all duration-200 overflow-hidden ${active ? 'shadow-2xl scale-[1.03]' : 'hover:scale-[1.01]'}`}
                        style={active ? { background: grad } : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                      >
                        {active && <>
                          <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/10 rounded-full pointer-events-none" />
                          <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-black/10 rounded-full pointer-events-none" />
                        </>}
                        <div className="relative w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-all" style={active ? { background: 'rgba(255,255,255,0.25)' } : { background: 'rgba(255,255,255,0.08)' }}>
                          <Icon size={17} className={active ? 'text-white' : 'text-white/60'} />
                        </div>
                        <p className={`relative font-black text-sm leading-tight ${active ? 'text-white' : 'text-slate-200'}`}>{label}</p>
                        <p className={`relative text-[10px] font-medium mt-1 leading-tight ${active ? 'text-white/70' : 'text-slate-500'}`}>{desc}</p>
                        {active && <div className="absolute bottom-3 right-3 w-2 h-2 rounded-full bg-white/50" />}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Step 2 — Filters */}
              <div className="p-6 pb-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                <div className="flex items-center gap-2.5 mb-5">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.12)' }}>
                    <span className="text-white text-[10px] font-black">2</span>
                  </div>
                  <p className="text-xs font-black text-slate-500 uppercase tracking-widest">Configure Filters</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {reportType === 'playerwise' && (
                    <div>
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-2">Device <span className="text-rose-400">*</span></label>
                      <select className={`${inputCls} glass-select`} value={reportDeviceId} onChange={e => setReportDeviceId(e.target.value)}>
                        <option value="">— choose device —</option>
                        {devices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.deviceName || d.deviceId}{d.group ? ` (${d.group})` : ''}</option>)}
                      </select>
                    </div>
                  )}
                  {reportType === 'groupwise' && (
                    <div>
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-2">Zone / Group</label>
                      <select className={`${inputCls} glass-select`} value={reportGroup} onChange={e => setReportGroup(e.target.value)}>
                        <option value="">All Zones</option>
                        {allGroups.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-2">From Date</label>
                    <input type="date" className={`${inputCls} glass-input`} value={reportFrom} onChange={e => setReportFrom(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-2">To Date</label>
                    <input type="date" className={`${inputCls} glass-input`} value={reportTo} onChange={e => setReportTo(e.target.value)} />
                  </div>
                  <div className="flex flex-col justify-end">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-2">Quick Range</label>
                    <div className="flex gap-1.5">
                      {[
                        { label: 'Today', fn: () => { const t = new Date().toISOString().split('T')[0]; setReportFrom(t); setReportTo(t) } },
                        { label: 'Week', fn: () => { const t = new Date(); const f = new Date(t); f.setDate(t.getDate() - 6); setReportFrom(f.toISOString().split('T')[0]); setReportTo(t.toISOString().split('T')[0]) } },
                        { label: 'Month', fn: () => { const t = new Date(); setReportFrom(new Date(t.getFullYear(), t.getMonth(), 1).toISOString().split('T')[0]); setReportTo(t.toISOString().split('T')[0]) } },
                        { label: 'All', fn: () => { setReportFrom(''); setReportTo('') } },
                      ].map(({ label, fn }) => (
                        <button key={label} type="button" onClick={fn} className="flex-1 text-[10px] font-bold py-2.5 rounded-xl text-slate-500 hover:text-slate-200 transition-all" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}>{label}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 — Generate */}
              <div className="px-6 py-4 flex items-center gap-3 flex-wrap">
                <button
                  onClick={generateReport}
                  disabled={reportLoading}
                  className="flex items-center gap-2.5 text-white font-bold px-8 py-3 rounded-xl transition-all text-sm disabled:opacity-50"
                  style={{ background: reportLoading ? 'rgba(255,255,255,0.1)' : activeRt.grad, boxShadow: reportLoading ? 'none' : `0 8px 28px ${activeRt.glow}` }}
                >
                  {reportLoading ? <><RefreshCcw size={15} className="animate-spin" />Generating…</> : <><Sparkles size={15} />Generate Report</>}
                </button>
                {reportData && (
                  <button onClick={exportReportCSV} className="flex items-center gap-2 text-white font-bold px-5 py-3 rounded-xl transition-all text-sm" style={{ background: 'linear-gradient(135deg,#065f46,#059669)', boxShadow: '0 6px 20px rgba(5,150,105,0.35)' }}>
                    <Download size={14} />Export CSV
                  </button>
                )}
                {(reportFrom || reportTo) && (
                  <div className="flex items-center gap-2 text-indigo-300 text-xs font-bold px-3.5 py-2.5 rounded-xl" style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.2)' }}>
                    <Clock size={12} />{reportFrom || '…'} → {reportTo || '…'}
                  </div>
                )}
              </div>

              {/* ── Results ── */}
              {reportData && (() => {
                const { type, rows } = reportData
                const rt = REPORT_TYPES.find(r => r.key === type)
                if (!rows.length) return (
                  <div className="p-20 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-5" style={{ background: `${rt.glow}` }}>
                      <rt.icon size={32} className="text-white/70" />
                    </div>
                    <p className="text-white font-black text-lg">No data found</p>
                    <p className="text-slate-400 text-sm mt-1">Try a different date range or report type</p>
                  </div>
                )
                const totalAllPlays = rows.reduce((s, r) => s + (r.totalPlays || r.plays || 0), 0)
                return (
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    {/* Gradient results header */}
                    <div className="px-6 py-5 flex items-center justify-between" style={{ background: rt.grad }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                          <rt.icon size={18} className="text-white" />
                        </div>
                        <div>
                          <p className="font-black text-white text-base">{rt.label} Report</p>
                          <p className="text-white/60 text-xs mt-0.5">{rows.length} records · {totalAllPlays.toLocaleString()} total plays</p>
                        </div>
                      </div>
                      <button onClick={exportReportCSV} className="flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-all border border-white/20 shadow-sm">
                        <Download size={13} />Export CSV
                      </button>
                    </div>

                    {/* Summary stat pills */}
                    <div className="px-6 py-4 flex flex-wrap gap-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                      {(type === 'summarized' ? [
                        { label: 'Total Plays', value: totalAllPlays.toLocaleString(), icon: BarChart2 },
                        { label: 'Active Devices', value: rows.length, icon: Monitor },
                        { label: 'Top Device', value: rows[0]?.deviceName || '—', icon: Zap },
                      ] : type === 'playerwise' ? [
                        { label: 'Total Plays', value: totalAllPlays.toLocaleString(), icon: BarChart2 },
                        { label: 'Media Items', value: rows.length, icon: Film },
                        { label: 'Total Air Time', value: fmtTime(rows.reduce((s, r) => s + r.plays * r.duration, 0)), icon: Clock },
                      ] : type === 'groupwise' ? [
                        { label: 'Total Plays', value: totalAllPlays.toLocaleString(), icon: BarChart2 },
                        { label: 'Total Zones', value: rows.length, icon: Activity },
                        { label: 'Top Zone', value: rows[0]?.group || '—', icon: Zap },
                      ] : [
                        { label: 'Total Plays', value: totalAllPlays.toLocaleString(), icon: BarChart2 },
                        { label: 'Media Files', value: rows.length, icon: Film },
                        { label: 'Total Air Time', value: fmtTime(rows.reduce((s, r) => s + r.totalPlays * r.duration, 0)), icon: Clock },
                      ]).map(s => (
                        <div key={s.label} className="flex items-center gap-3 rounded-2xl px-4 py-3 transition-all" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                          <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${rt.glow}` }}>
                            <s.icon size={14} className="text-white/80" />
                          </div>
                          <div>
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{s.label}</p>
                            <p className="font-black text-white text-sm leading-tight truncate max-w-[120px]">{s.value}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Data Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">

                        {/* ── Summarized ── */}
                        {type === 'summarized' && (<>
                          <thead><tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <th className={`${thCls} w-12`}>#</th>
                            <th className={thCls}>Device</th>
                            <th className={thCls}>Zone</th>
                            <th className={thCls}>Total Plays</th>
                            <th className={thCls}>Share</th>
                            <th className={thCls}>Unique Media</th>
                            <th className={thCls}>Avg / Media</th>
                          </tr></thead>
                          <tbody className="divide-y divide-white/5">
                            {rows.map((r, i) => {
                              const { color: avatarColor, initials } = deviceAvatar(r.deviceName, r.deviceId)
                              const pct = totalAllPlays > 0 ? (r.totalPlays / totalAllPlays) * 100 : 0
                              return (
                                <tr key={r.deviceId} className="neon-row transition-colors group">
                                  <td className="px-4 py-4 w-12 text-center">
                                    {i < 3 ? <span className="text-xl">{['🥇','🥈','🥉'][i]}</span> : <span className="text-xs font-black text-slate-600">#{i+1}</span>}
                                  </td>
                                  <td className={tdCls}>
                                    <div className="flex items-center gap-3">
                                      <div className={`w-9 h-9 ${avatarColor} rounded-xl flex items-center justify-center shrink-0 shadow-sm`}><span className="text-white font-black text-[10px]">{initials}</span></div>
                                      <div>
                                        <p className="font-bold text-slate-100 text-sm">{r.deviceName}</p>
                                        <p className="text-[10px] text-slate-500 font-mono">{r.deviceId?.slice(0, 12)}…</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className={tdCls}>{r.group !== '—' ? <span className="text-[11px] font-bold text-violet-300 px-2.5 py-1 rounded-full" style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.2)' }}>{r.group}</span> : <span className="text-slate-600 text-xs">—</span>}</td>
                                  <td className={tdCls}><span className="font-black text-2xl font-mono num-glow">{r.totalPlays.toLocaleString()}</span></td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-2.5 min-w-[130px]">
                                      <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                                        <div className={`h-full rounded-full bg-gradient-to-r ${rt.barGrad} transition-all duration-700`} style={{ width: `${pct}%` }} />
                                      </div>
                                      <span className="text-xs font-black text-slate-400 w-10 text-right">{pct.toFixed(1)}%</span>
                                    </div>
                                  </td>
                                  <td className={`${tdCls} font-bold text-slate-300 font-mono`}>{r.uniqueMedia}</td>
                                  <td className={`${tdCls} text-slate-500 font-mono text-sm`}>{r.uniqueMedia > 0 ? Math.round(r.totalPlays / r.uniqueMedia).toLocaleString() : '—'}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </>)}

                        {/* ── Playerwise ── */}
                        {type === 'playerwise' && (<>
                          <thead><tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <th className={`${thCls} w-12`}>#</th>
                            <th className={thCls}>Media</th>
                            <th className={thCls}>Plays</th>
                            <th className={thCls}>Share</th>
                            <th className={thCls}>Air Time</th>
                            <th className={thCls}>Last Played</th>
                          </tr></thead>
                          <tbody className="divide-y divide-white/5">
                            {rows.map((r, i) => {
                              const pct = totalAllPlays > 0 ? (r.plays / totalAllPlays) * 100 : 0
                              return (
                                <tr key={i} className="neon-row transition-colors">
                                  <td className="px-4 py-4 w-12 text-center">
                                    {i < 3 ? <span className="text-xl">{['🥇','🥈','🥉'][i]}</span> : <span className="text-xs font-black text-slate-600">#{i+1}</span>}
                                  </td>
                                  <td className={tdCls}>
                                    <div className="flex items-center gap-3">
                                      <div className="w-16 h-10 rounded-xl overflow-hidden bg-slate-900 shrink-0 cursor-pointer relative group shadow-md" onClick={() => setSelectedVideo({ fileUrl: r.fileUrl, fileType: r.fileType, rotation: 0 })}>
                                        {r.fileType === 'video' ? <video src={r.fileUrl} className="w-full h-full object-cover opacity-80" muted playsInline /> : <img src={r.fileUrl} className="w-full h-full object-cover opacity-80" alt="" />}
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 rounded-xl"><PlayCircle size={16} className="text-white" /></div>
                                      </div>
                                      <div className="min-w-0">
                                        <p className="font-bold text-slate-100 text-xs truncate max-w-[140px]">{getFileName(r)}</p>
                                        <span className={`inline-block text-[9px] font-black uppercase px-2 py-0.5 rounded-full mt-1 ${r.fileType === 'video' ? 'text-blue-300' : 'text-purple-300'}`} style={r.fileType === 'video' ? { background: 'rgba(59,130,246,0.2)' } : { background: 'rgba(139,92,246,0.2)' }}>{r.fileType} · {r.duration}s</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className={tdCls}><span className="font-black text-2xl font-mono num-glow">{r.plays.toLocaleString()}</span></td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-2.5 min-w-[130px]">
                                      <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                                        <div className={`h-full rounded-full bg-gradient-to-r ${rt.barGrad} transition-all duration-700`} style={{ width: `${pct}%` }} />
                                      </div>
                                      <span className="text-xs font-black text-slate-400 w-10 text-right">{pct.toFixed(1)}%</span>
                                    </div>
                                  </td>
                                  <td className={`${tdCls} font-black num-glow-green`}>{fmtTime(r.plays * r.duration)}</td>
                                  <td className={`${tdCls} text-xs text-slate-500 font-mono`}>{r.lastPlayedAt ? new Date(r.lastPlayedAt).toLocaleString('en-IN') : '—'}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </>)}

                        {/* ── Groupwise ── */}
                        {type === 'groupwise' && (<>
                          <thead><tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <th className={`${thCls} w-12`}>#</th>
                            <th className={thCls}>Zone / Group</th>
                            <th className={thCls}>Devices</th>
                            <th className={thCls}>Total Plays</th>
                            <th className={thCls}>Share</th>
                            <th className={thCls}>Unique Media</th>
                            <th className={thCls}>Avg / Device</th>
                          </tr></thead>
                          <tbody className="divide-y divide-white/5">
                            {rows.map((r, i) => {
                              const pct = totalAllPlays > 0 ? (r.totalPlays / totalAllPlays) * 100 : 0
                              const zoneGrads = ['linear-gradient(135deg,#10b981,#0d9488)','linear-gradient(135deg,#3b82f6,#4f46e5)','linear-gradient(135deg,#8b5cf6,#9333ea)','linear-gradient(135deg,#f59e0b,#f97316)','linear-gradient(135deg,#f43f5e,#e11d48)']
                              const zg = zoneGrads[i % zoneGrads.length]
                              return (
                                <tr key={r.group} className="neon-row-green transition-colors">
                                  <td className="px-4 py-4 w-12 text-center">
                                    {i < 3 ? <span className="text-xl">{['🥇','🥈','🥉'][i]}</span> : <span className="text-xs font-black text-slate-600">#{i+1}</span>}
                                  </td>
                                  <td className={tdCls}>
                                    <div className="flex items-center gap-3">
                                      <div className="w-3 h-10 rounded-full shrink-0" style={{ background: zg }} />
                                      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${zg.replace('linear-gradient(135deg,','').replace(')','').split(',')[0]}22` }}>
                                        <Activity size={15} className="text-emerald-400" />
                                      </div>
                                      <span className="font-black text-white">{r.group}</span>
                                    </div>
                                  </td>
                                  <td className={tdCls}><div className="flex items-center gap-1.5"><Monitor size={13} className="text-slate-500" /><span className="font-bold text-slate-300">{r.deviceCount}</span></div></td>
                                  <td className={tdCls}><span className="font-black text-2xl font-mono num-glow">{r.totalPlays.toLocaleString()}</span></td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-2.5 min-w-[130px]">
                                      <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: zg }} />
                                      </div>
                                      <span className="text-xs font-black text-slate-400 w-10 text-right">{pct.toFixed(1)}%</span>
                                    </div>
                                  </td>
                                  <td className={`${tdCls} font-bold text-slate-300 font-mono`}>{r.uniqueMedia}</td>
                                  <td className={`${tdCls} text-slate-500 font-mono text-sm`}>{r.deviceCount > 0 ? Math.round(r.totalPlays / r.deviceCount).toLocaleString() : '—'}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </>)}

                        {/* ── Mediawise ── */}
                        {type === 'mediawise' && (<>
                          <thead><tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <th className={`${thCls} w-12`}>#</th>
                            <th className={thCls}>Media</th>
                            <th className={thCls}>Total Plays</th>
                            <th className={thCls}>Share</th>
                            <th className={thCls}>Devices</th>
                            <th className={thCls}>Air Time</th>
                            <th className={thCls}>Last Played</th>
                          </tr></thead>
                          <tbody className="divide-y divide-white/5">
                            {rows.map((r, i) => {
                              const pct = totalAllPlays > 0 ? (r.totalPlays / totalAllPlays) * 100 : 0
                              return (
                                <tr key={i} className="neon-row-amber transition-colors">
                                  <td className="px-4 py-4 w-12 text-center">
                                    {i < 3 ? <span className="text-xl">{['🥇','🥈','🥉'][i]}</span> : <span className="text-xs font-black text-slate-600">#{i+1}</span>}
                                  </td>
                                  <td className={tdCls}>
                                    <div className="flex items-center gap-3">
                                      <div className="w-16 h-10 rounded-xl overflow-hidden bg-slate-900 shrink-0 cursor-pointer relative group shadow-md" onClick={() => setSelectedVideo({ fileUrl: r.fileUrl, fileType: r.fileType, rotation: 0 })}>
                                        {r.fileType === 'video' ? <video src={r.fileUrl} className="w-full h-full object-cover opacity-80" muted playsInline /> : <img src={r.fileUrl} className="w-full h-full object-cover opacity-80" alt="" />}
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 rounded-xl"><PlayCircle size={16} className="text-white" /></div>
                                      </div>
                                      <div className="min-w-0">
                                        <p className="font-bold text-slate-100 text-xs truncate max-w-[160px]">{getFileName(r)}</p>
                                        <span className={`inline-block text-[9px] font-black uppercase px-2 py-0.5 rounded-full mt-1 ${r.fileType === 'video' ? 'text-blue-300' : 'text-purple-300'}`} style={r.fileType === 'video' ? { background: 'rgba(59,130,246,0.2)' } : { background: 'rgba(139,92,246,0.2)' }}>{r.fileType} · {r.duration}s</span>
                                      </div>
                                    </div>
                                  </td>
                                  <td className={tdCls}><span className="font-black text-2xl font-mono num-glow">{r.totalPlays.toLocaleString()}</span></td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-2.5 min-w-[130px]">
                                      <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                                        <div className={`h-full rounded-full bg-gradient-to-r ${rt.barGrad} transition-all duration-700`} style={{ width: `${pct}%` }} />
                                      </div>
                                      <span className="text-xs font-black text-slate-400 w-10 text-right">{pct.toFixed(1)}%</span>
                                    </div>
                                  </td>
                                  <td className={`${tdCls} font-bold text-slate-300`}><div className="flex items-center gap-1.5"><Monitor size={13} className="text-slate-500" />{r.deviceCount}</div></td>
                                  <td className={`${tdCls} font-black num-glow-green`}>{fmtTime(r.totalPlays * r.duration)}</td>
                                  <td className={`${tdCls} text-xs text-slate-500 font-mono`}>{r.lastPlayedAt ? new Date(r.lastPlayedAt).toLocaleString('en-IN') : '—'}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </>)}

                      </table>
                    </div>
                  </div>
                )
              })()}
            </div>

            {/* ── Quick Export Strip ── */}
            <div className="relative overflow-hidden rounded-3xl p-6 flex flex-wrap items-center justify-between gap-4 shadow-2xl" style={{ background: 'linear-gradient(135deg,#0d1117 0%,#1e1b4b 100%)' }}>
              <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-10" style={{ background: 'radial-gradient(circle,#818cf8,transparent 70%)' }} />
              <div className="flex items-center gap-4 relative">
                <div className="w-11 h-11 bg-white/10 rounded-2xl flex items-center justify-center border border-white/10">
                  <Download size={18} className="text-white" />
                </div>
                <div>
                  <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-0.5">Quick Export</p>
                  <h3 className="text-white font-black text-base">Device Audit CSV</h3>
                  <p className="text-slate-500 text-[11px] mt-0.5">All devices with status, plays & assignments</p>
                </div>
              </div>
              <button onClick={downloadReport} className="flex items-center gap-2 text-white font-bold px-6 py-3 rounded-2xl transition-all text-sm shadow-xl relative" style={{ background: 'linear-gradient(135deg,#3b82f6,#6366f1)', boxShadow: '0 6px 24px #3b82f640' }}>
                <Download size={14} />Download Device Audit
              </button>
            </div>

          </div>
        )
      }

      // ── Cloud Accounts ─────────────────────────────────────────────────────
      case 'cloudinary':
        return (
          <div className="space-y-6 max-w-4xl" style={{ animation: 'fadeInUp 0.35s ease-out' }}>
            <SectionCard title="Cloudinary Accounts" sub="Each device can upload to a separate account to multiply free credits (25 MB × N accounts).">
              {cloudinaryAccounts.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.03)' }}>
                      <th className={thCls}>Name</th><th className={thCls}>Cloud Name</th><th className={thCls}>Folder</th><th className={thCls}>Status</th><th className={`${thCls} text-center`}>Actions</th>
                    </tr></thead>
                    <tbody>
                      {cloudinaryAccounts.map(a => (
                        <tr key={a._id} className="neon-row transition-all" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td className={`${tdCls} font-bold text-slate-200 text-sm`}>{a.name}</td>
                          <td className={`${tdCls} font-mono text-sm text-slate-400`}>{a.cloudName}</td>
                          <td className={`${tdCls} font-mono text-xs text-slate-600`}>{a.folder}</td>
                          <td className={tdCls}>{a.isDefault ? <Badge color="blue">Default</Badge> : <Badge>Standard</Badge>}</td>
                          <td className={`${tdCls} text-center`}>
                            <div className="flex items-center justify-center gap-2">
                              {!a.isDefault && <button onClick={() => handleSetDefaultAccount(a._id)} className="text-xs font-bold px-3 py-1.5 rounded-lg text-white transition-all" style={{ background: 'linear-gradient(135deg,#2563eb,#4f46e5)' }}>Set Default</button>}
                              <button onClick={() => handleDeleteAccount(a._id)} className="text-xs font-bold px-3 py-1.5 rounded-lg text-rose-400 hover:text-white transition-all" style={{ background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.2)' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(244,63,94,0.3)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(244,63,94,0.1)'}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(99,102,241,0.2)' }}><Cloud className="text-indigo-400" size={28} /></div>
                  <p className="text-slate-500 font-bold">No accounts yet</p>
                  <p className="text-slate-700 text-xs mt-1">Add your first Cloudinary account below</p>
                </div>
              )}
            </SectionCard>
            <div className="rounded-2xl p-6" style={{ background: 'rgba(13,20,45,0.75)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 25px 50px rgba(0,0,0,0.4)' }}>
              <h3 className="font-black text-white mb-5">Add New Account</h3>
              <form onSubmit={handleAddAccount} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[['Account Name', 'name', 'e.g. Screen 1 Account', true], ['Cloud Name', 'cloudName', 'your-cloud-name', true], ['API Key', 'apiKey', '123456789012345', true], ['Folder', 'folder', 'nexus-signage', false]].map(([label, key, ph, req]) => (
                  <div key={key}>
                    <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2 block">{label}</label>
                    <input required={req} placeholder={ph} className={`${inputCls} glass-input`} value={newAccount[key]} onChange={e => setNewAccount(p => ({ ...p, [key]: e.target.value }))} />
                  </div>
                ))}
                <div>
                  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2 block">API Secret</label>
                  <input required type="password" placeholder="••••••••••••••••" className={`${inputCls} glass-input`} value={newAccount.apiSecret} onChange={e => setNewAccount(p => ({ ...p, apiSecret: e.target.value }))} />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={newAccount.isDefault} onChange={e => setNewAccount(p => ({ ...p, isDefault: e.target.checked }))} className="w-4 h-4 accent-indigo-500 rounded" />
                    <span className="text-sm font-semibold text-slate-400">Set as Default Account</span>
                  </label>
                </div>
                <button type="submit" disabled={addingAccount} className="md:col-span-2 text-white font-bold py-3.5 rounded-xl transition-all disabled:opacity-50" style={{ background: 'linear-gradient(135deg,#3730a3,#4f46e5)', boxShadow: '0 8px 24px rgba(99,102,241,0.35)' }}>
                  {addingAccount ? 'Adding…' : 'Add Cloudinary Account'}
                </button>
              </form>
            </div>
          </div>
        )

      // ── Cloud Storage Dashboard ────────────────────────────────────────────
      case 'storage': {
        const ACCT_PALETTES = [
          { grad: 'linear-gradient(135deg,#1e1b4b,#3730a3)', accent: '#818cf8', glow: 'rgba(129,140,248,0.35)' },
          { grad: 'linear-gradient(135deg,#064e3b,#047857)', accent: '#34d399', glow: 'rgba(52,211,153,0.35)'  },
          { grad: 'linear-gradient(135deg,#4c0519,#9f1239)', accent: '#fb7185', glow: 'rgba(251,113,133,0.35)' },
          { grad: 'linear-gradient(135deg,#0c1445,#1e3a8a)', accent: '#60a5fa', glow: 'rgba(96,165,250,0.35)'  },
          { grad: 'linear-gradient(135deg,#2e1065,#4a1d96)', accent: '#c4b5fd', glow: 'rgba(196,181,253,0.35)' },
          { grad: 'linear-gradient(135deg,#451a03,#92400e)', accent: '#fbbf24', glow: 'rgba(251,191,36,0.35)'  },
        ]

        const totalAccounts = cloudStorageData?.accounts?.length ?? 0
        const totalCap      = 25 * (totalAccounts || 1)
        const totalUsedMB   = cloudStorageData ? cloudStorageData.accounts.reduce((s,a) => s + parseFloat(a.totalMB||0), 0) : 0
        const globalPct     = Math.min(100, (totalUsedMB / totalCap) * 100)
        const globalBar     = globalPct > 80 ? '#f43f5e' : globalPct > 55 ? '#f59e0b' : '#10b981'

        return (
          <div className="space-y-6 pb-6 max-w-2xl" style={{ animation: 'fadeInUp 0.35s ease-out' }}>

            {/* ── Header ── */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white font-black text-xl">Cloud Storage</h2>
                <p className="text-slate-500 text-xs mt-0.5">Har account ka storage status — kitna bhara, kitna bacha</p>
              </div>
              <button onClick={fetchCloudStorage} disabled={cloudStorageLoading}
                className="flex items-center gap-2 text-slate-400 text-xs font-bold px-4 py-2.5 rounded-xl transition-all disabled:opacity-50"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)' }}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}
                onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'}>
                <RefreshCcw size={12} className={cloudStorageLoading ? 'animate-spin' : ''} /> Refresh
              </button>
            </div>

            {/* ── Loading ── */}
            {cloudStorageLoading && (
              <div className="space-y-3">
                {[1,2,3].map(i => (
                  <div key={i} className="rounded-2xl p-5 animate-pulse" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-2xl" style={{ background: 'rgba(255,255,255,0.07)' }} />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 w-28 rounded" style={{ background: 'rgba(255,255,255,0.07)' }} />
                        <div className="h-2 w-20 rounded" style={{ background: 'rgba(255,255,255,0.05)' }} />
                      </div>
                    </div>
                    <div className="h-3 rounded-full" style={{ background: 'rgba(255,255,255,0.07)' }} />
                  </div>
                ))}
              </div>
            )}

            {/* ── Error ── */}
            {!cloudStorageLoading && !cloudStorageData && (
              <div className="flex flex-col items-center justify-center py-16 rounded-2xl" style={{ background: 'rgba(13,20,45,0.75)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Database size={28} className="text-slate-600 mb-3" />
                <p className="text-slate-500 font-bold text-sm">Data load nahi hua</p>
                <button onClick={fetchCloudStorage} className="mt-4 text-xs font-bold px-5 py-2.5 rounded-xl text-white" style={{ background: 'linear-gradient(135deg,#3730a3,#4f46e5)' }}>Retry</button>
              </div>
            )}

            {/* ── Account cards ── */}
            {!cloudStorageLoading && cloudStorageData && (
              <>
                {cloudStorageData.accounts.length === 0 ? (
                  <div className="rounded-2xl p-10 text-center" style={{ background: 'rgba(13,20,45,0.75)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <Cloud size={30} className="text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-500 font-bold text-sm">Koi account nahi mila</p>
                    <button onClick={() => setView('cloudinary')} className="mt-4 text-xs font-bold px-5 py-2.5 rounded-xl text-white" style={{ background: 'linear-gradient(135deg,#3730a3,#4f46e5)' }}>Cloud Accounts → Add karo</button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cloudStorageData.accounts.map((acc, idx) => {
                      const pal      = ACCT_PALETTES[idx % ACCT_PALETTES.length]
                      const usedMB   = parseFloat(acc.totalMB) || 0
                      const remMB    = Math.max(0, 25 - usedMB)
                      const pct      = Math.min(100, (usedMB / 25) * 100)
                      const barCol   = pct > 80 ? '#f43f5e' : pct > 55 ? '#f59e0b' : pal.accent
                      const barGlow  = pct > 80 ? 'rgba(244,63,94,0.5)' : pct > 55 ? 'rgba(245,158,11,0.5)' : pal.glow
                      const statusLabel = pct > 80 ? 'Full ho raha hai!' : pct > 55 ? 'Half used' : 'Plenty of space'
                      const statusCol   = pct > 80 ? '#f43f5e' : pct > 55 ? '#f59e0b' : '#10b981'
                      return (
                        <div key={acc._id} className="rounded-2xl p-5 relative overflow-hidden"
                          style={{ background: 'rgba(10,16,38,0.85)', backdropFilter: 'blur(20px)', border: `1px solid ${pal.accent}20`, boxShadow: `0 4px 24px rgba(0,0,0,0.4)` }}>
                          {/* Subtle ambient */}
                          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none opacity-20" style={{ background: `radial-gradient(circle,${pal.accent},transparent 70%)` }} />

                          {/* Row 1: Account name + status */}
                          <div className="flex items-center gap-3 mb-4 relative">
                            <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style={{ background: pal.grad, boxShadow: `0 4px 14px ${pal.glow}` }}>
                              <Cloud size={16} className="text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-white font-black text-sm truncate">{acc.name}</p>
                                {acc.isDefault && (
                                  <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0" style={{ color: pal.accent, background: `${pal.accent}18`, border: `1px solid ${pal.accent}30` }}>Default</span>
                                )}
                              </div>
                              <p className="text-slate-500 text-[11px] font-mono truncate">{acc.cloudName}</p>
                            </div>
                            <span className="text-[10px] font-black shrink-0" style={{ color: statusCol }}>{statusLabel}</span>
                          </div>

                          {/* Row 2: Progress bar */}
                          <div className="relative mb-3">
                            <div className="h-4 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                              <div className="h-full rounded-full transition-all duration-700 relative" style={{ width: `${pct}%`, background: `linear-gradient(90deg,${barCol}99,${barCol})`, boxShadow: `0 0 12px ${barGlow}` }}>
                                {/* Shimmer */}
                                <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.5) 50%,transparent 100%)' }} />
                              </div>
                            </div>
                            {/* Percentage label inside bar area */}
                            {pct > 15 && (
                              <span className="absolute left-3 top-0 bottom-0 flex items-center text-[10px] font-black text-white" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                                {pct.toFixed(0)}%
                              </span>
                            )}
                          </div>

                          {/* Row 3: Used / Remaining */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full shrink-0" style={{ background: barCol }} />
                              <span className="text-xs font-bold" style={{ color: barCol }}>{usedMB.toFixed(2)} MB used</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="w-2 h-2 rounded-full shrink-0" style={{ background: 'rgba(255,255,255,0.15)' }} />
                              <span className="text-xs font-bold text-slate-400">{remMB.toFixed(2)} MB remaining</span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* ── Overall summary ── */}
                {cloudStorageData.accounts.length > 1 && (
                  <div className="rounded-2xl p-5" style={{ background: 'rgba(13,20,45,0.75)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Total — Saare Accounts Milake</p>
                    <div className="h-3 rounded-full overflow-hidden mb-2" style={{ background: 'rgba(255,255,255,0.06)' }}>
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${globalPct}%`, background: `linear-gradient(90deg,${globalBar}88,${globalBar})`, boxShadow: `0 0 10px ${globalBar}66` }} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold" style={{ color: globalBar }}>{totalUsedMB.toFixed(2)} MB used</span>
                      <span className="text-xs font-bold text-slate-400">{(totalCap - totalUsedMB).toFixed(2)} MB remaining of {totalCap} MB total</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )
      }

      // ── How to Use Guide ───────────────────────────────────────────────────
      case 'guide': {
        const glassCard = { background: 'rgba(13,20,45,0.75)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }
        const sectionHeader = (icon, label, color, gradStop) => (
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `linear-gradient(135deg,${color},${gradStop})` }}>
              {React.createElement(icon, { size: 15, className: 'text-white' })}
            </div>
            <h3 className="text-white font-black text-xl">{label}</h3>
            <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg,${color}55,transparent)` }} />
          </div>
        )
        const bullet = (text, color='#6366f1') => (
          <div className="flex items-start gap-2.5">
            <div className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: color }} />
            <p className="text-slate-300 text-sm leading-relaxed">{text}</p>
          </div>
        )

        return (
          <div className="space-y-8 pb-6" style={{ animation: 'fadeInUp 0.35s ease-out' }}>

            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* ── 1. Hero + Project Motive ────────────────────────────────────── */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div className="relative overflow-hidden rounded-3xl" style={{ background: 'linear-gradient(135deg,#060d20 0%,#0f1a3a 45%,#0a1228 100%)', border: '1px solid rgba(99,102,241,0.2)', boxShadow: '0 30px 80px rgba(0,0,0,0.6), 0 0 80px rgba(99,102,241,0.06)' }}>
              <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle,#818cf8,transparent 70%)' }} />
              <div className="absolute -bottom-20 left-10 w-72 h-72 rounded-full opacity-12 pointer-events-none" style={{ background: 'radial-gradient(circle,#34d399,transparent 70%)' }} />
              <div className="absolute inset-0 opacity-[0.025] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.4) 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
              <div className="relative p-8">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5" style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" style={{ animation: 'dot-blink 2s infinite' }} />
                  <span className="text-indigo-300 text-[11px] font-black uppercase tracking-widest">Nexus Esplanade Mall · Digital Signage System</span>
                </div>
                <h1 className="text-white font-black text-4xl leading-tight mb-4">Complete <span style={{ background: 'linear-gradient(135deg,#818cf8,#34d399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Guide</span> &amp; Documentation</h1>
                <p className="text-slate-300 text-base leading-relaxed max-w-2xl mb-6">
                  Yeh system Nexus Esplanade Mall ke liye specially banaya gaya hai — ek centralized platform jahan se mall ke saare digital screens ko remotely control kiya ja sake. Koi bhi technical team ki zaroorat nahi, sirf ek phone ya laptop chahiye.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Project Motive', c: '#818cf8', bg: 'rgba(129,140,248,0.12)' },
                    { label: 'System Architecture', c: '#34d399', bg: 'rgba(52,211,153,0.12)' },
                    { label: 'Feature Deep Dives', c: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
                    { label: 'Cloudinary Explained', c: '#f43f5e', bg: 'rgba(244,63,94,0.12)' },
                  ].map(b => (
                    <div key={b.label} className="rounded-xl px-3 py-2 text-center" style={{ background: b.bg, border: `1px solid ${b.c}25` }}>
                      <p className="text-xs font-black" style={{ color: b.c }}>{b.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* ── 2. Project Motive ─────────────────────────────────────────── */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div>
              {sectionHeader(Sparkles, 'Why This System Was Built', '#f59e0b', '#d97706')}
              <div className="rounded-2xl overflow-hidden" style={glassCard}>
                {/* Top gradient band */}
                <div className="h-1" style={{ background: 'linear-gradient(90deg,#f59e0b,#f43f5e,#8b5cf6,#6366f1)' }} />
                <div className="p-6 space-y-5">
                  {/* Problem */}
                  <div className="rounded-2xl p-5" style={{ background: 'rgba(244,63,94,0.06)', border: '1px solid rgba(244,63,94,0.15)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(244,63,94,0.2)' }}>
                        <AlertOctagon size={13} className="text-rose-400" />
                      </div>
                      <p className="text-rose-300 font-black text-sm uppercase tracking-wider">The Problem</p>
                    </div>
                    <div className="space-y-2">
                      {[
                        'Nexus Esplanade Mall mein multiple digital screens hain — Ground Floor, Food Court, Entry Gate, aur aur.',
                        'Pehle har screen pe manually USB ya pen drive se video copy karni padti thi — bahut time waste hota tha.',
                        'Koi ek screen pe content badalna ho toh physically jaana padta tha. Emergency announcement possible nahi thi.',
                        'Koi bhi data nahi tha — konsi video kitni baar chali, konsi screen online hai — sab andhera tha.',
                      ].map((t, i) => bullet(t, '#f43f5e'))}
                    </div>
                  </div>
                  {/* Solution */}
                  <div className="rounded-2xl p-5" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.2)' }}>
                        <CheckCircle2 size={13} className="text-emerald-400" />
                      </div>
                      <p className="text-emerald-300 font-black text-sm uppercase tracking-wider">Our Solution</p>
                    </div>
                    <div className="space-y-2">
                      {[
                        'Ek web-based admin panel banaya (yeh) — kisi bhi device se access kar sako.',
                        'Saare screens ko real-time mein control karo — content upload karo, assign karo, schedule karo, sab ek jagah se.',
                        'Emergency broadcast — ek click mein saare screens pe announcement.',
                        'Full analytics — kitni baar konsi video chali, kitna time on-air rahi, konsi zone ka performance better hai.',
                        'Zero server storage cost — media Cloudinary cloud pe store hoti hai, CDN se fast delivery milti hai.',
                      ].map((t, i) => bullet(t, '#10b981'))}
                    </div>
                  </div>
                  {/* Goal */}
                  <div className="rounded-2xl p-5" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.2)' }}>
                        <Star size={13} className="text-indigo-400" />
                      </div>
                      <p className="text-indigo-300 font-black text-sm uppercase tracking-wider">Our Goal</p>
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">Mall management ke liye ek aisa tool banana jahan <span className="text-white font-bold">koi bhi non-technical person</span> baith ke 5 minute mein naya content live kar sake. Har advertiser ke liye ek dedicated zone, proper analytics, aur full control — bina kisi IT team ke.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* ── 3. System Architecture ────────────────────────────────────── */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div>
              {sectionHeader(Layers, 'How the System Works — Architecture', '#10b981', '#059669')}
              <div className="rounded-2xl p-6 space-y-5" style={glassCard}>
                <p className="text-slate-300 text-sm leading-relaxed">Poora system 4 parts mein kaam karta hai. Yeh sab ek saath real-time mein connected hain:</p>
                {/* Architecture visual */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-center">
                  {[
                    { label: 'Admin Panel', sub: 'Yeh app (React.js)', icon: LayoutDashboard, color: '#6366f1', grad: 'linear-gradient(135deg,#1e1b4b,#3730a3)' },
                    { label: 'Backend Server', sub: 'Node.js + Express + MongoDB', icon: HardDrive, color: '#10b981', grad: 'linear-gradient(135deg,#064e3b,#047857)' },
                    { label: 'Cloudinary CDN', sub: 'Media Storage & Delivery', icon: Cloud, color: '#f59e0b', grad: 'linear-gradient(135deg,#451a03,#92400e)' },
                    { label: 'Screen App', sub: 'React Native / TV App', icon: Monitor, color: '#f43f5e', grad: 'linear-gradient(135deg,#4c0519,#9f1239)' },
                  ].map(({ label, sub, icon: Icon, color, grad }, i) => (
                    <div key={label} className="flex sm:flex-col items-center gap-3">
                      <div className="rounded-2xl p-4 flex-1 sm:flex-none sm:w-full text-center" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${color}25` }}>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-2" style={{ background: grad }}>
                          <Icon size={16} className="text-white" />
                        </div>
                        <p className="text-white font-black text-xs">{label}</p>
                        <p className="text-slate-500 text-[10px] mt-0.5">{sub}</p>
                      </div>
                      {i < 3 && <ArrowRight size={14} className="text-slate-600 shrink-0 sm:hidden" />}
                    </div>
                  ))}
                </div>
                {/* Flow explanation */}
                <div className="space-y-3">
                  {[
                    { num: '1', color: '#6366f1', title: 'Admin Panel → Backend', desc: 'Aap jab bhi koi action karte ho (device add, content assign, broadcast) — yeh admin panel backend server ko API request bhejta hai (HTTPS secure).' },
                    { num: '2', color: '#10b981', title: 'Backend → MongoDB', desc: 'Backend server saari information MongoDB database mein store karta hai — devices, assigned content, schedules, analytics plays — sab kuch.' },
                    { num: '3', color: '#f59e0b', title: 'Upload → Cloudinary', desc: 'Jab aap koi video ya image upload karte ho — file seedha Cloudinary ke servers pe jaati hai. Backend sirf Cloudinary ka secure URL store karta hai.' },
                    { num: '4', color: '#f43f5e', title: 'Backend → Screen App (Real-time)', desc: 'Content assign hote hi, backend Socket.IO ke through screen app ko turant notify karta hai. Screen app Cloudinary URL se media download karke play karta hai. Koi manual refresh nahi.' },
                    { num: '5', color: '#8b5cf6', title: 'Screen App → Backend (Analytics)', desc: 'Jab bhi koi media screen pe play hoti hai, screen app backend ko ek "play event" bhejta hai. Yahi data Analytics mein dikhta hai.' },
                  ].map(({ num, color, title, desc }) => (
                    <div key={num} className="flex items-start gap-3 rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${color}18` }}>
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 mt-0.5 font-black text-white text-[11px]" style={{ background: color }}>{num}</div>
                      <div>
                        <p className="text-white font-bold text-sm mb-0.5">{title}</p>
                        <p className="text-slate-400 text-xs leading-relaxed">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* ── 4. Getting Started Steps ──────────────────────────────────── */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div>
              {sectionHeader(MousePointerClick, 'Getting Started — Step by Step', '#6366f1', '#4f46e5')}
              <div className="space-y-4">
                {[
                  {
                    num:'01', color:'#6366f1', grad:'linear-gradient(135deg,#1e1b4b,#3730a3)', icon: Monitor,
                    title: 'Register Your Screen',
                    tags: ['Dashboard', 'Register Device'],
                    desc: 'Dashboard pe jao aur "Register Device" button click karo. Jo popup aayega usme 3 cheezein bharo:',
                    details: [
                      'Device ID — yeh TV/screen app launch hone pe screen pe dikhta hai (unique ID hoti hai).',
                      'Device Name — ek readable naam jaise "Ground Floor Screen 1" ya "Food Court Left".',
                      'Zone / Group — floor ya area ka naam jaise "Ground Floor" ya "Food Court" — analytics filtering ke liye use hota hai.',
                    ],
                    tip: 'Device ID ek baar register hone ke baad change nahi hoti. Screen app restart karne pe bhi same ID rehti hai.',
                  },
                  {
                    num:'02', color:'#10b981', grad:'linear-gradient(135deg,#064e3b,#047857)', icon: Upload,
                    title: 'Upload Your Media',
                    tags: ['Upload Media', 'Cloudinary'],
                    desc: '"Upload Media" section mein jao. Files add karne ke 2 tarike hain:',
                    details: [
                      'Drag & Drop — file ko browser window ke andar chhod do.',
                      'Click to Browse — "Browse Files" click karo, multiple files ek saath select kar sakte ho.',
                      'Supported formats: MP4, MOV (video) aur JPG, PNG, WebP (images).',
                      'Files ek-ek karke queue mein upload hoti hain — Cloudinary API limits avoid karne ke liye.',
                      'Same file dubara upload karo toh duplicate nahi banega (SHA-256 deduplication).',
                    ],
                    tip: 'Image files ke liye duration aap khud set karte ho (default 10 seconds). Video apni natural length tak chalti hai.',
                  },
                  {
                    num:'03', color:'#8b5cf6', grad:'linear-gradient(135deg,#2e1065,#4c1d95)', icon: Layers,
                    title: 'Assign Content to Screen',
                    tags: ['Dashboard', 'Edit Content'],
                    desc: 'Dashboard pe device row mein "Edit Content" button click karo. Ek side panel khulega:',
                    details: [
                      '"Add from Gallery" button se Media Library khulti hai — wahan se jo media chahiye select karo.',
                      'Multiple items ek saath add kar sakte ho.',
                      'Items ko drag karke reorder kar sakte ho (jo pehle hoga woh pehle play hoga).',
                      'Images ke liye duration set karo (seconds mein) — 5 se 15 seconds ideal hai.',
                      '"Save" click karte hi screen pe instantly update ho jaata hai — koi manual refresh nahi.',
                    ],
                    tip: 'Ek hi media ko multiple screens pe assign kar sakte ho. Gallery se select karo, bas.',
                  },
                  {
                    num:'04', color:'#f59e0b', grad:'linear-gradient(135deg,#451a03,#92400e)', icon: Clock,
                    title: 'Schedule Content (Optional)',
                    tags: ['Schedule', 'Time Slots', 'Days'],
                    desc: 'Edit Content mein har media item ke saath ek "Schedule" toggle hota hai:',
                    details: [
                      'Toggle OFF (default) = Always On — media 24 ghante, 7 din play hoti rehti hai.',
                      'Toggle ON = Scheduled — aap time range aur days select karte ho.',
                      'Start Time / End Time — e.g. 11:00 AM to 02:00 PM.',
                      'Days — Mon, Tue, Wed... koi bhi combination select karo.',
                      'Example: "Lunch Offer" video sirf Mon-Sat, 11 AM–3 PM play karo.',
                    ],
                    tip: 'Ek device pe mix rakh sakte ho — kuch Always On (brand logo) aur kuch Scheduled (offers/promos).',
                  },
                  {
                    num:'05', color:'#f43f5e', grad:'linear-gradient(135deg,#4c0519,#9f1239)', icon: Megaphone,
                    title: 'Emergency Broadcast',
                    tags: ['Broadcast', 'Instant', 'All Screens'],
                    desc: 'Dashboard pe device row ke saath ya header mein "Broadcast" button hota hai:',
                    details: [
                      'Text message — saari screens pe instantly text display hoga.',
                      'Video broadcast — koi specific video/ad emergency basis pe push kar sakte ho.',
                      'Target: All Devices ya sirf specific zone ke screens.',
                      'Broadcast current content ko override karta hai — turant play hota hai.',
                      'Use cases: Fire drill, mall timing change, flash sale announcement, technical notice.',
                    ],
                    tip: 'Broadcast Socket.IO ke through real-time jaata hai — internet connected screen pe 1-2 second mein dikhai deta hai.',
                  },
                ].map(({ num, color, grad, icon: Icon, title, tags, desc, details, tip }) => (
                  <div key={num} className="guide-step rounded-2xl overflow-hidden" style={glassCard}>
                    <div className="flex">
                      <div className="w-1.5 shrink-0" style={{ background: `linear-gradient(180deg,${color},transparent)` }} />
                      <div className="flex-1 p-5">
                        <div className="flex items-start gap-4">
                          <div className="shrink-0">
                            <div className="w-14 h-14 rounded-2xl flex flex-col items-center justify-center" style={{ background: grad, boxShadow: `0 6px 20px ${color}50` }}>
                              <Icon size={20} className="text-white" />
                              <span className="text-white/50 text-[9px] font-black tracking-widest mt-0.5">{num}</span>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h4 className="text-white font-black text-base">{title}</h4>
                              {tags.map(t => <span key={t} className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ color, background: `${color}18`, border: `1px solid ${color}30` }}>{t}</span>)}
                            </div>
                            <p className="text-slate-400 text-sm mb-3">{desc}</p>
                            <div className="space-y-1.5 mb-3">
                              {details.map((d, i) => (
                                <div key={i} className="flex items-start gap-2">
                                  <ArrowRight size={11} className="shrink-0 mt-1" style={{ color }} />
                                  <p className="text-slate-300 text-sm leading-relaxed">{d}</p>
                                </div>
                              ))}
                            </div>
                            <div className="flex items-start gap-2 rounded-xl px-3.5 py-2.5" style={{ background: `${color}0d`, border: `1px solid ${color}1a` }}>
                              <Lightbulb size={13} className="shrink-0 mt-0.5" style={{ color }} />
                              <p className="text-xs leading-relaxed" style={{ color: `${color}cc` }}>{tip}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* ── 5. Cloudinary Explained ───────────────────────────────────── */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div>
              {sectionHeader(Cloud, 'Cloudinary — Media Storage Explained', '#f59e0b', '#d97706')}
              <div className="rounded-2xl overflow-hidden" style={glassCard}>
                <div className="h-1" style={{ background: 'linear-gradient(90deg,#f59e0b,#f97316)' }} />
                <div className="p-6 space-y-5">
                  {/* What is Cloudinary */}
                  <div>
                    <p className="text-white font-black text-base mb-3">Cloudinary kya hai?</p>
                    <p className="text-slate-300 text-sm leading-relaxed mb-3">
                      Cloudinary ek <span className="text-white font-bold">cloud-based media management service</span> hai. Jaise Google Drive media store karta hai, waise hi Cloudinary — lekin yeh specifically videos aur images ke liye optimize hai aur ek <span className="text-amber-300 font-bold">CDN (Content Delivery Network)</span> ke through fast delivery karta hai.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[
                        { label: 'Free Storage', value: '25 MB', sub: 'per account', color: '#10b981' },
                        { label: 'Free Bandwidth', value: '25 GB', sub: 'per month', color: '#6366f1' },
                        { label: 'Transformations', value: '25,000', sub: 'per month', color: '#f59e0b' },
                      ].map(s => (
                        <div key={s.label} className="rounded-xl p-4 text-center" style={{ background: `${s.color}10`, border: `1px solid ${s.color}20` }}>
                          <p className="font-black text-2xl" style={{ color: s.color }}>{s.value}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{s.sub}</p>
                          <p className="text-xs font-bold text-slate-400 mt-1">{s.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* How it works in our system */}
                  <div className="rounded-xl p-5" style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)' }}>
                    <p className="text-amber-300 font-black text-sm mb-3">Hamare system mein Cloudinary kaise kaam karta hai?</p>
                    <div className="space-y-2">
                      {[
                        'Aap Upload Media page pe file select karte ho.',
                        'Admin panel file ko seedha Cloudinary ke servers pe upload karta hai (secure API ke through).',
                        'Cloudinary file store karke ek public URL generate karta hai — e.g. https://res.cloudinary.com/your-cloud/video/upload/nexus-signage/myvideo.mp4',
                        'Yeh URL hamare MongoDB database mein save ho jaata hai.',
                        'Jab screen app ko content assign hota hai, usse yeh URL milti hai.',
                        'Screen app Cloudinary ke CDN se seedha media download karke play karta hai — hamare server pe koi load nahi.',
                      ].map((t, i) => (
                        <div key={i} className="flex items-start gap-2.5">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[10px] font-black mt-0.5" style={{ background: 'rgba(245,158,11,0.2)', color: '#f59e0b' }}>{i+1}</div>
                          <p className="text-slate-300 text-sm leading-relaxed">{t}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Multiple accounts strategy */}
                  <div className="rounded-xl p-5" style={{ background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.15)' }}>
                    <p className="text-indigo-300 font-black text-sm mb-3">Multiple Accounts Strategy — Free Storage Multiply Karo</p>
                    <div className="space-y-2 mb-4">
                      {[
                        'Cloudinary ka free plan sirf 25 MB storage deta hai — jo zyada videos ke liye kam pad sakta hai.',
                        'Lekin aap multiple free accounts bana sakte ho — har account 25 MB free.',
                        '4 accounts = 100 MB free storage, 8 accounts = 200 MB free storage.',
                        'Hamare system mein aap "Cloud Accounts" section mein multiple accounts add kar sakte ho.',
                        'Har device ya zone ko alag account assign karo — Ground Floor ka alag account, Food Court ka alag.',
                        'Upload automatically us device ke assigned account pe jaati hai.',
                      ].map((t, i) => bullet(t, '#6366f1'))}
                    </div>
                    <div className="flex items-start gap-2 rounded-lg px-3 py-2.5" style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)' }}>
                      <Lightbulb size={12} className="text-indigo-400 shrink-0 mt-0.5" />
                      <p className="text-indigo-300 text-xs">Pro tip: Alag email IDs se Cloudinary accounts banao (Gmail + alias trick: yourname+floor1@gmail.com) — sab same inbox mein aate hain.</p>
                    </div>
                  </div>
                  {/* Benefits */}
                  <div>
                    <p className="text-white font-bold text-sm mb-3">Cloudinary use karne ke fayde:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {[
                        { icon: Zap, color: '#f59e0b', t: 'CDN delivery — videos fast load hoti hain kyunki Cloudinary ke servers globally distributed hain.' },
                        { icon: HardDrive, color: '#10b981', t: 'Hamare server pe zero storage — server cost minimum rehti hai.' },
                        { icon: ShieldCheck, color: '#6366f1', t: 'Files secure hain — Cloudinary enterprise-grade security use karta hai.' },
                        { icon: Globe, color: '#f43f5e', t: 'URL permanent hai — file kabhi delete nahi hogi jab tak aap manually na karein.' },
                      ].map(({ icon: Icon, color, t }) => (
                        <div key={t} className="flex items-start gap-2.5 rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                          <Icon size={13} className="shrink-0 mt-0.5" style={{ color }} />
                          <p className="text-slate-300 text-xs leading-relaxed">{t}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* ── 6. Feature Deep Dives ─────────────────────────────────────── */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div>
              {sectionHeader(Sparkles, 'Every Feature — Detailed Explanation', '#8b5cf6', '#7c3aed')}
              <div className="space-y-4">
                {[
                  {
                    icon: LayoutDashboard, color: '#6366f1', grad: 'linear-gradient(135deg,#1e1b4b,#3730a3)',
                    title: 'Dashboard', badge: 'Command Center',
                    overview: 'Dashboard poore system ka heart hai. Yahan se saare screens manage hote hain ek hi jagah se.',
                    sections: [
                      {
                        sub: 'Device Table',
                        points: [
                          'Har registered screen ka ek row hota hai table mein.',
                          'Real-time online/offline status — green dot = online, gray = offline.',
                          'Zone/Group column — device kis area mein hai.',
                          'Play Count — is session mein device ne kitni baar media play ki.',
                          'Content Button — device ka assigned content edit karo.',
                          'Broadcast Button — sirf is device pe emergency message bhejo.',
                          'Cloud Account — is device ke liye konsa Cloudinary account use hoga.',
                          'Delete Button — device registry se hata do.',
                        ],
                      },
                      {
                        sub: 'Group Filter Tabs',
                        points: [
                          'Agar bahut saari screens hain, zones ke hisaab se filter karo.',
                          '"All" tab = saari devices. "Ground Floor" tab = sirf woh screens.',
                        ],
                      },
                      {
                        sub: 'Live Broadcast Bar',
                        points: [
                          'Upar "Broadcast to All" button se saari screens pe ek saath message bhejo.',
                          'Text ya video — dono options available hain.',
                        ],
                      },
                    ],
                  },
                  {
                    icon: Upload, color: '#10b981', grad: 'linear-gradient(135deg,#064e3b,#047857)',
                    title: 'Upload Media', badge: 'Media Uploader',
                    overview: 'Yahan aap apni videos aur images upload karte ho. Files Cloudinary pe store hoti hain aur phir Gallery mein dikhti hain.',
                    sections: [
                      {
                        sub: 'Upload Process',
                        points: [
                          'Files select karo — drag & drop ya click to browse.',
                          'File list mein saari selected files dikhti hain with size aur type.',
                          'Queue-based upload — ek waqt mein ek file upload hoti hai (API rate limits avoid karne ke liye).',
                          'Progress bar har file ke liye alag — real-time percentage dikhti hai.',
                          'Upload complete hone pe file Gallery mein available ho jaati hai.',
                        ],
                      },
                      {
                        sub: 'Deduplication',
                        points: [
                          'Agar same file pehle se upload hai (same content) toh duplicate nahi banega.',
                          'SHA-256 hash checksum se detect hota hai — same bytes = same file.',
                          'Storage waste nahi hota, aur screen pe same video ka duplicate play nahi hota.',
                        ],
                      },
                      {
                        sub: 'Supported Files',
                        points: [
                          'Video: MP4 (H.264 recommended), MOV',
                          'Image: JPG/JPEG, PNG, WebP',
                          'Best size: Video max 50–100 MB, Image max 5–10 MB',
                          'Resolution: 1920×1080 (Full HD landscape) ideal hai screens ke liye.',
                        ],
                      },
                    ],
                  },
                  {
                    icon: Images, color: '#8b5cf6', grad: 'linear-gradient(135deg,#2e1065,#4c1d95)',
                    title: 'Media Library', badge: 'All Your Media',
                    overview: 'Saari uploaded media ka central store. Yahan preview, manage, aur reuse kar sakte ho.',
                    sections: [
                      {
                        sub: 'Browsing',
                        points: [
                          'Videos aur Images alag sections mein sorted hain.',
                          'Thumbnails grid view mein dikhte hain.',
                          'Video thumbnail pe click karo — inline preview player khulta hai.',
                          'Image pe click — full preview.',
                        ],
                      },
                      {
                        sub: 'Storage Management',
                        points: [
                          'Har Cloudinary account ka storage usage dikhta hai.',
                          '"Cleanup" button — orphaned files delete karo (jo kisi device ko assign nahi hain).',
                          'Yeh Cloudinary ka bandwidth aur storage dono save karta hai.',
                        ],
                      },
                    ],
                  },
                  {
                    icon: CalendarDays, color: '#38bdf8', grad: 'linear-gradient(135deg,#0c4a6e,#0369a1)',
                    title: 'Schedule View', badge: 'Weekly Planner',
                    overview: 'Visual weekly grid jahan dekh sakte ho ki kaunsa screen kaunse din kya play karega.',
                    sections: [
                      {
                        sub: 'Weekly Grid',
                        points: [
                          'Rows = Devices. Columns = Days of week (Mon to Sun).',
                          'Har cell mein number hai — us din us screen pe kitni videos scheduled hain.',
                          'Blue/Indigo cell = aaj ka din — easily identify hota hai.',
                          'Cell pe click karo ya device name pe click karo — Edit Content modal khulta hai.',
                          '"Always On" column — 24/7 play hone wali videos count.',
                          '"Scheduled" column — time-slot specific videos count.',
                        ],
                      },
                      {
                        sub: "Today's Breakdown",
                        points: [
                          'Niche ek grid hai — aaj ke din saari screens pe kya chal raha hai.',
                          'Har device ka card — us device ki aaj ki saari active media list.',
                          'Green "Always" badge = Always On. Purple time = Scheduled slot.',
                          'Quick edit button — seedha Edit Content pop karo.',
                        ],
                      },
                    ],
                  },
                  {
                    icon: BarChart2, color: '#f43f5e', grad: 'linear-gradient(135deg,#4c0519,#9f1239)',
                    title: 'Analytics', badge: '4 Report Types',
                    overview: 'Poori performance tracking. Konsi media kitni baar chali, konsa device most active hai, kitna air time mila — sab kuch.',
                    sections: [
                      {
                        sub: '4 Report Types',
                        points: [
                          'Summarized — Overall view: har device ne total kitni plays ki. Best device, average plays, share percentage.',
                          'Playerwise — Ek specific device ki detail: uske har media item ne kitni baar play hua, kitna air time.',
                          'Groupwise — Zone/Group ke hisaab se comparison: Ground Floor vs Food Court performance.',
                          'Mediawise — Konsi video/image sabse popular hai: saari devices across total plays, devices count, air time.',
                        ],
                      },
                      {
                        sub: 'Filters & Export',
                        points: [
                          'Date Range filter — aaj, is hafte, is mahine, ya custom range.',
                          'Quick Range buttons — Today, Week, Month, All ek click mein.',
                          'Generate button — report load hoti hai, table mein dikhti hai.',
                          'Export CSV — Excel mein open kar sakte ho, management ko email kar sakte ho.',
                          'Results header mein bhi Export button hai for convenience.',
                        ],
                      },
                      {
                        sub: 'How Play Data is Recorded',
                        points: [
                          'Jab bhi screen app koi media play karta hai, woh server ko ek event bhejta hai.',
                          'Server MongoDB mein us media ka "play count" increment karta hai.',
                          'Per-device breakdown bhi store hota hai — same media kaun kaun se device pe chali.',
                          'Yeh data real-time accumulate hota rehta hai — Analytics generate karte hi latest data milta hai.',
                        ],
                      },
                    ],
                  },
                  {
                    icon: Cloud, color: '#f59e0b', grad: 'linear-gradient(135deg,#451a03,#92400e)',
                    title: 'Cloud Accounts', badge: 'Cloudinary Manager',
                    overview: 'Multiple Cloudinary accounts manage karo. Har zone ke liye alag account = zyada free storage.',
                    sections: [
                      {
                        sub: 'Adding an Account',
                        points: [
                          'Cloudinary.com pe signup karo (free) — cloudName, API Key, API Secret milega dashboard mein.',
                          '"Add New Account" form bharo — Name (aap ka label), cloudName, apiKey, apiSecret, aur folder name.',
                          'Folder name = Cloudinary ke andar ek sub-folder — "nexus-signage" default hai.',
                          '"Set as Default" toggle — agar device ka koi account assign nahi toh yeh account use hoga.',
                        ],
                      },
                      {
                        sub: 'Assigning to Devices',
                        points: [
                          'Dashboard pe device row mein Cloud Account dropdown hoti hai.',
                          'Wahan se choose karo konsa Cloudinary account is device ke uploads ke liye use ho.',
                          'Isse har zone ka media alag account pe store hota hai — storage multiply hoti hai.',
                        ],
                      },
                    ],
                  },
                ].map(({ icon: Icon, color, grad, title, badge, overview, sections }) => (
                  <div key={title} className="rounded-2xl overflow-hidden" style={glassCard}>
                    <div className="h-0.5" style={{ background: `linear-gradient(90deg,${color},transparent)` }} />
                    <div className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: grad, boxShadow: `0 6px 20px ${color}40` }}>
                          <Icon size={20} className="text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="text-white font-black text-lg">{title}</h4>
                            <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full" style={{ color, background: `${color}18`, border: `1px solid ${color}30` }}>{badge}</span>
                          </div>
                          <p className="text-slate-400 text-sm mt-0.5">{overview}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {sections.map(({ sub, points }) => (
                          <div key={sub} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                            <p className="font-black text-xs uppercase tracking-wider mb-3" style={{ color }}>{sub}</p>
                            <div className="space-y-1.5">
                              {points.map((p, i) => (
                                <div key={i} className="flex items-start gap-2">
                                  <div className="w-1 h-1 rounded-full mt-2 shrink-0" style={{ background: color }} />
                                  <p className="text-slate-300 text-xs leading-relaxed">{p}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* ── 7. Pro Tips ───────────────────────────────────────────────── */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div>
              {sectionHeader(Star, 'Pro Tips & Best Practices', '#f59e0b', '#d97706')}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { icon: Star, color: '#f59e0b', title: 'Video Length', text: '10–30 second clips best hain. Chhote clips loop smoothly karte hain aur viewers attention hold karte hain. 60+ second ki video bore karti hai mall shoppers ko.' },
                  { icon: Layers, color: '#8b5cf6', title: 'Use Zones Smartly', text: 'Ground Floor, Food Court, Entry Gate — alag zones banao. Isse sirf relevant ads relevant screens pe chalte hain. Food court pe food ads, entry pe promo ads.' },
                  { icon: Globe, color: '#10b981', title: 'Maximize Free Storage', text: 'Ek Cloudinary account 25 MB deta hai. 4-5 free accounts banao alag emails se, aur har zone ko alag account assign karo. Zero cost mein 100–200 MB storage.' },
                  { icon: BarChart2, color: '#6366f1', title: 'Weekly Analytics Check', text: 'Har hafte Mediawise report dekho — konsi video sabse zyada chali. Top 3 performing ads ko zyada screens pe assign karo for maximum impact.' },
                  { icon: Clock, color: '#f43f5e', title: 'Peak Hour Scheduling', text: 'Promotional content sirf peak hours pe schedule karo (11 AM–2 PM, 5 PM–9 PM). Off-peak mein brand/logo Always On rakhao. Isse Cloudinary bandwidth bhi bachti hai.' },
                  { icon: CheckSquare, color: '#38bdf8', title: 'Image Duration', text: 'Images ke liye 6–8 seconds ideal hai. Zyada short (3s) = log nahi padh paate. Zyada long (15s+) = boring lag raha hai. Video apni natural duration pe chalti hai.' },
                  { icon: Megaphone, color: '#f97316', title: 'Emergency Broadcast Ready', text: 'Ek broadcast template ready rakhao (fire drill, medical emergency, mall closing). Jab zaroorat ho ek click mein push karo — precious seconds mat waste karo typing mein.' },
                  { icon: Monitor, color: '#a78bfa', title: 'Screen Naming Convention', text: 'Devices ko clear naam do: "GF-Screen-01", "FC-Left", "Entry-Main". Random names se baad mein confuse hote ho — especially jab 10+ screens ho jaate hain.' },
                ].map(({ icon: Icon, color, title, text }, i) => (
                  <div key={i} className="tip-card rounded-2xl p-4" style={{ background: 'rgba(13,20,45,0.6)', border: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
                        <Icon size={14} style={{ color }} />
                      </div>
                      <p className="text-white font-black text-sm">{title}</p>
                    </div>
                    <p className="text-slate-400 text-xs leading-relaxed pl-11">{text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════ */}
            {/* ── 8. Quick Reference ────────────────────────────────────────── */}
            {/* ══════════════════════════════════════════════════════════════════ */}
            <div className="relative overflow-hidden rounded-3xl p-6" style={{ background: 'linear-gradient(135deg,#0d1117 0%,#1e1b4b 60%,#0f172a 100%)', border: '1px solid rgba(99,102,241,0.2)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-15 pointer-events-none" style={{ background: 'radial-gradient(circle,#818cf8,transparent 70%)' }} />
              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <Settings size={16} className="text-indigo-400" />
                  <h3 className="text-white font-black text-base">Quick Reference</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {[
                    { title: 'Supported Formats', color: '#6366f1', items: ['MP4 (H.264)', 'MOV', 'JPG / JPEG', 'PNG', 'WebP'] },
                    { title: 'Recommended Specs', color: '#10b981', items: ['Resolution: 1920×1080 FHD', 'Video: Max 100 MB/file', 'Image: Max 10 MB/file', 'Duration: 5–30 seconds', 'Orientation: Landscape'] },
                    { title: 'System Stack', color: '#f59e0b', items: ['Frontend: React.js', 'Backend: Node.js + Express', 'Database: MongoDB', 'Media: Cloudinary CDN', 'Realtime: Socket.IO'] },
                  ].map(({ title, color, items }) => (
                    <div key={title}>
                      <p className="font-black text-[10px] uppercase tracking-widest mb-3" style={{ color }}>{title}</p>
                      <div className="space-y-2">
                        {items.map(item => (
                          <div key={item} className="flex items-center gap-2">
                            <ArrowRight size={10} style={{ color }} className="shrink-0" />
                            <span className="text-slate-300 text-xs">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        )
      }

      default: return null
    }
  }

  // ── Shell ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex" style={{ background: 'radial-gradient(ellipse 80% 50% at 10% 0%,rgba(99,102,241,0.18) 0%,transparent 55%), radial-gradient(ellipse 60% 40% at 90% 10%,rgba(16,185,129,0.12) 0%,transparent 50%), radial-gradient(ellipse 70% 60% at 50% 100%,rgba(244,63,94,0.08) 0%,transparent 55%), #030b1a' }}>
      <style>{`
        @keyframes fadeInUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes floatB { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes glow-pulse { 0%,100%{opacity:.5} 50%{opacity:1} }
        @keyframes gradient-shift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes scan-line { 0%{transform:translateY(-100%);opacity:0} 30%{opacity:1} 70%{opacity:1} 100%{transform:translateY(400%);opacity:0} }
        @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes pulse-ring { 0%{transform:scale(1);opacity:.8} 100%{transform:scale(2.2);opacity:0} }
        @keyframes border-flow { 0%{background-position:0% 50%} 100%{background-position:200% 50%} }
        @keyframes dot-blink { 0%,100%{opacity:1} 50%{opacity:.3} }
        .stat-float  { animation: float  5s ease-in-out infinite; }
        .stat-float:nth-child(2){ animation-name:floatB; animation-delay:.6s; }
        .stat-float:nth-child(3){ animation-delay:1.2s; }
        .stat-float:nth-child(4){ animation-delay:1.8s; }
        .nav-glow { box-shadow:0 0 24px rgba(99,102,241,0.5),0 4px 16px rgba(99,102,241,0.3); }
        .neon-row:hover { background:rgba(99,102,241,0.06)!important; box-shadow:inset 3px 0 0 rgba(99,102,241,0.6); }
        .neon-row-green:hover { background:rgba(16,185,129,0.05)!important; box-shadow:inset 3px 0 0 rgba(16,185,129,0.5); }
        .neon-row-amber:hover { background:rgba(245,158,11,0.05)!important; box-shadow:inset 3px 0 0 rgba(245,158,11,0.5); }
        .glass-input { background:rgba(255,255,255,0.06)!important; border:1px solid rgba(255,255,255,0.1)!important; color:#e2e8f0!important; }
        .glass-input::placeholder { color:#475569; }
        .glass-input:focus { border-color:rgba(99,102,241,0.5)!important; box-shadow:0 0 0 3px rgba(99,102,241,0.15)!important; }
        .glass-input option { background:#0d1229; color:#e2e8f0; }
        .glass-select { background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.1); color:#e2e8f0; }
        .glass-select option { background:#0d1229; color:#e2e8f0; }
        ::-webkit-scrollbar { width:5px; height:5px; }
        ::-webkit-scrollbar-track { background:rgba(255,255,255,0.02); }
        ::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.12); border-radius:999px; }
        ::-webkit-scrollbar-thumb:hover { background:rgba(255,255,255,0.2); }
        input[type="date"],input[type="time"] { color-scheme:dark; }

        /* ── Global text brightness lift for dark theme ─────────────────────── */
        .text-slate-600 { color:#7e8fa8 !important; }
        .text-slate-500 { color:#8b9db6 !important; }
        .text-slate-400 { color:#9eb0c8 !important; }
        .text-slate-300 { color:#c4d0df !important; }

        /* ── Gradient glow text for big numbers ─────────────────────────────── */
        .num-glow { background:linear-gradient(135deg,#e2e8f0,#fff); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; filter:drop-shadow(0 0 12px rgba(255,255,255,0.25)); }
        .num-glow-green { background:linear-gradient(135deg,#34d399,#6ee7b7); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; filter:drop-shadow(0 0 10px rgba(52,211,153,0.4)); }
        .num-glow-blue { background:linear-gradient(135deg,#60a5fa,#93c5fd); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; filter:drop-shadow(0 0 10px rgba(96,165,250,0.4)); }
        .num-glow-violet { background:linear-gradient(135deg,#a78bfa,#c4b5fd); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; filter:drop-shadow(0 0 10px rgba(167,139,250,0.4)); }
        .num-glow-amber { background:linear-gradient(135deg,#fbbf24,#fde68a); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; filter:drop-shadow(0 0 10px rgba(251,191,36,0.4)); }

        /* ── Guide page animations ───────────────────────────────────────────── */
        @keyframes slide-in-left { from{opacity:0;transform:translateX(-20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes pop-in { from{opacity:0;transform:scale(0.92)} to{opacity:1;transform:scale(1)} }
        .guide-step { animation: slide-in-left 0.4s ease-out both; }
        .guide-step:nth-child(1){animation-delay:0.05s}
        .guide-step:nth-child(2){animation-delay:0.12s}
        .guide-step:nth-child(3){animation-delay:0.19s}
        .guide-step:nth-child(4){animation-delay:0.26s}
        .guide-step:nth-child(5){animation-delay:0.33s}
        .guide-card { animation: pop-in 0.4s ease-out both; }
        .guide-card:nth-child(1){animation-delay:0.1s}
        .guide-card:nth-child(2){animation-delay:0.2s}
        .guide-card:nth-child(3){animation-delay:0.3s}
        .guide-card:nth-child(4){animation-delay:0.4s}
        .guide-card:nth-child(5){animation-delay:0.5s}
        .guide-card:nth-child(6){animation-delay:0.6s}
        .tip-card:hover { transform:translateY(-3px); }
        .tip-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
      `}</style>

      {/* ── Sidebar ── */}
      <aside className="fixed inset-y-0 left-0 w-64 flex flex-col z-50" style={{ background: 'linear-gradient(180deg,#050d1e 0%,#070f22 50%,#040b18 100%)', boxShadow: '4px 0 40px rgba(0,0,0,0.6), 1px 0 0 rgba(255,255,255,0.04)' }}>
        {/* Ambient glows */}
        <div className="absolute -top-20 -left-20 w-56 h-56 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle,rgba(99,102,241,0.12),transparent 70%)' }} />
        <div className="absolute -bottom-20 left-10 w-40 h-40 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle,rgba(16,185,129,0.08),transparent 70%)' }} />

        {/* Logo */}
        <div className="px-5 py-5 relative" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-2xl shrink-0 relative" style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', boxShadow: '0 6px 24px rgba(99,102,241,0.6),0 0 0 1px rgba(255,255,255,0.1)' }}>
              <Layout size={19} className="text-white" />
              <div className="absolute inset-0 rounded-2xl" style={{ background: 'radial-gradient(circle at 30% 30%,rgba(255,255,255,0.2),transparent 60%)' }} />
            </div>
            <div className="min-w-0">
              <p className="text-white font-black text-sm truncate leading-tight">Nexus Esplanade</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" style={{ animation: 'dot-blink 2s ease-in-out infinite' }} />
                <p className="text-indigo-400 text-[10px] uppercase tracking-widest font-bold">Digital Signage</p>
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-5 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map(({ key, label, icon: Icon }) => {
            const active = view === key
            return (
              <button key={key} onClick={() => setView(key)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold transition-all duration-200 text-left group relative ${active ? 'text-white nav-glow' : 'text-slate-600 hover:text-slate-200'}`}
                style={active ? { background: 'linear-gradient(135deg,#3730a3,#4f46e5,#6366f1)' } : {}}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = '' }}
              >
                {active && <>
                  <div className="absolute inset-0 rounded-2xl" style={{ background: 'radial-gradient(circle at 15% 50%,rgba(255,255,255,0.12),transparent 60%)' }} />
                  <div className="absolute right-0 top-2 bottom-2 w-0.5 rounded-l-full" style={{ background: 'linear-gradient(180deg,transparent,#a5b4fc,transparent)' }} />
                </>}
                <div className={`relative w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-all ${active ? 'bg-white/20' : 'bg-white/4 group-hover:bg-white/8'}`}>
                  <Icon size={15} strokeWidth={active ? 2.5 : 2} className={active ? 'text-white' : 'text-slate-500 group-hover:text-slate-300'} />
                </div>
                <span className="relative font-bold text-sm">{label}</span>
                {active && <div className="ml-auto w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'rgba(255,255,255,0.5)', boxShadow: '0 0 6px rgba(255,255,255,0.5)' }} />}
              </button>
            )
          })}
        </nav>

        {/* Live status */}
        <div className="mx-3 mb-3 rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-3">Network Status</p>
          <div className="flex items-center justify-between mb-2.5">
            <div className="flex items-center gap-2">
              <div className="relative w-2.5 h-2.5 shrink-0">
                <span className="absolute inset-0 rounded-full bg-emerald-500" />
                <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
              </div>
              <span className="text-xs font-bold text-emerald-400">{onlineDevices.length} Online</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: 'rgba(255,255,255,0.15)' }} />
              <span className="text-xs font-bold text-slate-600">{devices.length - onlineDevices.length} Offline</span>
            </div>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div className="h-full rounded-full transition-all duration-700" style={{ width: devices.length > 0 ? `${(onlineDevices.length / devices.length) * 100}%` : '0%', background: 'linear-gradient(90deg,#059669,#10b981,#34d399)', boxShadow: '0 0 8px rgba(16,185,129,0.6)' }} />
          </div>
          <p className="text-[10px] text-slate-600 mt-2 font-medium">{devices.length > 0 ? `${Math.round((onlineDevices.length / devices.length) * 100)}% uptime` : 'No devices'}</p>
        </div>

        {/* User */}
        <div className="px-3 pb-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="flex items-center gap-3 px-3 py-2 rounded-xl mb-1" style={{ background: 'rgba(255,255,255,0.03)' }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-lg" style={{ background: 'linear-gradient(135deg,#3b82f6,#6366f1)', boxShadow: '0 4px 12px rgba(99,102,241,0.4)' }}>
              <User size={15} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-white text-xs font-bold">Super Admin</p>
              <p className="text-slate-600 text-[10px] font-medium">Administrator</p>
            </div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:text-red-400 transition-all" style={{ '--hover-bg': 'rgba(239,68,68,0.08)' }} onMouseEnter={e => e.currentTarget.style.background='rgba(239,68,68,0.08)'} onMouseLeave={e => e.currentTarget.style.background=''}>
            <LogOut size={15} /> Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="ml-64 flex-1 flex flex-col min-h-screen">
        <header className="sticky top-0 z-40 px-8 py-3 flex items-center justify-between" style={{ background: 'rgba(4,10,25,0.85)', backdropFilter: 'blur(24px)', borderBottom: '1px solid rgba(255,255,255,0.07)', boxShadow: '0 4px 30px rgba(0,0,0,0.4)' }}>
          <div className="flex items-center gap-2">
            <span className="text-slate-600 text-xs font-medium">Nexus</span>
            <ChevronRight size={11} className="text-slate-700" />
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)' }}>
                {(() => { const n = NAV_ITEMS.find(n => n.key === view); return n ? <n.icon size={12} className="text-white" /> : null })()}
              </div>
              <span className="text-white font-black text-sm">{PAGE_TITLES[view] || view}</span>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="hidden sm:flex items-center gap-1.5 rounded-full px-3 py-1.5" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs font-bold text-emerald-300">{onlineDevices.length} live</span>
            </div>
            <button onClick={() => setShowBroadcast(true)} className="flex items-center gap-2 text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-all" style={{ background: 'linear-gradient(135deg,#be123c,#f43f5e)', boxShadow: '0 4px 16px rgba(244,63,94,0.4)' }}>
              <Megaphone size={13} /><span className="hidden sm:inline">Broadcast</span>
            </button>
            <button onClick={fetchData} className="p-2.5 rounded-xl transition-all" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }} onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'} onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.06)'}>
              <RefreshCcw size={13} className={`text-slate-400 ${isSyncing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={() => setShowModal(true)} className="flex items-center gap-2 text-white text-xs font-bold px-4 py-2.5 rounded-xl transition-all" style={{ background: 'linear-gradient(135deg,#2563eb,#4f46e5)', boxShadow: '0 4px 16px rgba(99,102,241,0.4)' }}>
              <Plus size={13} /> Add Device
            </button>
          </div>
        </header>

        <main className="flex-1 p-8">{renderContent()}</main>

        <footer className="px-8 py-4 text-center text-xs font-medium" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', color: '#334155' }}>
          Nexus Esplanade Mall · Bhubaneswar · Digital Signage Control System v4.0
        </footer>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Live Preview Modal ──────────────────────────────────────────────── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {previewDevice && (
        <div className="fixed inset-0 flex items-center justify-center z-[200] p-6"
          style={{ background: 'rgba(2,8,20,0.88)', backdropFilter: 'blur(14px)' }}
          onClick={() => setPreviewDevice(null)}>
          <div className="w-full max-w-lg rounded-3xl overflow-hidden"
            style={{ background: 'rgba(10,16,38,0.98)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 40px 80px rgba(0,0,0,0.7)' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'linear-gradient(135deg,#064e3b,#047857)' }}>
              <div className="flex items-center gap-3">
                <PlayCircle size={18} className="text-white" />
                <div>
                  <p className="text-white font-black text-sm">{previewDevice.deviceName || 'Device'}</p>
                  <p className="text-emerald-200/60 text-[11px] font-mono">{previewDevice.deviceId}</p>
                </div>
              </div>
              <button onClick={() => setPreviewDevice(null)} className="p-2 rounded-xl text-white/70 hover:text-white" style={{ background: 'rgba(255,255,255,0.1)' }}><X size={16} /></button>
            </div>
            {/* Content */}
            <div className="p-6">
              {nowPlayingData[previewDevice.deviceId] ? (() => {
                const np = nowPlayingData[previewDevice.deviceId]
                return (
                  <div>
                    <div className="rounded-2xl overflow-hidden mb-4 relative" style={{ background: '#000', aspectRatio: '16/9' }}>
                      {np.fileType === 'image'
                        ? <img src={np.fileUrl} alt="preview" className="w-full h-full object-contain" />
                        : <video src={np.fileUrl} className="w-full h-full object-contain" controls autoPlay muted playsInline />}
                      <div className="absolute top-3 left-3">
                        <span className="text-[10px] font-black uppercase px-2 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.9)', color: '#fff' }}>● LIVE</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-slate-300 text-xs font-medium truncate max-w-xs">{np.fileUrl.split('/').pop()}</p>
                        <p className="text-slate-600 text-[10px] mt-0.5">Last updated: {new Date(np.updatedAt).toLocaleTimeString('en-IN')}</p>
                      </div>
                      <span className="text-[10px] font-black uppercase px-2 py-1 rounded-lg" style={np.fileType === 'video' ? { color: '#818cf8', background: 'rgba(129,140,248,0.15)' } : { color: '#34d399', background: 'rgba(52,211,153,0.15)' }}>{np.fileType}</span>
                    </div>
                  </div>
                )
              })() : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <Monitor size={28} className="text-slate-600" />
                  </div>
                  <p className="text-slate-400 font-bold text-sm">Koi content play nahi ho raha</p>
                  <p className="text-slate-600 text-xs mt-1">{onlineDevices.includes(previewDevice.deviceId) ? 'Device online hai — player se update ka wait karo' : 'Device abhi offline hai'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* ── Bulk Assign floating bar + Modal ────────────────────────────────── */}
      {/* ══════════════════════════════════════════════════════════════════════ */}

      {/* Floating bar — appears when devices are selected in bulk mode */}
      {bulkMode && bulkSelected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[190] flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl"
          style={{ background: 'rgba(10,16,38,0.97)', border: '1px solid rgba(99,102,241,0.4)', boxShadow: '0 8px 40px rgba(99,102,241,0.3), 0 0 0 1px rgba(255,255,255,0.05)' }}>
          <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg,#3730a3,#4f46e5)' }}>
            <CheckSquare size={14} className="text-white" />
          </div>
          <p className="text-white font-black text-sm">{bulkSelected.size} screen{bulkSelected.size > 1 ? 's' : ''} selected</p>
          <div className="w-px h-5 mx-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
          <button onClick={() => setShowBulkAssign(true)}
            className="flex items-center gap-1.5 text-white text-xs font-black px-4 py-2 rounded-xl transition-all"
            style={{ background: 'linear-gradient(135deg,#3730a3,#4f46e5)', boxShadow: '0 4px 14px rgba(99,102,241,0.4)' }}>
            <Images size={12} /> Assign Content
          </button>
          <button onClick={() => { setBulkSelected(new Set()) }} className="text-slate-500 hover:text-slate-300 text-xs font-bold px-3 py-2 rounded-xl transition-all" style={{ background: 'rgba(255,255,255,0.05)' }}>Clear</button>
        </div>
      )}

      {/* Bulk Assign Modal */}
      {showBulkAssign && (
        <div className="fixed inset-0 flex items-center justify-center z-[210] p-6"
          style={{ background: 'rgba(2,8,20,0.9)', backdropFilter: 'blur(14px)' }}
          onClick={() => setShowBulkAssign(false)}>
          <div className="w-full max-w-2xl rounded-3xl overflow-hidden flex flex-col"
            style={{ background: 'rgba(10,16,38,0.98)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 40px 80px rgba(0,0,0,0.7)', maxHeight: '85vh' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-5 flex items-center justify-between shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'linear-gradient(135deg,#1e1b4b,#3730a3)' }}>
              <div>
                <p className="text-white font-black">Bulk Assign Content</p>
                <p className="text-indigo-300/70 text-xs mt-0.5">{bulkSelected.size} screen{bulkSelected.size > 1 ? 's' : ''} will receive the same content</p>
              </div>
              <button onClick={() => setShowBulkAssign(false)} className="p-2 rounded-xl text-white/70 hover:text-white" style={{ background: 'rgba(255,255,255,0.1)' }}><X size={16} /></button>
            </div>

            {/* Selected devices chips */}
            <div className="px-6 py-3 flex flex-wrap gap-1.5 shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              {[...bulkSelected].map(id => {
                const dev = devices.find(d => d.deviceId === id)
                return <span key={id} className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: 'rgba(99,102,241,0.15)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.3)' }}>{dev?.deviceName || id}</span>
              })}
            </div>

            {/* Media picker */}
            <div className="px-6 py-4 shrink-0">
              <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Select Media from Library ({bulkGallerySelected.size} selected)</p>
            </div>
            <div className="flex-1 overflow-y-auto px-6 pb-4" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.08) transparent' }}>
              {allMediaAssets.length === 0
                ? <p className="text-slate-600 text-sm text-center py-8">No media in library</p>
                : <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {allMediaAssets.map(asset => {
                    const sel = bulkGallerySelected.has(asset._id)
                    return (
                      <button key={asset._id}
                        onClick={() => {
                          setBulkGallerySelected(prev => { const s = new Set(prev); sel ? s.delete(asset._id) : s.add(asset._id); return s })
                          setBulkContent(prev => sel ? prev.filter(a => a._id !== asset._id) : [...prev, asset])
                        }}
                        className="relative rounded-xl overflow-hidden transition-all"
                        style={{ aspectRatio: '16/9', background: '#0a1026', border: sel ? '2px solid #6366f1' : '2px solid rgba(255,255,255,0.06)', boxShadow: sel ? '0 0 0 2px rgba(99,102,241,0.3)' : 'none' }}>
                        {asset.fileType === 'image'
                          ? <img src={asset.cloudinaryUrl.replace('/upload/', '/upload/w_200,h_120,c_fill,q_50/')} alt="" className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center"><Film size={20} className="text-indigo-400" /></div>}
                        {sel && <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.35)' }}><CheckCircle2 size={22} className="text-white" /></div>}
                        <div className="absolute bottom-0 inset-x-0 p-1.5" style={{ background: 'linear-gradient(to top,rgba(0,0,0,0.8),transparent)' }}>
                          <p className="text-white text-[9px] truncate font-medium">{asset.originalFilename}</p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              }
            </div>

            {/* Footer actions */}
            <div className="px-6 py-4 flex items-center justify-between shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="text-slate-500 text-xs">{bulkContent.length} file{bulkContent.length !== 1 ? 's' : ''} selected to assign</p>
              <div className="flex gap-2">
                <button onClick={() => setShowBulkAssign(false)} className="text-slate-400 text-sm font-bold px-4 py-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancel</button>
                <button onClick={handleBulkAssign} disabled={bulkAssigning || bulkContent.length === 0}
                  className="flex items-center gap-2 text-white text-sm font-black px-5 py-2.5 rounded-xl transition-all disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg,#3730a3,#4f46e5)', boxShadow: '0 4px 16px rgba(99,102,241,0.4)' }}>
                  {bulkAssigning ? <><RefreshCcw size={13} className="animate-spin" /> Assigning…</> : <><CheckSquare size={13} /> Assign to {bulkSelected.size} Screen{bulkSelected.size > 1 ? 's' : ''}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Device Modal ── */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] p-6" style={{ background: 'rgba(2,8,20,0.85)', backdropFilter: 'blur(12px)' }} onClick={() => setShowModal(false)}>
          <div className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: 'rgba(11,17,35,0.97)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 40px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.08)' }} onClick={e => e.stopPropagation()}>
            <div className="px-8 py-6" style={{ background: 'linear-gradient(135deg,#1e1b4b,#1e3a8a,#1d4ed8)' }}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-black text-xl text-white">Add Device</h3>
                  <p className="text-blue-300/70 text-sm mt-0.5">Register a new screen node</p>
                </div>
                <button onClick={() => setShowModal(false)} className="p-2 rounded-xl transition-all text-white/70 hover:text-white" style={{ background: 'rgba(255,255,255,0.1)' }}><X size={17} /></button>
              </div>
            </div>
            <form onSubmit={handleRegister} className="p-8 space-y-5">
              <div>
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Device Serial ID</label>
                <input required className={`${inputCls} glass-input`} placeholder="e.g. EXPO-ABC123" value={newDevice.id} onChange={e => setNewDevice({ ...newDevice, id: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Location Name</label>
                <input required className={`${inputCls} glass-input`} placeholder="e.g. Food Court Screen" value={newDevice.name} onChange={e => setNewDevice({ ...newDevice, name: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Zone / Group <span className="text-slate-700 font-normal normal-case">(optional)</span></label>
                <div className="relative">
                  <Tag className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-600" size={14} />
                  <input className={`${inputCls} glass-input pl-10`} placeholder="e.g. Floor 1, Food Court" value={newDevice.group} onChange={e => setNewDevice({ ...newDevice, group: e.target.value })} />
                </div>
                {allGroups.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {allGroups.map(g => (
                      <button key={g} type="button" onClick={() => setNewDevice({ ...newDevice, group: g })} className="text-[10px] font-bold text-slate-500 hover:text-indigo-300 px-2.5 py-1 rounded-full transition-all" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>{g}</button>
                    ))}
                  </div>
                )}
              </div>
              <button type="submit" className="w-full text-white font-bold py-3.5 rounded-xl active:scale-[0.98] transition-all" style={{ background: 'linear-gradient(135deg,#3730a3,#4f46e5)', boxShadow: '0 8px 24px rgba(99,102,241,0.4)' }}>
                Register Device
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Video Preview Modal ── */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-[200] p-6" onClick={() => setSelectedVideo(null)}>
          <div className="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden bg-black shadow-2xl border border-white/10" onClick={e => e.stopPropagation()}>
            <button className="absolute top-4 right-4 z-50 bg-black/50 hover:bg-black/70 backdrop-blur-sm p-2.5 rounded-xl text-white transition-all" onClick={() => setSelectedVideo(null)}><X size={18} /></button>
            {selectedVideo.fileType === 'video'
              ? <video src={selectedVideo.fileUrl} style={{ transform: `rotate(${selectedVideo.rotation || 0}deg)` }} className="w-full h-full object-contain" autoPlay controls loop />
              : <img src={selectedVideo.fileUrl} style={{ transform: `rotate(${selectedVideo.rotation || 0}deg)` }} className="w-full h-full object-contain" alt="preview" />
            }
          </div>
        </div>
      )}

      {/* ── Edit Device Content Modal (video-wise schedule) ── */}
      {deviceContentModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[220] p-4" style={{ background: 'rgba(2,8,20,0.88)', backdropFilter: 'blur(12px)' }} onClick={() => setDeviceContentModal(null)}>
          <div className="w-full max-w-5xl rounded-3xl overflow-hidden flex flex-col" style={{ maxHeight: '90vh', background: 'rgba(9,15,32,0.98)', border: '1px solid rgba(255,255,255,0.09)', boxShadow: '0 50px 100px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.07)' }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="px-8 py-5 flex items-center justify-between shrink-0" style={{ background: 'linear-gradient(135deg,#1e1b4b,#312e81,#1e3a8a)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div>
                <h3 className="font-black text-white text-lg">Edit Content</h3>
                <p className="text-indigo-300/70 text-sm mt-0.5">📺 {deviceContentModal.deviceName || deviceContentModal.deviceId}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setContentGalleryOpen(v => !v)}
                  className={`flex items-center gap-2 text-xs font-bold px-4 py-2.5 rounded-xl transition-all ${contentGalleryOpen ? 'text-white' : 'text-indigo-300 hover:text-white'}`}
                  style={contentGalleryOpen ? { background: 'rgba(99,102,241,0.4)', border: '1px solid rgba(99,102,241,0.5)' } : { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <Images size={13} /> {contentGalleryOpen ? 'Hide Gallery' : 'Gallery'}
                </button>
                <button onClick={() => setDeviceContentModal(null)} className="p-2.5 rounded-xl text-white/60 hover:text-white transition-all" style={{ background: 'rgba(255,255,255,0.08)' }}><X size={17} /></button>
              </div>
            </div>

            <div className="flex flex-1 overflow-hidden">

              {/* ── Selected items panel ── */}
              <div className="flex-1 flex flex-col overflow-hidden" style={{ borderRight: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="px-6 py-3 flex items-center justify-between shrink-0" style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <span className="text-xs font-black text-slate-500 uppercase tracking-wider">{deviceContent.length} item{deviceContent.length !== 1 ? 's' : ''} selected</span>
                  <span className="text-[10px] text-slate-700 flex items-center gap-1"><GripVertical size={10} /> Drag to reorder</span>
                </div>

                {deviceContent.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(109,40,217,0.15)', border: '1px solid rgba(139,92,246,0.2)' }}>
                      <Film size={26} className="text-violet-400" />
                    </div>
                    <p className="text-slate-500 font-bold">No media selected</p>
                    <p className="text-slate-700 text-xs text-center">Click <strong className="text-slate-500">Gallery</strong> to pick videos/images from your library</p>
                    <button onClick={() => setContentGalleryOpen(true)} className="flex items-center gap-2 text-white text-xs font-bold px-5 py-2.5 rounded-xl transition-all" style={{ background: 'linear-gradient(135deg,#3730a3,#4f46e5)', boxShadow: '0 4px 16px rgba(99,102,241,0.35)' }}>
                      <Images size={13} /> Open Gallery
                    </button>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {deviceContent.map((it, idx) => (
                      <div
                        key={idx}
                        draggable
                        onDragStart={() => handleContentDragStart(idx)}
                        onDragEnter={() => handleContentDragEnter(idx)}
                        onDragEnd={handleContentDragEnd}
                        onDragOver={e => e.preventDefault()}
                        className="rounded-2xl p-3 select-none cursor-grab active:cursor-grabbing transition-all"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', opacity: contentDragItem.current === idx ? 0.45 : 1 }}
                      >
                        <div className="flex items-center gap-3">
                          <GripVertical size={15} className="text-slate-700 shrink-0" />
                          <span className="text-slate-600 text-xs font-black w-5 shrink-0">#{idx+1}</span>
                          <div className="w-14 h-9 rounded-lg overflow-hidden bg-black shrink-0" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                            {it.fileType === 'video' ? <video src={it.fileUrl} className="w-full h-full object-cover" muted playsInline /> : <img src={it.fileUrl} className="w-full h-full object-cover" alt="" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold text-slate-200 truncate">{it.originalFilename || it.fileUrl.split('/').pop()}</p>
                            <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full mt-0.5 inline-block ${it.fileType === 'video' ? 'text-blue-400 bg-blue-400/15' : 'text-purple-400 bg-purple-400/15'}`}>{it.fileType}</span>
                          </div>
                          <div className="flex items-center gap-1 rounded-lg px-2 py-1 shrink-0" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <span className="text-[10px] text-slate-600 font-bold">s</span>
                            <input className="w-10 bg-transparent outline-none text-blue-400 font-bold text-xs text-right" inputMode="numeric" value={it.duration ?? 10} onChange={e => updateContentItem(idx, { duration: e.target.value })} />
                          </div>
                          <button
                            onClick={() => updateContentSchedule(idx, { enabled: !it.schedule?.enabled })}
                            className="flex items-center gap-1 text-[10px] font-bold px-2.5 py-1.5 rounded-lg transition-all shrink-0"
                            style={it.schedule?.enabled ? { background: 'rgba(109,40,217,0.2)', border: '1px solid rgba(139,92,246,0.3)', color: '#c4b5fd' } : { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#475569' }}
                          >
                            <Clock size={10} />{it.schedule?.enabled ? 'Sched' : 'Always'}
                          </button>
                          <button onClick={() => removeContentItem(idx)} className="p-1.5 rounded-lg text-rose-500/50 hover:text-rose-400 transition-all shrink-0" onMouseEnter={e=>e.currentTarget.style.background='rgba(244,63,94,0.15)'} onMouseLeave={e=>e.currentTarget.style.background=''}><X size={13} /></button>
                        </div>
                        {it.schedule?.enabled && (
                          <div className="mt-3 ml-8 pl-3 space-y-2" style={{ borderLeft: '2px solid rgba(139,92,246,0.25)' }}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Clock size={11} className="text-violet-400 shrink-0" />
                              <input type="time" value={it.schedule.startTime || '00:00'} onChange={e => updateContentSchedule(idx, { startTime: e.target.value })} className="text-xs font-bold rounded-lg px-2 py-1 outline-none" style={{ background: 'rgba(109,40,217,0.15)', border: '1px solid rgba(139,92,246,0.3)', color: '#c4b5fd' }} />
                              <span className="text-slate-600 text-xs font-bold">→</span>
                              <input type="time" value={it.schedule.endTime || '23:59'} onChange={e => updateContentSchedule(idx, { endTime: e.target.value })} className="text-xs font-bold rounded-lg px-2 py-1 outline-none" style={{ background: 'rgba(109,40,217,0.15)', border: '1px solid rgba(139,92,246,0.3)', color: '#c4b5fd' }} />
                            </div>
                            <div className="flex items-center gap-1 flex-wrap">
                              {DAYS.map(day => {
                                const active = (it.schedule.days || DAYS).includes(day)
                                return (
                                  <button key={day} onClick={() => toggleContentDay(idx, day)}
                                    className="text-[10px] font-black px-2 py-1 rounded-lg transition-all"
                                    style={active ? { background: 'rgba(109,40,217,0.35)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.4)' } : { background: 'rgba(255,255,255,0.05)', color: '#475569', border: '1px solid rgba(255,255,255,0.08)' }}
                                  >{day}</button>
                                )
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Footer */}
                <div className="px-6 py-4 flex items-center justify-between gap-3 shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setDeviceContentModal(null)} className="text-slate-600 text-sm font-bold hover:text-slate-400 transition-colors px-4 py-2.5 rounded-xl" onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.05)'} onMouseLeave={e=>e.currentTarget.style.background=''}>Cancel</button>
                    {deviceContent.length > 0 && (
                      <button
                        onClick={() => showConfirm(`Remove all ${deviceContent.length} video(s) from "${deviceContentModal?.deviceName || 'this device'}"?`, () => setDeviceContent([]), { title: 'Clear All Content', danger: true })}
                        className="flex items-center gap-1.5 text-rose-400 hover:text-white text-xs font-bold px-3.5 py-2.5 rounded-xl transition-all"
                        style={{ border: '1px solid rgba(244,63,94,0.3)' }}
                        onMouseEnter={e => { e.currentTarget.style.background='rgba(244,63,94,0.2)'; e.currentTarget.style.borderColor='rgba(244,63,94,0.5)' }}
                        onMouseLeave={e => { e.currentTarget.style.background=''; e.currentTarget.style.borderColor='rgba(244,63,94,0.3)' }}
                      >
                        <Trash2 size={13} /> Clear All
                      </button>
                    )}
                  </div>
                  <button onClick={saveDeviceContent} disabled={deviceContentSaving} className="flex items-center gap-2 text-white text-sm font-black px-6 py-2.5 rounded-xl transition-all disabled:opacity-50" style={{ background: 'linear-gradient(135deg,#3730a3,#4f46e5)', boxShadow: '0 4px 16px rgba(99,102,241,0.35)' }}>
                    {deviceContentSaving ? <><RefreshCcw size={14} className="animate-spin" /> Saving…</> : <><CheckCircle2 size={14} /> Save Content</>}
                  </button>
                </div>
              </div>

              {/* ── Gallery picker panel ── */}
              {contentGalleryOpen && (
                <div className="w-80 flex flex-col overflow-hidden shrink-0">
                  <div className="px-4 py-3 flex items-center justify-between shrink-0" style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <span className="text-xs font-black text-slate-500 uppercase tracking-wider">Media Library</span>
                    {contentGallerySelected.size > 0 && (
                      <button onClick={addGalleryItemsToContent} className="flex items-center gap-1.5 text-[11px] font-black text-white px-3 py-1.5 rounded-lg transition-all" style={{ background: 'linear-gradient(135deg,#5b21b6,#4f46e5)' }}>
                        <Plus size={11} /> Add {contentGallerySelected.size}
                      </button>
                    )}
                  </div>
                  <div className="flex-1 overflow-y-auto p-3 grid grid-cols-2 gap-2 content-start">
                    {allMediaAssets.length === 0 ? (
                      <div className="col-span-2 flex flex-col items-center justify-center py-12 gap-2">
                        <Images size={24} className="text-slate-600" />
                        <p className="text-slate-500 text-xs font-bold">No media uploaded yet</p>
                      </div>
                    ) : allMediaAssets.map(asset => {
                      const key = asset._id || asset.cloudinaryUrl
                      const sel = contentGallerySelected.has(key)
                      const alreadyAdded = deviceContent.some(it => it.fileUrl === asset.cloudinaryUrl)
                      return (
                        <button key={key} disabled={alreadyAdded}
                          onClick={() => { if (alreadyAdded) return; setContentGallerySelected(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n }) }}
                          className={`relative aspect-video rounded-xl overflow-hidden transition-all ${alreadyAdded ? 'opacity-30 cursor-not-allowed' : ''}`}
                          style={sel ? { border: '2px solid rgba(139,92,246,0.8)', boxShadow: '0 0 20px rgba(139,92,246,0.3)' } : { border: '2px solid rgba(255,255,255,0.06)' }}
                        >
                          {asset.fileType === 'video' ? <video src={asset.cloudinaryUrl} className="w-full h-full object-cover" muted playsInline /> : <img src={asset.cloudinaryUrl} className="w-full h-full object-cover" alt="" />}
                          {sel && <div className="absolute inset-0 bg-violet-600/30 flex items-center justify-center"><div className="w-6 h-6 rounded-full bg-violet-600 flex items-center justify-center"><CheckCircle2 size={13} className="text-white" /></div></div>}
                          {alreadyAdded && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><span className="text-[9px] font-black text-white bg-black/60 px-1.5 py-0.5 rounded-full">Added</span></div>}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1">
                            <p className="text-[9px] font-bold text-white truncate">{asset.originalFilename || asset.cloudinaryUrl.split('/').pop()}</p>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Emergency Broadcast Modal ── */}
      {showBroadcast && (
        <div className="fixed inset-0 flex items-center justify-center z-[230] p-6" style={{ background: 'rgba(2,8,20,0.9)', backdropFilter: 'blur(12px)' }} onClick={() => setShowBroadcast(false)}>
          <div className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: 'rgba(11,17,35,0.97)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 40px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.08)' }} onClick={e => e.stopPropagation()}>
            <div className="px-8 py-6" style={{ background: 'linear-gradient(135deg,#7f1d1d,#be123c,#f43f5e)' }}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1"><AlertOctagon size={18} className="text-white" /><h3 className="font-black text-xl text-white">Emergency Broadcast</h3></div>
                  <p className="text-rose-300/70 text-sm">Push instant message to all {onlineDevices.length} online screens</p>
                </div>
                <button onClick={() => setShowBroadcast(false)} className="p-2 rounded-xl text-white/70 hover:text-white transition-all" style={{ background: 'rgba(255,255,255,0.1)' }}><X size={17} /></button>
              </div>
            </div>
            <div className="p-8 space-y-5">
              <div>
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Alert Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    ['info', 'Info', 'linear-gradient(135deg,#1e3a8a,#2563eb)'],
                    ['warning', 'Warning', 'linear-gradient(135deg,#78350f,#d97706)'],
                    ['alert', 'Alert', 'linear-gradient(135deg,#7f1d1d,#dc2626)'],
                  ].map(([t, l, bg]) => (
                    <button key={t} type="button" onClick={() => setBroadcastType(t)}
                      className={`py-2.5 rounded-xl text-xs font-bold transition-all ${broadcastType === t ? 'text-white' : 'text-slate-500'}`}
                      style={broadcastType === t ? { background: bg, boxShadow: '0 4px 16px rgba(0,0,0,0.4)' } : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                    >{l}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Message</label>
                <textarea rows={3} className={`${inputCls} glass-input resize-none`} placeholder="e.g. Store closing in 30 minutes. Please proceed to exits." value={broadcastMsg} onChange={e => setBroadcastMsg(e.target.value)} />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowBroadcast(false)} className="flex-1 py-3 rounded-xl font-bold text-sm text-slate-500 hover:text-slate-300 transition-all" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancel</button>
                <button onClick={handleBroadcast} disabled={broadcastSending || !broadcastMsg.trim()} className="flex-1 flex items-center justify-center gap-2 text-white font-bold py-3 rounded-xl disabled:opacity-40 transition-all" style={{ background: 'linear-gradient(135deg,#be123c,#f43f5e)', boxShadow: '0 8px 24px rgba(244,63,94,0.4)' }}>
                  <Megaphone size={15} />{broadcastSending ? 'Sending…' : 'Send Now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm Modal ── */}
      {confirm && (
        <div className="fixed inset-0 flex items-center justify-center z-[300] p-6" style={{ background: 'rgba(2,8,20,0.9)', backdropFilter: 'blur(12px)' }} onClick={() => setConfirm(null)}>
          <div className="w-full max-w-sm rounded-3xl overflow-hidden" style={{ background: 'rgba(11,17,35,0.97)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 40px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.08)' }} onClick={e => e.stopPropagation()}>
            <div className="px-7 py-5" style={{ background: confirm.danger ? 'linear-gradient(135deg,#7f1d1d,#dc2626)' : 'linear-gradient(135deg,#1e1b4b,#2563eb)' }}>
              <div className="flex items-center gap-2">
                <AlertOctagon size={16} className="text-white" /><h4 className="font-black text-white">{confirm.title}</h4>
              </div>
            </div>
            <div className="p-7">
              <p className="text-slate-300 text-sm font-medium leading-relaxed">{confirm.message}</p>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setConfirm(null)} className="flex-1 py-2.5 rounded-xl font-bold text-sm text-slate-500 hover:text-slate-300 transition-all" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>Cancel</button>
                <button onClick={() => { confirm.onOk(); setConfirm(null) }} className="flex-1 py-2.5 rounded-xl text-white font-bold text-sm transition-all" style={{ background: confirm.danger ? 'linear-gradient(135deg,#7f1d1d,#dc2626)' : 'linear-gradient(135deg,#1e1b4b,#2563eb)', boxShadow: confirm.danger ? '0 8px 24px rgba(220,38,38,0.4)' : '0 8px 24px rgba(37,99,235,0.4)' }}>
                  {confirm.danger ? 'Yes, Delete' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default function App() {
  return (
    <ToastProvider>
      <AppInner />
    </ToastProvider>
  )
}
